import { i as ViewPort, r as LOD, t as Align } from "./enums-ArxA_qeI.js";
import { A as noop, C as exists, M as uuid, O as isVector, S as denormalize, T as intersects, _ as capitalize, b as clampMin, d as Constant, f as Hooks, g as canConnect, h as binarySearch, j as normalize, k as lerp, l as AVLTree, o as BiMap, p as Log, s as List, v as clamp, w as getRandom, x as cloneAudioBuffer } from "./utils-DpUdZbGl.js";
//#region src/core/vector.ts
var Vector = class Vector {
	constructor(xOrDOMPoint, y) {
		if (xOrDOMPoint instanceof DOMPoint) [this.x, this.y] = [xOrDOMPoint.x, xOrDOMPoint.y];
		else [this.x, this.y] = [xOrDOMPoint || 0, y || 0];
	}
	addInPlace(arg1, arg2) {
		if (arg1 instanceof Vector) {
			this.x += arg1.x;
			this.y += arg1.y;
		} else if (typeof arg2 === "undefined") {
			this.x += arg1;
			this.y += arg1;
		} else {
			this.x += arg1;
			this.y += arg2;
		}
		return this;
	}
	multiplyInPlace(arg1, arg2) {
		if (arg1 instanceof Vector) {
			this.x *= arg1.x;
			this.y *= arg1.y;
		} else if (typeof arg2 === "undefined") {
			this.x *= arg1;
			this.y *= arg1;
		} else {
			this.x *= arg1;
			this.y *= arg2;
		}
		return this;
	}
	subtractInPlace(arg1, arg2) {
		if (arg1 instanceof Vector) {
			this.x -= arg1.x;
			this.y -= arg1.y;
		} else if (typeof arg2 === "undefined") {
			this.x -= arg1;
			this.y -= arg1;
		} else {
			this.x -= arg1;
			this.y -= arg2;
		}
		return this;
	}
	normalizeInPlace(min, max, minY, maxY) {
		if (typeof minY !== "undefined" && typeof maxY !== "undefined") {
			this.x = normalize(this.x, min, max);
			this.y = normalize(this.y, minY, maxY);
		} else {
			this.x = normalize(this.x, min, max);
			this.y = normalize(this.y, min, max);
		}
		return this;
	}
	clampInPlace(min, max, minY, maxY) {
		if (typeof minY !== "undefined" && typeof maxY !== "undefined") {
			this.x = clamp(this.x, min, max);
			this.y = clamp(this.y, minY, maxY);
		} else {
			this.x = clamp(this.x, min, max);
			this.y = clamp(this.y, min, max);
		}
		return this;
	}
	transformInPlace(transform) {
		let transformedPoint = transform.transformPoint(this);
		[this.x, this.y] = [transformedPoint.x, transformedPoint.y];
		return this;
	}
	absInPlace() {
		this.x = Math.abs(this.x);
		this.y = Math.abs(this.y);
		return this;
	}
	add(arg1, arg2) {
		return new Vector(this.x, this.y).addInPlace(arg1, arg2);
	}
	multiply(arg1, arg2) {
		return new Vector(this.x, this.y).multiplyInPlace(arg1, arg2);
	}
	subtract(arg1, arg2) {
		return new Vector(this.x, this.y).subtractInPlace(arg1, arg2);
	}
	normalize(min, max, minY, maxY) {
		const vector = new Vector(this.x, this.y);
		if (typeof minY !== "undefined" && typeof maxY !== "undefined") return vector.normalizeInPlace(min, max, minY, maxY);
		return vector.normalizeInPlace(min, max);
	}
	clamp(min, max, minY, maxY) {
		const vector = new Vector(this.x, this.y);
		if (typeof minY !== "undefined" && typeof maxY !== "undefined") return vector.clampInPlace(min, max, minY, maxY);
		return vector.clampInPlace(min, max);
	}
	isInside(arg1, arg2, arg3, arg4) {
		if (arg1 instanceof Vector && arg2 instanceof Vector) {
			if (this.x < arg1.x || this.x > arg2.x) return false;
			if (this.y < arg1.y || this.y > arg2.y) return false;
		} else if (typeof arg1 === "number" && typeof arg2 === "number") {
			if (this.x < arg1 || this.x > arg3) return false;
			if (this.y < arg2 || this.y > arg4) return false;
		} else return false;
		return true;
	}
	assign(arg1, arg2) {
		if (arg1 instanceof Vector) {
			this.x = arg1.x;
			this.y = arg1.y;
		} else if (typeof arg2 === "undefined") {
			this.x = arg1;
			this.y = arg1;
		} else {
			this.x = arg1;
			this.y = arg2;
		}
		return this;
	}
	transform(transform) {
		return new Vector(this.x, this.y).transformInPlace(transform);
	}
	abs() {
		return new Vector(this.x, this.y).absInPlace();
	}
	toString() {
		return "[" + this.x.toFixed(3) + ", " + this.y.toFixed(3) + "]";
	}
	clone() {
		return new Vector(this.x, this.y);
	}
	max() {
		return Math.max(this.x, this.y);
	}
	isEqual(vector, threshold) {
		if (typeof threshold !== "undefined") return Vector.Distance(this, vector) <= threshold;
		return this.x === vector.x && this.y === vector.y;
	}
	static Distance(vector1OrX1, vector2OrY1, x2, y2) {
		if (vector1OrX1 instanceof Vector && vector2OrY1 instanceof Vector) return Math.sqrt(Math.pow(vector2OrY1.x - vector1OrX1.x, 2) + Math.pow(vector2OrY1.y - vector1OrX1.y, 2));
		else if (typeof vector1OrX1 === "number" && typeof vector2OrY1 === "number") return Math.sqrt(Math.pow(x2 - vector1OrX1, 2) + Math.pow(y2 - vector2OrY1, 2));
		return -1;
	}
	static Midpoint(vector1, vector2) {
		return new Vector((vector1.x + vector2.x) / 2, (vector1.y + vector2.y) / 2);
	}
	static Zero() {
		return new Vector(0, 0);
	}
	static Random(minX, maxX, minY, maxY) {
		return new Vector(Math.random() * (maxX - minX) + minX, Math.random() * (maxY - minY) + minY);
	}
	serialize() {
		return {
			x: this.x,
			y: this.y
		};
	}
	static create(arg1, arg2) {
		if (typeof arg1 === "object") return new Vector(arg1.x, arg1.y);
		return new Vector(arg1, arg2);
	}
};
//#endregion
//#region src/core/color.ts
var Color = class Color {
	constructor(color) {
		let hex;
		if (typeof color === "string") {
			hex = color;
			color = Color.hexToRGBA(color);
		}
		this.rgbaValue = color;
		this.hexValue = hex ?? Color.rgbaToHex(this.rgbaValue);
		this.rgbaString = Color.rgbaToString(this.rgbaValue);
		this.rgbaCSSString = Color.rgbaToCSSString(this.rgbaValue);
	}
	isEqual(color) {
		return color.rgbaValue[0] === this.rgbaValue[0] && color.rgbaValue[1] === this.rgbaValue[1] && color.rgbaValue[2] === this.rgbaValue[2] && color.rgbaValue[3] === this.rgbaValue[3];
	}
	static Random() {
		return new Color(new Uint8ClampedArray([
			Math.floor(getRandom(0, 255)),
			Math.floor(getRandom(0, 255)),
			Math.floor(getRandom(0, 255)),
			255
		]));
	}
	static hexToRGBA(hex) {
		return new Uint8ClampedArray([
			parseInt(hex.slice(1, 3), 16),
			parseInt(hex.slice(3, 5), 16),
			parseInt(hex.slice(5, 7), 16),
			parseInt(hex.length > 7 ? hex.slice(7, 9) : "FF", 16)
		]);
	}
	static _componentToHex(c) {
		let hex = c.toString(16);
		return hex.length === 1 ? "0" + hex : hex;
	}
	static rgbaToHex(rgba) {
		return "#" + Color._componentToHex(rgba[0]) + Color._componentToHex(rgba[1]) + Color._componentToHex(rgba[2]) + Color._componentToHex(rgba[3]);
	}
	static rgbaToString(rgba) {
		return `${rgba[0]}:${rgba[1]}:${rgba[2]}:${rgba[3]}`;
	}
	static rgbaToCSSString(rgba) {
		return `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${normalize(rgba[3], 0, 255).toFixed(3)})`;
	}
	/** A linear interpolator for given array of colors */
	static scale(colors) {
		if (!Array.isArray(colors)) {
			Log.error("An array is expected to create a color scale");
			return () => "";
		}
		if (colors.length <= 1) {
			Log.error("At least two colors are required to create a color scale");
			return () => "";
		}
		let colorObjs = [];
		if (colors[0] instanceof Uint8ClampedArray || Array.isArray(colors[0]) && typeof colors[0][0] === "number") colors.forEach((color) => colorObjs.push(new Color(color)));
		else if (typeof colors[0] === "string") colors.forEach((color) => colorObjs.push(new Color(Color.hexToRGBA(color))));
		else colorObjs = colors;
		return (t) => {
			t = clamp(t, 0, 1);
			let index = (colorObjs.length - 1) * t;
			let start = Math.floor(index);
			let end = Math.ceil(index);
			t = index - start;
			let startColor = colorObjs[start], endColor = colorObjs[end];
			return "rgba(" + startColor.rgbaValue.map((component, i) => {
				component = lerp(component, endColor.rgbaValue[i], t);
				if (i < 3) component = Math.round(component);
				return component;
			}) + ")";
		};
	}
	serialize() {
		return [
			this.rgbaValue[0],
			this.rgbaValue[1],
			this.rgbaValue[2],
			this.rgbaValue[3]
		];
	}
	static create(data) {
		return new Color(data);
	}
};
//#endregion
//#region src/core/connector.ts
var Connector = class Connector extends Hooks {
	get data() {
		return this._data;
	}
	set data(data) {
		this.setData(data);
		if (this.flow?.state !== FlowState.Stopped && this.endNode) this.flow?.executionGraph.setDirty(this.endNode);
	}
	constructor() {
		super();
		this.renderer = () => () => void 0;
	}
	static create(flow, start, end, options = DefaultConnectorOptions()) {
		const connector = new Connector();
		const { style = {}, id = uuid(), floatingTip } = options;
		connector.flow = flow;
		connector.style = {
			...DefaultConnectorStyle(),
			...flow.flowConnect.getDefaultStyle("connector") || {},
			...style
		};
		connector.id = id;
		connector.floatingTip = floatingTip;
		connector.start = start;
		connector.end = end;
		connector.startNode = connector.start?.node;
		connector.endNode = connector.end?.node;
		if (start && end) {
			connector.floatingTip = void 0;
			start.connectors.push(connector);
			if (end.connectors.length > 0) {
				end.connectors[0].disconnect();
				end.connectors[0] = connector;
			} else end.connectors.push(connector);
			if (connector.startNode && connector.endNode) flow.executionGraph.connect(connector.startNode, connector.endNode);
			start.onConnect(connector);
			end.onConnect(connector);
		}
		return connector;
	}
	disconnect() {
		let [startTerm, endTerm] = [this.start, this.end];
		if (this.id) this.flow?.removeConnector(this.id);
		this.start?.connectors.splice(this.start.connectors.findIndex((cntr) => cntr.id === this.id), 1);
		this.end?.connectors.pop();
		if (startTerm?.node && endTerm?.node) this.flow?.executionGraph.disconnect(startTerm.node, endTerm.node);
		this.start?.onDisconnect(this, startTerm, endTerm);
		this.end?.onDisconnect(this, startTerm, endTerm);
	}
	setData(data) {
		this._data = data;
		this.end && this.end.call("data", this.end, data);
	}
	canConnect(other) {
		let firstTerminal = !this.start ? this.end : this.start;
		let source, destination;
		if (firstTerminal?.type === TerminalType.IN) {
			source = other;
			destination = firstTerminal;
		} else {
			source = firstTerminal;
			destination = other;
		}
		if (this.flow?.rules && source) return canConnect(source, destination, this.flow.rules, this.flow.executionGraph);
		return false;
	}
	render() {
		let context = this.flow?.flowConnect.context;
		context?.save();
		const scopeFlowConnect = this.flow?.flowConnect.getRegisteredRenderer("connector");
		const scopeFlow = this.flow?.renderers.connector;
		const scopeConnector = this.renderer;
		const renderFn = scopeConnector && scopeConnector(this) || scopeFlow && scopeFlow(this) || scopeFlowConnect && scopeFlowConnect(this) || this._render;
		if (context) renderFn(context, this.getRenderParams(), this);
		context?.restore();
		let offContext = this.flow?.flowConnect.offContext;
		offContext?.save();
		this._offRender();
		offContext?.restore();
		this.call("render", this);
	}
	_render(context, params, connector) {
		if (!params.start || !params.end) return;
		let ax = params.start.x, ay = params.start.y, dx = params.end.x, dy = params.end.y;
		let offset = Vector.Distance(ax, ay, dx, dy);
		offset *= .2;
		let [bx, by] = [ax + offset, ay];
		let [cx, cy] = [dx - offset, dy];
		let [midx, midy] = [(bx + cx) / 2, (by + cy) / 2];
		if (connector.style?.border) {
			if (connector.style.borderColor) context.strokeStyle = connector.style.borderColor;
			if (typeof connector.style.width === "number") context.lineWidth = connector.style.width + 2;
			context.beginPath();
			context.moveTo(ax, ay);
			context.quadraticCurveTo(bx, by, midx, midy);
			context.moveTo(midx, midy);
			context.quadraticCurveTo(cx, cy, dx, dy);
			context.stroke();
		}
		if (connector.style?.color) context.strokeStyle = connector.style.color;
		if (typeof connector.style?.width === "number") context.lineWidth = connector.style.width;
		context.beginPath();
		context.moveTo(ax, ay);
		context.quadraticCurveTo(bx, by, midx, midy);
		context.moveTo(midx, midy);
		context.quadraticCurveTo(cx, cy, dx, dy);
		context.stroke();
	}
	_offRender() {}
	getRenderParams() {
		if (!this.startNode?.position || !this.endNode?.position) return;
		let start, end;
		if (this.start) if (this.startNode.renderState.nodeState === NodeUIState.MAXIMIZED) start = this.start.position.serialize();
		else {
			start = this.startNode.position.serialize();
			start.x += (this.startNode.width ?? 0) + (this.startNode.style?.terminalStripMargin ?? 0) + (this.start.style?.radius ?? 0);
			start.y += (this.startNode.style?.titleHeight ?? 0) / 2;
		}
		else start = this.floatingTip?.serialize();
		if (this.end) if (this.endNode.renderState.nodeState === NodeUIState.MAXIMIZED) end = this.end.position.serialize();
		else {
			end = this.endNode.position.serialize();
			end.x -= (this.endNode.style?.terminalStripMargin ?? 0) + (this.end.style?.radius ?? 0);
			end.y += (this.endNode.style?.titleHeight ?? 0) / 2;
		}
		else end = this.floatingTip?.serialize();
		return {
			start,
			end
		};
	}
	serialize() {
		return {
			id: this.id,
			startId: this.start?.ui ? this.startNode?.outputsUI.findIndex((term) => this.start === term) : this.start?.id,
			endId: this.end?.ui ? this.endNode?.inputsUI.findIndex((term) => this.end === term) : this.end?.id,
			startNodeId: this.startNode?.id,
			endNodeId: this.endNode?.id,
			style: this.style
		};
	}
};
var DefaultConnectorStyle = () => {
	return {
		width: 5,
		color: "#7fff00aa",
		border: true,
		borderColor: "grey"
	};
};
var DefaultConnectorOptions = () => {
	return {
		style: {},
		id: uuid()
	};
};
//#endregion
//#region src/core/terminal.ts
var Terminal = class Terminal extends Hooks {
	get ui() {
		return this._ui;
	}
	set ui(val) {
		this._ui = val;
	}
	get propName() {
		return this._propName;
	}
	set propName(propName) {
		if (!propName || propName === "") {
			Log.error("Failed to set prop name '" + propName + "', prop name is invalid");
			return;
		}
		this.bindToProp(propName);
	}
	constructor() {
		super();
		this.renderer = () => () => void 0;
		this.connectors = [];
		this.focus = false;
		this.position = Vector.Zero();
	}
	static create(node, type, dataType, options = DefaultTerminalOptions()) {
		const terminal = new Terminal();
		const { name = "", hitColor, propName, style = {}, id = uuid(), ui = false } = options;
		terminal.node = node;
		terminal.type = type;
		terminal.dataType = dataType;
		terminal.name = name;
		if (propName) terminal._propName = propName;
		terminal.style = {
			...DefaultTerminalStyle(),
			...node.flow?.flowConnect.getDefaultStyle("terminal") || {},
			...style
		};
		terminal.id = id;
		terminal.ui = ui;
		terminal.setHitColor(hitColor);
		if (terminal._propName) terminal.bindToProp(terminal._propName);
		if (terminal.type === 1) terminal.on("data", (t, data) => {
			if (t.propName) t.node.state[t.propName] = data;
		});
		return terminal;
	}
	bindToProp(propName) {
		if (!this._propName || !this.watcherId) return;
		let oldPropName = this._propName;
		let newPropName = propName;
		this.watcherId ?? this.node?.unwatch(oldPropName, this.watcherId);
		this._propName = newPropName;
		this.watcherId = this.node?.watch(newPropName, (_oldVal, newVal) => {
			if (this.type === 2) this.setData(newVal);
		});
	}
	getData() {
		if (this.type === 2) {
			Log.error("Cannot call 'getData' on output terminal");
			return;
		}
		if (this.connectors.length > 0) return this.connectors[0].data;
	}
	setData(data) {
		if (this.type === 1) {
			Log.error("Cannot call 'setData' on input terminal");
			return;
		}
		this.connectors.forEach((connector) => connector.data = data);
	}
	setHitColor(hitColor) {
		if (!hitColor) {
			hitColor = Color.Random();
			while (this.node?.terminals.get(hitColor.rgbaString) || this.node?.uiNodes.get(hitColor.rgbaString)) hitColor = Color.Random();
		}
		this.hitColor = hitColor;
		this.node?.terminals.set(this.hitColor.rgbaString, this);
	}
	render() {
		if (!this.node?.context || !this.node?.flow) return;
		let context = this.node.context;
		context.save();
		let scopeNode = this.node.renderers.terminal;
		let scopeFlow = this.node.flow.renderers.terminal;
		let scopeFlowConnect = this.node.flow.flowConnect.getRegisteredRenderer("terminal");
		const scopeTerminal = this.renderer;
		(scopeTerminal && scopeTerminal(this) || scopeNode && scopeNode(this) || scopeFlow && scopeFlow(this) || scopeFlowConnect && scopeFlowConnect(this) || this._render)(context, this.getRenderParams(), this);
		context.restore();
		this.offUIRender();
		this.call("render", this);
	}
	_render(context, params, terminal) {
		if (!terminal.dataType) return;
		const termRadius = terminal.style?.radius ?? 0;
		if (params.focus) {
			context.beginPath();
			context.arc(params.position.x, params.position.y, termRadius * 3, 0, Constant.TAU);
			context.fillStyle = terminal.style?.outerFocusColor ?? "";
			context.fill();
		}
		if (terminal.dataType === "event") {
			context.beginPath();
			context.moveTo(params.position.x, params.position.y - termRadius * 1.3);
			context.lineTo(params.position.x + termRadius * 1.3, params.position.y);
			context.lineTo(params.position.x, params.position.y + termRadius * 1.3);
			context.lineTo(params.position.x - termRadius * 1.3, params.position.y);
			context.lineTo(params.position.x, params.position.y - termRadius * 1.3);
			context.closePath();
		} else {
			context.beginPath();
			context.arc(params.position.x, params.position.y, termRadius, 0, Constant.TAU);
		}
		context.fillStyle = params.focus ? terminal.style?.focusColor ?? "" : terminal.node?.flow?.ruleColors?.[terminal.dataType]?.hexValue || (terminal.style?.color ?? "");
		context.strokeStyle = terminal.style?.borderColor ?? "";
		context.shadowBlur = terminal.style?.shadowBlur ?? 0;
		context.shadowColor = terminal.style?.shadowColor ?? "";
		context.fill();
		context.stroke();
	}
	getRenderParams() {
		return {
			focus: this.focus,
			position: this.position.serialize()
		};
	}
	connect(otherTerminal, options) {
		if (!this.node?.flow?.rules) return;
		if (this.type !== otherTerminal.type) {
			let start = this.type === 2 ? this : otherTerminal;
			let end = this.type === 1 ? this : otherTerminal;
			if (end.connectors.length > 0 && start.connectors.includes(end.connectors[0])) return false;
		}
		let source, destination;
		if (this.type === 1) {
			source = otherTerminal;
			destination = this;
		} else {
			source = this;
			destination = otherTerminal;
		}
		if (canConnect(source, destination, this.node.flow.rules, this.node.flow.executionGraph)) {
			let connector = Connector.create(this.node.flow, source, destination, options);
			if (!connector.id) return;
			this.node.flow.connectors.set(connector.id, connector);
			return true;
		} else return false;
	}
	/**
	* Disconnects terminal to terminal connections.
	* If no parameters are passed and the terminal on which this method is called is an output terminal then,
	* all the connections going out from this terminal will be disconnected.
	*
	* @param connector optional connector Id or reference, used only when the terminal on which this method is called is an Output terminal
	* @returns
	*/
	disconnect(connector) {
		if (this.type === 1) {
			this.connectors[0].disconnect();
			return true;
		} else {
			if (typeof connector === "string") {
				let cntr = this.connectors.find((currCntr) => currCntr.id === connector);
				if (!cntr) {
					Log.error("Connector not found while disconnecting", connector);
					return false;
				}
				cntr.disconnect();
			} else if (connector instanceof Connector) {
				if (!this.connectors.find((cntr) => cntr.id === connector.id)) {
					Log.error("Connector cannot be disconnected from the terminal because its not associated with it", connector);
					return false;
				}
				connector.disconnect();
			} else this.connectors.forEach((cntr) => cntr.disconnect());
			return true;
		}
	}
	offUIRender() {
		if (!this.node?.offUIContext) return;
		let context = this.node.offUIContext;
		context.save();
		context.beginPath();
		context.arc(this.position.x, this.position.y, (this.style?.radius ?? 0) + (this.node.style?.terminalStripMargin ?? 0), 0, Constant.TAU);
		context.fillStyle = this.hitColor?.rgbaCSSString ?? "";
		context.fill();
		context.restore();
	}
	isConnected() {
		return this.connectors.length > 0;
	}
	onEnter(screenPosition, realPosition) {
		if (!this.node?.flow) return;
		this.call("enter", this, screenPosition, realPosition);
		this.focus = true;
		this.node.flow.flowConnect.cursor = "pointer";
	}
	onExit(screenPosition, realPosition) {
		if (!this.node?.flow) return;
		this.call("exit", this, screenPosition, realPosition);
		this.focus = false;
		this.node.flow.flowConnect.cursor = "unset";
	}
	onDown(screenPosition, realPosition) {
		if (!this.node?.flow) return;
		if (this.connectors.length > 0) if (this.type === 1 && this.connectors[0].start) {
			const start = this.connectors[0].start;
			this.disconnect();
			this.node.flow.setFloatingConnector(realPosition, start, "left");
			this.node.currHitTerminal = void 0;
		} else {
			if (this.node.flow.floatingConnector) return;
			this.node.flow.setFloatingConnector(realPosition, this, "left");
		}
		else {
			if (this.node.flow.floatingConnector) return;
			this.node.flow.setFloatingConnector(realPosition, this, this.type === 1 ? "right" : "left");
		}
		this.call("down", this, screenPosition, realPosition);
	}
	onUp(screenPosition, realPosition) {
		this.call("up", this, screenPosition, realPosition);
	}
	onDrag(screenPosition, realPosition) {
		this.call("drag", this, screenPosition, realPosition);
	}
	onClick(screenPosition, realPosition) {
		this.call("click", this, screenPosition, realPosition);
	}
	onOver(screenPosition, realPosition) {
		this.call("over", this, screenPosition, realPosition);
	}
	onContextMenu() {
		this.call("rightclick", this);
	}
	onConnect(connector) {
		this.call("connect", this, connector);
	}
	onDisconnect(connector, startTerminal, endTerminal) {
		this.call("disconnect", this, connector, startTerminal, endTerminal);
	}
	onEvent(data) {
		if (this.type === 1) this.call("event", this, data);
	}
	emit(data) {
		if (this.type === 2 && this.connectors.length !== 0) {
			this.connectors.forEach((connector) => {
				connector.end && connector.end.onEvent(data);
			});
			this.call("emit", this, data);
		}
	}
	serialize() {
		return {
			name: this.name,
			type: this.type,
			dataType: this.dataType,
			propName: this.propName,
			id: this.id,
			style: this.style,
			hitColor: this.hitColor?.serialize(),
			ui: this.ui
		};
	}
};
var TerminalType = /* @__PURE__ */ function(TerminalType) {
	TerminalType[TerminalType["IN"] = 1] = "IN";
	TerminalType[TerminalType["OUT"] = 2] = "OUT";
	return TerminalType;
}({});
var DefaultTerminalStyle = () => {
	return {
		radius: 4,
		color: "#888",
		borderColor: "#222",
		shadowBlur: 0,
		shadowColor: "#ccc",
		focusColor: "#00ff00",
		outerFocusColor: "#bbbbbb80"
	};
};
var DefaultTerminalOptions = () => {
	return {
		name: "",
		style: {}
	};
};
//#endregion
//#region src/ui/ui-node.ts
var UINode = class extends Hooks {
	get context() {
		return this.node.context;
	}
	get offUIContext() {
		return this.node.offUIContext;
	}
	get disabled() {
		return this._disabled;
	}
	set disabled(disabled) {
		this._disabled = disabled;
		this.children.forEach((child) => child.disabled = disabled);
	}
	get visible() {
		return this._visible;
	}
	set visible(value) {
		this._visible = value;
		this.node.ui?.update();
	}
	constructor() {
		super();
		this.width = 0;
		this.height = 0;
		this.children = [];
		this.disabled = false;
	}
	static create(type, node, options = DefaultUINodeOptions(node)) {
		options.style = {
			...node.flow?.flowConnect.getDefaultStyle("ui", type) || {},
			...options.style || {}
		};
		const uiNode = new (FlowConnect.getRegistered("ui", type))(node, options);
		const { hitColor, id = uuid(), visible = true, propName, position = Vector.Zero() } = options;
		uiNode.type = type;
		uiNode.node = node;
		uiNode.setHitColor(hitColor);
		uiNode.id = id;
		uiNode._visible = visible;
		uiNode.position = position;
		uiNode.propName = propName;
		if (propName) node.watch(propName, (oldVal, newVal) => uiNode.onPropChange(oldVal, newVal));
		uiNode.created(options);
		return uiNode;
	}
	createTerminal(type, dataType, name) {
		if (type === TerminalType.IN && this.input || type === TerminalType.OUT && this.output) {
			Log.error("Terminal for UINode was already configured, ignoring terminal creation");
			return;
		}
		const terminal = Terminal.create(this.node, type, dataType, {
			name: name ?? "",
			ui: true
		});
		if (type === TerminalType.IN) {
			this.input = terminal;
			this.node.inputsUI.push(terminal);
			terminal.on("connect", () => this.disabled = true);
			terminal.on("disconnect", () => this.disabled = false);
		} else {
			this.output = terminal;
			this.node.outputsUI.push(terminal);
		}
		return terminal;
	}
	append(childs) {
		if (Array.isArray(childs)) this.children.push(...childs);
		else this.children.push(childs);
		this.update();
		this.node.ui?.update();
	}
	update() {
		this.reflow();
		this.call("update", this);
		this.children.forEach((child) => child.update());
	}
	updateRenderState() {
		if (this.node.renderState.nodeState === NodeUIState.MINIMIZED) return;
		let realPos = this.position.transform(this.node.flow.flowConnect.transform);
		this.renderState = intersects(0, 0, this.node.flow.flowConnect.canvasDimensions.width, this.node.flow.flowConnect.canvasDimensions.height, realPos.x, realPos.y, realPos.x + this.width * this.node.flow.flowConnect.scale, realPos.y + this.height * this.node.flow.flowConnect.scale);
		this.children.forEach((child) => child.updateRenderState());
	}
	setHitColor(hitColor) {
		if (!hitColor) {
			hitColor = Color.Random();
			while (this.node.uiNodes.get(hitColor.rgbaString) || this.node.terminals.get(hitColor.rgbaString)) hitColor = Color.Random();
		}
		this.hitColor = hitColor;
		this.node.uiNodes.set(this.hitColor.rgbaString, this);
	}
	render() {
		if (!this.visible) return;
		if (this.renderState === ViewPort.OUTSIDE) return;
		let context = this.context;
		if (this.node.renderState.lod === LOD.LOD1) {
			context.save();
			this.paintLOD1();
			context.restore();
		} else if (this.node.renderState.lod === LOD.LOD2) {
			context.save();
			this.paint();
			context.restore();
			this.offUIContext.save();
			this.offPaint();
			this.offUIContext.restore();
		} else if (this.type === "core/container") {
			context.save();
			this.paintLOD1();
			context.restore();
		}
		if (this.node.renderState.lod > 0) {
			if (this.input) this.input.render();
			if (this.output) this.output.render();
		}
		this.call("render", this);
		this.children.forEach((child) => child.render());
	}
	getProp() {
		return this.node.state[this.propName];
	}
	setProp(propValue) {
		this.node.state[this.propName] = propValue;
	}
	query(query) {
		let result = [];
		query = query.trim();
		let queue = [this];
		while (queue.length !== 0) {
			let curr = queue.shift();
			if (curr.type === query) result.push(curr);
			curr.children.forEach((child) => queue.push(child));
		}
		return result;
	}
	sendEvent(type, event) {
		if (this.disabled) return;
		const orgType = type;
		type = `on${type.split("-").map((part) => capitalize(part)).reduce((prev, curr) => prev + curr, "")}`;
		this[type](event);
		this.call(orgType, event);
	}
	onWheel(_) {}
	onEnter(_disabled) {}
	onExit(_disabled) {}
	onOver(_) {}
	onDown(_) {}
	onUp(_) {}
	onClick(_) {}
	onDrag(_) {}
	onContextMenu(_) {}
};
var DefaultUINodeOptions = (node) => {
	return {
		visible: true,
		style: {},
		propName: void 0,
		id: uuid(),
		hitColor: void 0,
		height: node.style?.rowHeight,
		position: Vector.Zero()
	};
};
//#endregion
//#region src/ui/container.ts
var Container = class extends UINode {
	constructor(_node, _options) {
		super();
		this.renderer = () => () => void 0;
	}
	created(options) {
		options = {
			...DefaultContainerOptions(this.node),
			...options
		};
		const { width, height, style = {} } = options;
		this.width = width ?? 0;
		this.height = height ?? (this.node.style?.padding ?? 0) * 2;
		this.contentWidth = (width ?? 0) - 2 * (this.node.style?.padding ?? 0);
		this.style = {
			...DefaultContainerStyle(),
			...style
		};
		this.position = this.node.position;
	}
	paint() {
		const context = this.context;
		const scopeFlowConnect = this.node.flow.flowConnect.getRegisteredRenderer("background");
		const scopeFlow = this.node.flow.renderers.background;
		const scopeNode = this.node.renderers.background;
		const scopeContainer = this.renderer;
		(scopeContainer && scopeContainer(this) || scopeNode && scopeNode(this) || scopeFlow && scopeFlow(this) || scopeFlowConnect && scopeFlowConnect(this) || this._paint)(context, this.getRenderParams(), this);
	}
	_paint(context, params, container) {
		context.shadowColor = container.style.shadowColor ?? "";
		context.shadowBlur = container.style.shadowBlur ?? 0;
		context.shadowOffsetX = container.style.shadowOffset?.x ?? 0;
		context.shadowOffsetY = container.style.shadowOffset?.y ?? 0;
		context.fillStyle = container.style.backgroundColor ?? "";
		context.strokeStyle = container.style.borderColor ?? "";
		context.lineWidth = container.style.borderWidth ?? 0;
		context.roundRect(params.position.x, params.position.y, params.width, params.height, 5);
		context.stroke();
		context.fill();
	}
	paintLOD1() {
		let context = this.context;
		context.fillStyle = this.style.backgroundColor ?? "";
		context.strokeStyle = this.style.borderColor ?? "";
		context.lineWidth = this.style.borderWidth ?? 0;
		context.roundRect(this.position.x, this.position.y + (this.node.style?.titleHeight ?? 0), this.width, this.height - (this.node.style?.titleHeight ?? 0), 5);
		context.stroke();
		context.fill();
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	getRenderParams() {
		let position = this.position.serialize();
		position.y += this.node.style?.titleHeight ?? 0;
		return {
			position,
			width: this.width,
			height: this.height - (this.node.style?.titleHeight ?? 0)
		};
	}
	reflow() {
		const nodeStyle = this.node.style;
		this.position = this.node.position;
		let terminalsDisplayHeight = Math.max(this.node.inputs.length, this.node.outputs.length) * (nodeStyle?.terminalRowHeight ?? 0) + (nodeStyle?.titleHeight ?? 0);
		let x = this.position.x + nodeStyle?.padding;
		let y = this.position.y + terminalsDisplayHeight;
		this.children.filter((child) => child.visible).forEach((child) => {
			y += nodeStyle?.spacing;
			let availableWidth = this.width - nodeStyle?.padding * 2;
			child.width = (child.width > availableWidth ? availableWidth : child.width) || availableWidth;
			if (child.width < availableWidth) {
				let childX;
				if (child.style.align === Align.Center) childX = this.position.x + this.width / 2 - child.width / 2;
				else if (child.style.align === Align.Right) childX = this.position.x + this.width - nodeStyle?.padding - child.width;
				else childX = x;
				child.position = Vector.create(childX, y);
			} else child.position = Vector.create(x, y);
			y += child.height;
		});
		this.height = y + nodeStyle?.padding - this.position.y;
	}
	onPropChange() {}
};
var DefaultContainerStyle = () => ({
	backgroundColor: "#ddd",
	shadowColor: "#666",
	shadowBlur: 3,
	shadowOffset: Vector.create(3, 3),
	borderWidth: 1,
	borderColor: "#444"
});
var DefaultContainerOptions = (node) => ({ width: node.width });
//#endregion
//#region src/core/node.ts
var Node = class Node extends Hooks {
	get style() {
		return this._style;
	}
	set style(style) {
		this._style = {
			...this._style,
			...style
		};
	}
	get height() {
		return this.ui?.height;
	}
	get context() {
		return this.flow?.flowConnect.context;
	}
	get offContext() {
		return this.flow?.flowConnect.offContext;
	}
	get offUIContext() {
		return this.flow?.flowConnect.offUIContext;
	}
	get position() {
		return this._position;
	}
	set position(position) {
		this._position = position;
		this.reflow();
		this.ui?.update();
		this.updateRenderState();
	}
	get zIndex() {
		return this._zIndex;
	}
	set zIndex(zIndex) {
		if (this.flow?.sortedNodes.remove(this)) {
			this._zIndex = zIndex;
			this.flow.sortedNodes.add(this);
		} else this._zIndex = zIndex;
	}
	get width() {
		return this._width;
	}
	set width(width) {
		this._width = width;
		if (this.ui) this.ui.width = width;
		this.ui?.update();
	}
	constructor() {
		super();
		this.renderers = {};
		this.focused = false;
		this.inputs = [];
		this.outputs = [];
		this.inputsUI = [];
		this.outputsUI = [];
		this.renderState = {
			viewport: ViewPort.INSIDE,
			nodeState: "Maximized",
			lod: LOD.LOD2
		};
		this._zIndex = 0;
		this.stateObserver = new Hooks();
		this.uiNodes = /* @__PURE__ */ new Map();
		this.terminals = /* @__PURE__ */ new Map();
		this.nodeButtons = /* @__PURE__ */ new Map();
	}
	static create(type, flow, position, options = DefaultNodeOptions(), isDeserialized = false) {
		const node = new (FlowConnect.getRegistered("node", type))(flow, options);
		const { name = "New Node", width = 100, style = {}, id = uuid(), state = {}, hitColor, inputs, outputs } = options;
		node.flow = flow;
		node.type = type;
		node.name = name;
		node._width = width;
		node.style = {
			...DefaultNodeStyle(),
			...flow.flowConnect.getDefaultStyle("node", type) || {},
			...style
		};
		node.id = id;
		node.state = state;
		node._position = position;
		node.ui = node.createUI("core/container", { width: node.width });
		node.setHitColor(hitColor);
		if (inputs && outputs) node.setupTerminals(inputs, outputs);
		node.reflow();
		node.ui.update();
		node.addNodeButton((n) => n.toggle(), Node.renderControlButton, Align.Left);
		node.on("transform", (n) => n.updateRenderState());
		!isDeserialized && node.setupIO(options);
		node.created(options);
		node.setupState(node.state);
		node.ui.update();
		node.reflow();
		return node;
	}
	setupTerminals(inputs, outputs) {
		inputs && this.inputs.push(...inputs.map((input) => Terminal.create(this, TerminalType.IN, input.dataType, {
			name: input.name,
			id: input.id,
			hitColor: input.hitColor ? Color.create(input.hitColor) : void 0
		})));
		outputs && this.outputs.push(...outputs.map((output) => Terminal.create(this, TerminalType.OUT, output.dataType, {
			name: output.name,
			id: output.id,
			hitColor: output.hitColor ? Color.create(output.hitColor) : void 0
		})));
	}
	setupState(state) {
		this.state = new Proxy({}, { set: (target, prop, value) => {
			let oldValue = target[prop];
			target[prop] = value;
			this.stateObserver.call(prop, oldValue, value);
			return true;
		} });
		Object.keys(state).forEach((key) => {
			if (this.state) this.state[key] = state[key];
		});
	}
	watch(propName, callback) {
		if (typeof this.state?.[propName] !== "undefined") return this.stateObserver.on(propName, callback);
		else Log.error(`Cannot watch prop '${propName}', prop not found`);
	}
	unwatch(propName, id) {
		if (typeof this.state?.[propName] !== "undefined") this.stateObserver.off(propName, id);
		else Log.error(`Cannot unwatch prop '${propName}', prop not found`);
	}
	setHitColor(hitColor) {
		if (!this.flow) return;
		if (!hitColor) {
			hitColor = Color.Random();
			while (this.flow.nodeHitColors.get(hitColor.rgbaString)) hitColor = Color.Random();
		}
		this.hitColor = hitColor;
		this.flow.nodeHitColors.set(this.hitColor.rgbaString, this);
	}
	addNodeButton(onClick, render, align) {
		if (typeof this.width !== "number") return;
		let newNodeButton = new NodeButton(this, onClick, render, align);
		const nodeBtnSize = this.style?.nodeButtonSize ?? 0;
		const nodeBtnSpacing = this.style?.nodeButtonSpacing ?? 0;
		let noOfButtons = [...this.nodeButtons.values()].filter((nodeButton) => nodeButton.align === newNodeButton.align).length - 1;
		let deltaX;
		if (align === Align.Left) deltaX = noOfButtons * (nodeBtnSize + nodeBtnSpacing);
		else deltaX = this.width - noOfButtons * (nodeBtnSize + nodeBtnSpacing) - nodeBtnSize;
		newNodeButton.deltaX = deltaX;
		return newNodeButton;
	}
	reflow() {
		if (!this.position) return;
		const termRowHeight = this.style?.terminalRowHeight ?? 0;
		const padding = this.style?.padding ?? 0;
		const titleHeight = this.style?.titleHeight ?? 0;
		let y = this.position.y + termRowHeight / 2 + padding / 2 + titleHeight;
		if (this.inputs.length > this.outputs.length) {
			this.recalculateInputTerminals(y);
			y = this.position.y + this.inputs.length * termRowHeight / 2 - this.outputs.length * termRowHeight / 2 + termRowHeight / 2 + padding / 2 + titleHeight;
			this.recalculateOutputTerminals(y);
		} else {
			this.recalculateOutputTerminals(y);
			y = this.position.y + this.outputs.length * termRowHeight / 2 - this.inputs.length * termRowHeight / 2 + termRowHeight / 2 + padding / 2 + titleHeight;
			this.recalculateInputTerminals(y);
		}
	}
	updateRenderState() {
		if (!this.position || !this.flow || !this.width) return;
		let realPos = this.position.transform(this.flow.flowConnect.transform);
		this.renderState.viewport = intersects(0, 0, this.flow.flowConnect.canvasDimensions.width, this.flow.flowConnect.canvasDimensions.height, realPos.x, realPos.y, realPos.x + this.width * this.flow.flowConnect.scale, realPos.y + ((this.renderState.nodeState === "Maximized" ? this.ui?.height : this.style?.titleHeight) ?? 0) * this.flow.flowConnect.scale);
		if (this.flow.flowConnect.scale > .6) this.renderState.lod = LOD.LOD2;
		else if (this.flow.flowConnect.scale <= .6 && this.flow.flowConnect.scale > .3) this.renderState.lod = LOD.LOD1;
		else this.renderState.lod = LOD.LOD0;
		if (this.renderState.viewport === ViewPort.INTERSECT) this.ui?.updateRenderState();
	}
	recalculateInputTerminals(y) {
		if (!this.position) return;
		this.inputs.forEach((terminal) => {
			terminal.position.x = this.position.x - (this.style?.terminalStripMargin ?? 0) - (terminal.style?.radius ?? 0);
			terminal.position.y = y;
			y += this.style?.terminalRowHeight ?? 0;
		});
	}
	recalculateOutputTerminals(y) {
		if (!this.position) return;
		this.outputs.forEach((terminal) => {
			terminal.position.x = this.position.x + (this.ui?.width ?? 0) + (this.style?.terminalStripMargin ?? 0) + (terminal.style?.radius ?? 0);
			terminal.position.y = y;
			y += this.style?.terminalRowHeight ?? 0;
		});
	}
	getHitTerminal(hitColor, screenPosition, realPosition) {
		if (!this.flow || !this.position) return;
		let hitTerminal = void 0;
		realPosition = realPosition.transform(this.flow.flowConnect.transform);
		let thisRealPosition = this.position.transform(this.flow.flowConnect.transform);
		if (this.inputs.length + this.inputsUI.length > 0 && realPosition.x < thisRealPosition.x || this.outputs.length + this.outputsUI.length > 0 && realPosition.x > thisRealPosition.x + (this.ui?.width ?? 0) * this.flow.flowConnect.scale) hitTerminal = this.terminals.get(hitColor);
		if (this.currHitTerminal && this.currHitTerminal !== hitTerminal) {
			this.currHitTerminal.onExit(screenPosition, realPosition);
			hitTerminal?.onEnter(screenPosition, realPosition);
		}
		return hitTerminal;
	}
	getHitUINode(hitColor) {
		let uiNode = this.uiNodes.get(hitColor);
		if (uiNode instanceof Container) return;
		return uiNode;
	}
	getHitNodeButton(hitColor) {
		return this.nodeButtons.get(hitColor);
	}
	run() {
		if (!this.flow) return;
		if (this.flow.state === FlowState.Stopped) return;
		const inputs = this.inputs.map((terminal) => terminal.connectors.length > 0 ? terminal.connectors[0].data : void 0);
		this.process(inputs);
		this.call("process", this, inputs);
	}
	render() {
		if (!this.context || !this.flow || !this.offContext) return;
		if (this.renderState.viewport === ViewPort.OUTSIDE) return;
		if (this.renderState.nodeState === "Maximized") this.ui?.render();
		let context = this.context;
		context.save();
		this.renderTerminals(context);
		this.renderName(context);
		this.renderFocused(context);
		let scopeFlowConnect = this.flow.flowConnect.getRegisteredRenderer("node");
		let scopeFlow = this.flow.renderers.node;
		let scopeNode = this.renderers.node;
		(scopeNode && scopeNode(this) || scopeFlow && scopeFlow(this) || scopeFlowConnect && scopeFlowConnect(this) || this._render)(context, this.getRenderParams(), this);
		context.restore();
		this.nodeButtons.forEach((nodeButton) => nodeButton.render());
		this.offContext.save();
		this._offRender();
		this.offContext.restore();
		this.call("render", this);
	}
	renderTerminals(context) {
		if (this.renderState.nodeState === "Maximized") {
			if (this.renderState.lod > 0) {
				this.inputs.forEach((terminal) => terminal.render());
				this.outputs.forEach((terminal) => terminal.render());
			}
			context.fillStyle = this.style?.color ?? "";
			context.font = (this.style?.fontSize ?? 0) + " " + (this.style?.font ?? "");
			context.textBaseline = "middle";
			this.inputs.forEach((terminal) => {
				if (terminal.name) context.fillText(terminal.name, terminal.position.x + (terminal.style?.radius ?? 0) + (this.style?.terminalStripMargin ?? 0) + (this.style?.padding ?? 0), terminal.position.y);
			});
			this.outputs.forEach((terminal) => {
				if (terminal.name) context.fillText(terminal.name, terminal.position.x - (terminal.style?.radius ?? 0) - (this.style?.terminalStripMargin ?? 0) - (this.style?.padding ?? 0) - context.measureText(terminal.name).width, terminal.position.y);
			});
		} else {
			context.fillStyle = this.style?.minimizedTerminalColor ?? "";
			if (this.inputs.length + this.inputsUI.length > 0) {
				let radius = this.inputs.length > 0 ? this.inputs[0].style?.radius : this.inputsUI[0].style?.radius;
				context.fillRect(this.position.x - (this.style?.terminalStripMargin ?? 0) - (radius ?? 0) * 2, this.position.y + (this.style?.titleHeight ?? 0) / 2 - (radius ?? 0), (radius ?? 0) * 2, (radius ?? 0) * 2);
			}
			if (this.outputs.length + this.outputsUI.length > 0) {
				let radius = this.outputs.length > 0 ? this.outputs[0].style?.radius : this.outputsUI[0].style?.radius;
				context.fillRect(this.position.x + this.width + (this.style?.terminalStripMargin ?? 0), this.position.y + (this.style?.titleHeight ?? 0) / 2 - (radius ?? 0), (radius ?? 0) * 2, (radius ?? 0) * 2);
			}
		}
	}
	renderName(context) {
		context.fillStyle = this.style?.titleColor ?? "";
		context.font = (this.style?.titleFontSize ?? 0) + " " + (this.style?.titleFont ?? "");
		context.textBaseline = "middle";
		context.fillText(this.name ?? "", this.position.x + (this.ui?.width ?? 0) / 2 - context.measureText(this.name ?? "").width / 2, this.position.y + (this.style?.titleHeight ?? 0) / 2);
	}
	renderFocused(context) {
		if (this.focused) {
			context.strokeStyle = this.style?.outlineColor ?? "";
			context.lineWidth = 2;
			let inputTerminalsWidth;
			if (this.inputs.length === 0) inputTerminalsWidth = this.inputsUI.length === 0 ? 0 : (this.inputsUI[0].style?.radius ?? 0) * 2;
			else inputTerminalsWidth = (this.inputs[0].style?.radius ?? 0) * 2;
			inputTerminalsWidth += (this.style?.terminalStripMargin ?? 0) * 2;
			let outputTerminalsWidth;
			if (this.outputs.length === 0) outputTerminalsWidth = this.outputsUI.length === 0 ? 0 : (this.outputsUI[0].style?.radius ?? 0) * 2;
			else outputTerminalsWidth = (this.outputs[0].style?.radius ?? 0) * 2;
			outputTerminalsWidth += (this.style?.terminalStripMargin ?? 0) * 2;
			context.strokeRoundRect(this.position.x - inputTerminalsWidth, this.position.y, this.width + inputTerminalsWidth + outputTerminalsWidth, (this.renderState.nodeState === "Maximized" ? (this.ui?.height ?? 0) + (this.style?.padding ?? 0) : this.style?.titleHeight) ?? 0, 4);
		}
	}
	_render() {}
	_offRender() {
		if (!this.offContext || !this.position) return;
		this.offContext.fillStyle = this.hitColor?.rgbaCSSString ?? "";
		let x = this.position.x;
		let y = this.position.y;
		let inputTerminalsStripWidth = 0, outputTerminalsStripWidth = 0;
		if (this.inputs.length + this.inputsUI.length !== 0) {
			let radius = this.inputs.length > 0 ? this.inputs[0].style?.radius : this.inputsUI[0].style?.radius;
			x -= (this.style?.terminalStripMargin ?? 0) + (radius ?? 0) * 2;
			inputTerminalsStripWidth = (radius ?? 0) * 2 + (this.style?.terminalStripMargin ?? 0);
		}
		if (this.outputs.length + this.outputsUI.length !== 0) outputTerminalsStripWidth = ((this.outputs.length > 0 ? this.outputs[0].style?.radius : this.outputsUI[0].style?.radius) ?? 0) * 2 + (this.style?.terminalStripMargin ?? 0);
		this.offContext.fillRect(x, y, (this.ui?.width ?? 0) + inputTerminalsStripWidth + outputTerminalsStripWidth, (this.renderState.nodeState === "Maximized" ? this.ui?.height : this.style?.titleHeight) ?? 0);
	}
	static renderControlButton(context, params, nodeButton) {
		if (!params) return;
		let style = nodeButton.node.style;
		context.fillStyle = style?.maximizeButtonColor ?? "";
		context.fillRect(params.position.x, params.position.y, style?.nodeButtonSize ?? 0, style?.nodeButtonSize ?? 0);
	}
	getRenderParams() {
		if (!this.position || !this.ui || typeof this.width !== "number") return;
		return {
			position: this.position.serialize(),
			width: this.width,
			height: this.ui.height,
			focus: this.focused
		};
	}
	addTerminals(terminals) {
		terminals?.forEach((terminal) => this.addTerminal(terminal));
	}
	addTerminal(terminal) {
		let t;
		if (!(terminal instanceof Terminal) && terminal.type && terminal.dataType && terminal.name) t = Terminal.create(this, terminal.type, terminal.dataType, {
			name: terminal.name,
			propName: terminal.propName,
			style: terminal.style,
			id: terminal.id,
			hitColor: terminal.hitColor ? Color.create(terminal.hitColor) : void 0
		});
		else t = terminal;
		(terminal.type === TerminalType.IN ? this.inputs : this.outputs).push(t);
		this.ui?.update();
		this.reflow();
		return t;
	}
	removeTerminal(terminal) {
		let type = terminal.type;
		let index = type === TerminalType.IN ? this.inputs.indexOf(terminal) : this.outputs.indexOf(terminal);
		if (index < 0) {
			Log.error("Cannot remove terminal, terminal not found");
			return;
		}
		terminal.disconnect();
		if (type === TerminalType.IN) this.inputs.splice(index, 1);
		else this.outputs.splice(index, 1);
		terminal.offAll();
		this.ui?.update();
		this.reflow();
	}
	getInput(terminal) {
		if (typeof terminal === "string") {
			let inputTerminal = this.inputs.find((currTerm) => currTerm.name === terminal);
			if (inputTerminal) return inputTerminal.getData();
		} else if (this.inputs[terminal]) return this.inputs[terminal].getData();
	}
	getInputs() {
		return this.inputs.map((terminal) => terminal.getData());
	}
	setOutputs(outputs, data) {
		if (!this.flow) return;
		if (typeof outputs === "string") {
			let outputTerminal = this.outputs.find((term) => term.name === outputs);
			if (outputTerminal) outputTerminal.setData(data);
		} else if (typeof outputs === "number") {
			if (this.outputs[outputs]) this.outputs[outputs].setData(data);
		} else {
			let outputData = /* @__PURE__ */ new Map();
			Object.entries(outputs).forEach((entry) => {
				let terminal = this.outputs.find((term) => term.name === entry[0]);
				if (terminal) outputData.set(terminal, entry[1]);
				else throw Log.error("Terminal '" + entry[0] + "' not found");
			});
			let groupedConnectors = /* @__PURE__ */ new Map();
			let outputDataIterator = outputData.keys();
			let curr = outputDataIterator.next().value;
			while (curr) {
				curr.connectors.forEach((connector) => {
					if (!connector.endNode) return;
					if (groupedConnectors.has(connector.endNode)) groupedConnectors.get(connector.endNode)?.push(connector);
					else groupedConnectors.set(connector.endNode, [connector]);
				});
				curr = outputDataIterator.next().value;
			}
			let gCntrsIterator = groupedConnectors.values();
			let connectors = gCntrsIterator.next().value ?? [];
			while (connectors) {
				for (let i = 1; i < connectors.length; i++) connectors[i].setData(outputData.get(connectors[i].start));
				connectors[0].data = outputData.get(connectors[0].start);
				connectors = gCntrsIterator.next().value ?? [];
			}
		}
		if (this.flow.state === FlowState.Idle) this.flow.executionGraph.start();
	}
	toggle() {
		this.renderState.nodeState = this.renderState.nodeState === "Maximized" ? "Minimized" : "Maximized";
	}
	dispose() {
		if (this.id) this.flow?.removeNode(this.id);
	}
	createUI(type, options) {
		return UINode.create(type, this, options);
	}
	onDown(screenPos, realPos) {
		if (!this.flow) return;
		this.call("down", this, screenPos, realPos);
		let hitColor = Color.rgbaToString(this.flow.flowConnect.offUIContext.getImageData(screenPos.x, screenPos.y, 1, 1).data);
		this.currHitUINode = this.getHitUINode(hitColor);
		this.currHitUINode && this.currHitUINode.sendEvent("down", {
			screenPos,
			realPos,
			target: this.currHitUINode
		});
		let hitTerminal = this.getHitTerminal(hitColor, screenPos, realPos);
		if (hitTerminal) {
			this.currHitTerminal = hitTerminal;
			this.currHitTerminal.onDown(screenPos, realPos);
		}
	}
	onOver(screenPos, realPos) {
		if (!this.flow) return;
		this.call("over", this, screenPos, realPos);
		let hitColor = Color.rgbaToString(this.flow.flowConnect.offUIContext.getImageData(screenPos.x, screenPos.y, 1, 1).data);
		let hitTerminal = this.getHitTerminal(hitColor, screenPos, realPos);
		if (hitTerminal !== this.prevHitTerminal) {
			this.prevHitTerminal && this.prevHitTerminal.onExit(screenPos, realPos);
			hitTerminal && hitTerminal.onEnter(screenPos, realPos);
		} else hitTerminal && !this.currHitTerminal && hitTerminal.onOver(screenPos, realPos);
		this.prevHitTerminal = hitTerminal;
		let hitUINode = this.getHitUINode(hitColor);
		if (hitUINode !== this.prevHitUINode) {
			this.prevHitUINode && this.prevHitUINode.sendEvent("exit", {
				screenPos,
				realPos,
				target: this.prevHitUINode
			});
			hitUINode && hitUINode.sendEvent("enter", {
				screenPos,
				realPos,
				target: hitUINode
			});
		} else hitUINode && !this.currHitUINode && hitUINode.sendEvent("over", {
			screenPos,
			realPos,
			target: hitUINode
		});
		this.prevHitUINode = hitUINode;
	}
	onEnter(screenPosition, realPosition) {
		this.call("enter", this, screenPosition, realPosition);
	}
	onExit(screenPosition, realPosition) {
		if (!this.flow) return;
		this.call("exit", this, screenPosition, realPosition);
		let hitColor = Color.rgbaToString(this.flow.flowConnect.offUIContext.getImageData(screenPosition.x, screenPosition.y, 1, 1).data);
		let hitTerminal = this.getHitTerminal(hitColor, screenPosition, realPosition);
		hitTerminal && hitTerminal.onExit(screenPosition, realPosition);
		this.prevHitTerminal && this.prevHitTerminal.onExit(screenPosition, realPosition);
		this.prevHitTerminal = void 0;
		this.currHitTerminal && this.currHitTerminal.onExit(screenPosition, realPosition);
	}
	onUp(screenPos, realPos) {
		if (!this.flow) return;
		this.call("up", this, screenPos, realPos);
		let hitColor = Color.rgbaToString(this.flow.flowConnect.offUIContext.getImageData(screenPos.x, screenPos.y, 1, 1).data);
		this.currHitUINode = void 0;
		let hitUINode = this.getHitUINode(hitColor);
		hitUINode && hitUINode.sendEvent("up", {
			screenPos: screenPos.clone(),
			realPos: realPos.clone(),
			target: hitUINode
		});
		let hitTerminal = this.getHitTerminal(hitColor, screenPos, realPos);
		hitTerminal && hitTerminal.onUp(screenPos, realPos);
	}
	onClick(screenPos, realPos) {
		if (!this.flow || !this.position) return;
		this.call("click", this, screenPos, realPos);
		let hitColor = Color.rgbaToString(this.flow.flowConnect.offUIContext.getImageData(screenPos.x, screenPos.y, 1, 1).data);
		if (realPos.y < this.position.y + (this.style?.titleHeight ?? 0) * this.flow.flowConnect.scale) {
			let hitNodeButton = this.getHitNodeButton(hitColor);
			hitNodeButton && hitNodeButton.onClick(this);
		} else {
			this.currHitTerminal && this.currHitTerminal.onClick(screenPos, realPos);
			let hitUINode = this.getHitUINode(hitColor);
			hitUINode && hitUINode.sendEvent("click", {
				screenPos: screenPos.clone(),
				realPos: realPos.clone(),
				target: hitUINode
			});
		}
	}
	onDrag(screenPos, realPos) {
		if (!this.flow) return;
		this.call("drag", this, screenPos, realPos);
		let hitColor = Color.rgbaToString(this.flow.flowConnect.offUIContext.getImageData(screenPos.x, screenPos.y, 1, 1).data);
		let hitUINodeWhileDragging = this.getHitUINode(hitColor);
		if (this.currHitUINode && this.currHitUINode.draggable) if (hitUINodeWhileDragging === this.currHitUINode) this.currHitUINode.sendEvent("drag", {
			screenPos,
			realPos,
			target: this.currHitUINode
		});
		else {
			this.currHitUINode.sendEvent("exit", {
				screenPos,
				realPos,
				target: this.currHitUINode
			});
			this.currHitUINode = void 0;
			this.flow.flowConnect.currHitNode = void 0;
			this.flow.flowConnect.pointers = [];
		}
	}
	onContextMenu(screenPos, realPos) {
		this.call("rightclick", this);
		if (this.currHitUINode) this.currHitUINode.sendEvent("context-menu", {
			screenPos,
			realPos,
			target: this.currHitUINode
		});
	}
	onWheel(direction, screenPos, realPos) {
		if (!this.flow) return;
		this.call("wheel", this, direction, screenPos, realPos);
		let hitColor = Color.rgbaToString(this.flow.flowConnect.offUIContext.getImageData(screenPos.x, screenPos.y, 1, 1).data);
		let hitUINode = this.getHitUINode(hitColor);
		hitUINode && hitUINode.zoomable && hitUINode.sendEvent("wheel", {
			screenPos,
			realPos,
			target: hitUINode,
			direction
		});
	}
	async serializeState(state, persist) {
		for (let key in state) if (state[key] instanceof File) if (persist) {
			const id = uuid();
			state[key] = {
				...await persist(id, state[key]),
				id: `raw##${id}`
			};
		} else state[key] = void 0;
		else if (state[key] instanceof Vector) state[key] = state[key].serialize();
		else if (state[key] instanceof AudioBuffer) state[key] = void 0;
		else if (typeof state[key] === "object") state[key] = await this.serializeState(state[key]);
		return state;
	}
	async serialize(persist) {
		const serializedState = await this.serializeState({ ...this.state }, persist);
		return Promise.resolve({
			id: this.id,
			name: this.name,
			type: this.type,
			position: this.position?.serialize(),
			width: this.width,
			state: serializedState,
			inputs: this.inputs.map((terminal) => terminal.serialize()),
			outputs: this.outputs.map((terminal) => terminal.serialize()),
			style: this.style,
			hitColor: this.hitColor?.serialize(),
			zIndex: this.zIndex,
			focused: this.focused,
			renderState: this.renderState
		});
	}
};
var NodeUIState = /* @__PURE__ */ function(NodeUIState) {
	NodeUIState["MAXIMIZED"] = "Maximized";
	NodeUIState["MINIMIZED"] = "Minimized";
	return NodeUIState;
}({});
var DefaultNodeStyle = () => ({
	font: "arial",
	fontSize: ".75rem",
	titleFont: "arial",
	titleFontSize: ".85rem",
	color: "#000",
	titleColor: "#000",
	maximizeButtonColor: "darkgrey",
	nodeButtonSize: 10,
	nodeButtonSpacing: 5,
	expandButtonColor: "#000",
	minimizedTerminalColor: "green",
	outlineColor: "#000",
	padding: 10,
	spacing: 10,
	rowHeight: 20,
	titleHeight: 29,
	terminalRowHeight: 24,
	terminalStripMargin: 8
});
var DefaultNodeOptions = () => ({
	name: "New Node",
	width: 100,
	style: {},
	state: {},
	id: uuid()
});
var NodeButton = class extends Hooks {
	constructor(node, onClick, renderer, align, style) {
		super();
		this.node = node;
		this.onClick = onClick;
		this.align = align;
		this.style = {};
		this.deltaX = 0;
		this.style = style ?? {};
		this.setHitColor();
		this.renderer = renderer;
	}
	setHitColor() {
		let color = Color.Random();
		while (this.node.nodeButtons.get(color.rgbaString)) color = Color.Random();
		this.hitColor = color;
		this.node.nodeButtons.set(this.hitColor.rgbaString, this);
	}
	render() {
		if (!this.node.context || !this.node.offUIContext) return;
		this.node.context.save();
		this.renderer(this.node.context, this.getRenderParams(), this);
		this.node.context.restore();
		this.node.offUIContext.save();
		this._offUIRender();
		this.node.offUIContext.restore();
		this.call("render", this);
	}
	getRenderParams() {
		if (!this.node.position) return;
		let position = this.node.position.serialize();
		position.x += this.deltaX;
		position.y += (this.node.style?.titleHeight ?? 0) / 2 - (this.node.style?.nodeButtonSize ?? 0) / 2;
		return { position };
	}
	_offUIRender() {
		if (!this.node.offUIContext || !this.node.position) return;
		this.node.offUIContext.fillStyle = this.hitColor?.rgbaCSSString ?? "";
		this.node.offUIContext.fillRect(this.node.position.x + this.deltaX, this.node.position.y + (this.node.style?.titleHeight ?? 0) / 2 - (this.node.style?.nodeButtonSize ?? 0) / 2, this.node.style?.nodeButtonSize ?? 0, this.node.style?.nodeButtonSize ?? 0);
	}
};
//#endregion
//#region src/core/group.ts
var Group = class Group extends Hooks {
	get name() {
		return this._name;
	}
	set name(name) {
		if (!name || name.trim() === "") this._name = "No Name";
		else this._name = name;
		this.computeTextMetrics();
	}
	get position() {
		return this._position;
	}
	set position(position) {
		this._position = position;
		this.updateRenderState();
		this.recomputeNodePositions();
	}
	constructor() {
		super();
		this.renderer = () => () => void 0;
		this.nodes = [];
		this.nodeDeltas = [];
		this.renderState = ViewPort.INSIDE;
	}
	static create(flow, position, options = DefaultGroupOptions()) {
		const group = new Group();
		const { name, width = 200, height = 200, id = uuid(), hitColor, style } = options;
		group.flow = flow;
		group._name = name;
		group.width = width;
		group.height = height;
		group.id = id;
		group.style = {
			...DefaultGroupStyle(),
			...flow.flowConnect.getDefaultStyle("group") || {},
			...style ?? {}
		};
		if (!group.style.color || !group.style.borderColor) {
			let colors = DefaultGroupColors.Random();
			group.style.borderColor = colors[0];
			group.style.color = colors[1];
		}
		group.setHitColor(hitColor);
		group.position = position;
		group.computeTextMetrics();
		group.on("transform", (g) => g.updateRenderState());
		return group;
	}
	add(node) {
		if (!this.flow || !this.position || !node || !node.position) return false;
		const scale = this.flow.flowConnect.scale;
		const transform = this.flow.flowConnect.transform;
		if (!Group.isNodeInside(this, node, scale, transform)) return false;
		node.group = this;
		this.nodes.push(node);
		this.nodeDeltas.push(node.position.subtract(this.position));
		return true;
	}
	remove(node) {
		let idx = this.nodes.findIndex((cNode) => cNode.id === node.id);
		if (idx < 0) return false;
		this.nodes.splice(idx, 1);
		this.nodeDeltas.splice(idx, 1);
		node.group = void 0;
		return true;
	}
	static isNodeInside(group, node, scale, transform) {
		if (!node.position || !group.position || typeof group.width !== "number" || typeof group.height !== "number" || typeof node.width !== "number" || typeof node.height !== "number") return false;
		let groupRealPos = group.position.transform(transform);
		let nodeRealPos = node.position.transform(transform);
		return intersects(groupRealPos.x, groupRealPos.y, groupRealPos.x + group.width * scale, groupRealPos.y + group.height * scale, nodeRealPos.x, nodeRealPos.y, nodeRealPos.x + node.width * scale, nodeRealPos.y + node.height * scale) === ViewPort.INSIDE;
	}
	setHitColor(hitColor) {
		if (!this.flow) return;
		if (!hitColor) {
			hitColor = Color.Random();
			while (this.flow.groupHitColors.get(hitColor.rgbaString)) hitColor = Color.Random();
		}
		this.hitColor = hitColor;
		this.flow.groupHitColors.set(this.hitColor.rgbaString, this);
	}
	computeTextMetrics() {
		if (!this.flow) return;
		let context = this.flow.flowConnect.context;
		context.font = (this.style?.fontSize ?? "") + " " + (this.style?.font ?? "");
		let metrics = context.measureText(this.name ?? "");
		this.textWidth = metrics.width;
		metrics = context.measureText("M");
		context.font = "";
		this.textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
		if (typeof this.textHeight === "undefined") {
			let d = document.createElement("span");
			d.textContent = "M";
			d.style.font = (this.style?.fontSize ?? "") + " " + (this.style?.font ?? "");
			document.body.appendChild(d);
			this.textHeight = d.offsetHeight;
			document.body.removeChild(d);
		}
	}
	updateRenderState() {
		if (!this.position || !this.flow) return;
		let realPos = this.position.transform(this.flow.flowConnect.transform);
		this.renderState = intersects(0, 0, this.flow.flowConnect.canvasDimensions.width, this.flow.flowConnect.canvasDimensions.height, realPos.x, realPos.y, realPos.x + (this.width ?? 0) * this.flow.flowConnect.scale, realPos.y + (this.height ?? 0) * this.flow.flowConnect.scale);
	}
	recomputeNodePositions() {
		this.nodes.forEach((node, index) => {
			node.position = this.position.add(this.nodeDeltas[index]);
		});
	}
	render() {
		if (!this.flow) return;
		if (this.renderState === ViewPort.OUTSIDE) return;
		let context = this.flow.flowConnect.context;
		context.save();
		let scopeFlowConnect = this.flow.flowConnect.getRegisteredRenderer("group");
		let scopeFlow = this.flow.renderers.group;
		let scopeGroup = this.renderer;
		(scopeGroup && scopeGroup(this) || scopeFlow && scopeFlow(this) || scopeFlowConnect && scopeFlowConnect(this) || this._render)(context, this.getRenderParams(), this);
		context.restore();
		this.flow.flowConnect.offGroupContext.save();
		this._offRender();
		this.flow.flowConnect.offGroupContext.restore();
		this.call("render", this);
	}
	_render(context, params, group) {
		context.strokeStyle = group.style?.borderColor ?? "";
		context.lineWidth = 2;
		context.fillStyle = group.style?.color ?? "";
		context.strokeRect(params.position.x, params.position.y, params.width, params.height);
		context.fillRect(params.position.x, params.position.y, params.width, params.height);
		context.fillStyle = group.style?.titleColor ?? "";
		context.textBaseline = "bottom";
		context.font = (group.style?.fontSize ?? "") + " " + (group.style?.font ?? "");
		context.fillText(group.name ?? "", params.position.x, params.position.y - 10);
	}
	_offRender() {
		if (!this.flow || !this.position) return;
		this.flow.flowConnect.offGroupContext.fillStyle = this.hitColor?.rgbaCSSString ?? "";
		this.flow.flowConnect.offGroupContext.fillRect(this.position.x, this.position.y, this.width ?? 0, this.height ?? 0);
		this.flow.flowConnect.offGroupContext.fillRect(this.position.x, this.position.y - (this.textHeight ?? 0) - 10, this.textWidth ?? 0, (this.textHeight ?? 0) + 10);
	}
	getRenderParams() {
		if (!this.position || typeof this.width !== "number" || typeof this.height !== "number") return;
		return {
			position: this.position.serialize(),
			width: this.width,
			height: this.height
		};
	}
	onClick(screenPosition, realPosition) {
		if (!this.position || !this.flow) return;
		this.call("click", this, screenPosition, realPosition);
		let thisRealPosition = this.position.transform(this.flow.flowConnect.transform);
		if (screenPosition.y < thisRealPosition.y) this.flow.flowConnect.showGenericInput(thisRealPosition.subtract(0, (10 + (this.textHeight ?? 0)) * this.flow.flowConnect.scale), this.name ?? "", {
			width: Math.max(this.textWidth ?? 0, 50) * this.flow.flowConnect.scale + "px",
			height: (this.textHeight ?? 0) * this.flow.flowConnect.scale + "px",
			fontSize: parseInt(this.style?.fontSize?.replace("px", "") ?? "0") * this.flow.flowConnect.scale + "px",
			fontFamily: this.style?.font ?? ""
		}, { type: "text" }, (value) => {
			this.name = value;
		});
	}
	serialize() {
		return {
			id: this.id,
			hitColor: this.hitColor?.serialize(),
			position: this.position?.serialize(),
			name: this.name,
			width: this.width,
			height: this.height,
			style: this.style,
			nodes: this.nodes.map((node) => node.id)
		};
	}
};
var DefaultGroupColors = {
	colors: [
		["rgba(239, 134, 119, 1)", "rgba(239, 134, 119, .5)"],
		["rgba(160, 231, 125, 1)", "rgba(160, 231, 125, .5)"],
		["rgba(130, 182, 217, 1)", "rgba(130, 182, 217, .5)"]
	],
	RED: () => DefaultGroupColors.colors[0],
	GREEN: () => DefaultGroupColors.colors[1],
	BLUE: () => DefaultGroupColors.colors[2],
	Random: () => DefaultGroupColors.colors[Math.floor(Math.random() * DefaultGroupColors.colors.length)]
};
var DefaultGroupStyle = () => {
	return {
		titleColor: "#000",
		fontSize: "16px",
		font: "arial"
	};
};
var DefaultGroupOptions = () => {
	return {
		name: "New Group",
		width: 200,
		height: 200,
		style: {},
		id: uuid()
	};
};
//#endregion
//#region src/core/subflow-node.ts
var SubFlowNode = class SubFlowNode extends Node {
	constructor(flow, options) {
		super();
		this.subFlow = options.subFlow;
		if (this.subFlow) this.subFlow.parentFlow = flow;
	}
	setupIO() {
		if (!this.subFlow) return;
		this.subFlow.inputs.forEach((tunnelNode) => {
			const { name, dataType } = tunnelNode.outputs[0];
			this.addTerminal({
				type: TerminalType.IN,
				name,
				dataType
			});
		});
		this.subFlow.outputs.forEach((tunnelNode) => {
			const { name, dataType } = tunnelNode.inputs[0];
			this.addTerminal({
				type: TerminalType.OUT,
				name,
				dataType
			});
		});
	}
	created() {
		if (!this.subFlow) return;
		this.subFlow.on("add-input", (_, tunnel) => {
			if (!tunnel.outputs[0].dataType || !tunnel.outputs[0].name) return;
			let proxyTerminal = Terminal.create(this, TerminalType.IN, tunnel.outputs[0].dataType, { name: tunnel.outputs[0].name });
			tunnel.proxyTerminal = proxyTerminal;
			this.addTerminal(proxyTerminal);
		});
		this.subFlow.on("add-output", (_, tunnel) => {
			if (!tunnel.inputs[0].dataType || !tunnel.inputs[0].name) return;
			let proxyTerminal = Terminal.create(this, TerminalType.OUT, tunnel.inputs[0].dataType, { name: tunnel.inputs[0].name });
			tunnel.proxyTerminal = proxyTerminal;
			this.addTerminal(proxyTerminal);
		});
		this.subFlow.inputs.forEach((inputNode, idx) => inputNode.proxyTerminal = this.inputs[idx]);
		this.subFlow.outputs.forEach((outputNode, idx) => outputNode.proxyTerminal = this.outputs[idx]);
		this.addNodeButton(() => {
			if (this.subFlow) this.flow?.flowConnect.render(this.subFlow);
		}, SubFlowNode.renderOpenButton, Align.Right);
	}
	process() {
		this.subFlow?.start();
	}
	static renderOpenButton(context, params, nodeButton) {
		if (!params) return;
		let style = nodeButton.node.style;
		const size = style?.nodeButtonSize ?? 0;
		context.strokeStyle = style?.expandButtonColor ?? "";
		context.beginPath();
		context.moveTo(params.position.x, params.position.y + size / 2);
		context.lineTo(params.position.x, params.position.y + size);
		context.lineTo(params.position.x + size, params.position.y);
		context.lineTo(params.position.x + size, params.position.y + size / 2);
		context.moveTo(params.position.x + size, params.position.y);
		context.lineTo(params.position.x + size / 2, params.position.y);
		context.moveTo(params.position.x, params.position.y + size);
		context.lineTo(params.position.x + size / 2, params.position.y + size);
		context.closePath();
		context.stroke();
	}
	async serialize(persist) {
		const serializedNode = await super.serialize(persist);
		const subFlow = await this.subFlow?.serialize(persist);
		return {
			...serializedNode,
			subFlow
		};
	}
};
//#endregion
//#region src/core/graph.ts
var Graph = class {
	constructor() {
		this.state = FlowState.Stopped;
		this.nodes = [];
		this.graphNodes = /* @__PURE__ */ new Map();
		this.dirtyNodes = /* @__PURE__ */ new Map();
	}
	add(data) {
		if (!data.id) return;
		if (!this.nodes[0]) this.nodes.push([]);
		let graphNode = new GraphNode(data);
		this.nodes[0].push(graphNode);
		this.graphNodes.set(data.id, graphNode);
	}
	remove(data) {
		if (!data.id) return;
		let graphNode = this.graphNodes.get(data.id);
		if (!graphNode) return;
		this.dirtyNodes.delete(graphNode.id);
		this.graphNodes.delete(data.id);
		this.nodes[0].splice(this.nodes[0].findIndex((node) => node.id === graphNode.id), 1);
	}
	connect(sourceNode, destinationNode) {
		if (!sourceNode.id || !destinationNode.id) return;
		let startGraphNode = this.graphNodes.get(sourceNode.id);
		let endGraphNode = this.graphNodes.get(destinationNode.id);
		if (!startGraphNode || !endGraphNode) return;
		if (!startGraphNode.childs.includes(endGraphNode)) {
			startGraphNode.childs.push(endGraphNode);
			if (endGraphNode.order <= startGraphNode.order) this.updateOrder(endGraphNode, startGraphNode.order + 1);
		}
	}
	disconnect(sourceNode, destinationNode) {
		if (!sourceNode.id || !destinationNode.id) return;
		let sourceGraphNode = this.graphNodes.get(sourceNode.id);
		let destinationGraphNode = this.graphNodes.get(destinationNode.id);
		let connectedEndNodes = /* @__PURE__ */ new Set();
		sourceNode.outputs.forEach((terminal) => terminal.connectors.forEach((connector) => connector.endNode && connectedEndNodes.add(connector.endNode)));
		if (connectedEndNodes.has(destinationNode)) return;
		if (!sourceGraphNode || !destinationGraphNode) return;
		sourceGraphNode.childs.splice(sourceGraphNode.childs.indexOf(destinationGraphNode), 1);
		let maxOrderOfConnectedStartNodes = destinationNode.inputs.filter((terminal) => terminal.connectors.length > 0).map((terminal) => this.graphNodes.get(terminal.connectors[0].startNode?.id).order).reduce((acc, curr) => Math.max(acc, curr), 0);
		this.updateOrder(destinationGraphNode, maxOrderOfConnectedStartNodes);
	}
	updateOrder(root, order) {
		this._updateOrder(root, order);
		let queue = [];
		queue.push(root);
		while (queue.length !== 0) {
			let currNode = queue.shift();
			currNode?.childs.forEach((child) => {
				if (child.order <= currNode.order) this._updateOrder(child, currNode.order + 1);
				if (!queue.includes(child)) queue.push(child);
			});
		}
	}
	_updateOrder(graphNode, order) {
		this.nodes[graphNode.order].splice(this.nodes[graphNode.order].indexOf(graphNode), 1);
		if (!this.nodes[order]) this.nodes[order] = [];
		graphNode.order = order;
		this.nodes[order].push(graphNode);
	}
	canConnect(sourceNode, destinationNode) {
		if (this.graphNodes.get(destinationNode.id)?.childs.includes(this.graphNodes.get(sourceNode.id))) return false;
		return true;
	}
	async start() {
		if (this.state === FlowState.Stopped) {
			this.state = FlowState.Running;
			if (this.nodes[0]) try {
				await this.runAll(this.nodes[0]);
				while (this.dirtyNodes.size !== 0) await this.runAll(this.lowestDirty([...this.dirtyNodes.values()]));
			} catch (error) {
				Log.error("Error while executing graph", error);
				this.state = FlowState.Stopped;
				return;
			}
		} else if (this.state === FlowState.Idle) {
			this.state = FlowState.Running;
			try {
				while (this.dirtyNodes.size !== 0) await this.runAll(this.lowestDirty([...this.dirtyNodes.values()]));
			} catch (error) {
				Log.error("Error while executing graph", error);
				this.state = FlowState.Stopped;
				return;
			}
		}
		this.state = FlowState.Idle;
	}
	stop() {
		this.state = FlowState.Stopped;
	}
	async runAll(graphNodes) {
		await Promise.all(graphNodes.map((graphNode) => new Promise((resolve) => {
			graphNode.flowNode.run();
			this.clearDirty(graphNode);
			resolve();
		})));
	}
	setDirty(node) {
		if (!node.id) return;
		let graphNode = node instanceof Node ? this.graphNodes.get(node.id) : node;
		if (!graphNode) return;
		this.dirtyNodes.set(graphNode.id, graphNode);
	}
	clearDirty(node) {
		if (!node.id) return;
		let graphNode = node instanceof Node ? this.graphNodes.get(node.id) : node;
		if (!graphNode) return;
		this.dirtyNodes.delete(graphNode.id);
	}
	lowestDirty(graphNodes) {
		let lowestOrder = Math.min(...graphNodes.map((graphNode) => graphNode.order));
		return graphNodes.filter((graphNode) => graphNode.order === lowestOrder);
	}
	propagate(root, callback) {
		if (!root.id) return;
		let start = root instanceof Node ? this.graphNodes.get(root.id) : root;
		if (!start) return;
		let queue = new List();
		queue.append(start);
		while (queue.length !== 0) {
			let currGNode = queue.removeFirst();
			if (!currGNode) continue;
			callback(currGNode.flowNode);
			currGNode.childs.forEach((child) => queue.append(child));
		}
	}
	debugNode(node, indent) {
		Log.info(`${indent}[${node.flowNode.name}, ${node.order}]`);
		node.childs.forEach((child) => this.debugNode(child, indent + "  "));
	}
	debugGraph() {
		this.nodes[0].forEach((graphNode) => {
			this.debugNode(graphNode, "");
		});
	}
};
var GraphNode = class {
	constructor(flowNode, order, id) {
		this.flowNode = flowNode;
		this.childs = [];
		this.order = order ? order : 0;
		this.id = id ? id : uuid();
	}
};
//#endregion
//#region src/core/flow.ts
/** A Flow is a set of Nodes, Connectors and Groups, it can also contain SubFlowNodes thereby creating a tree of Flows.
*  ![](media://example.png)
*/
var Flow = class Flow extends Hooks {
	get state() {
		return this.executionGraph.state;
	}
	constructor(flowConnect) {
		super();
		this.flowConnect = flowConnect;
		this.globalEvents = new Hooks();
		this.listeners = {};
		this.renderers = {};
		this.nodes = /* @__PURE__ */ new Map();
		this.groups = [];
		this.connectors = /* @__PURE__ */ new Map();
		this.nodeHitColors = /* @__PURE__ */ new Map();
		this.groupHitColors = /* @__PURE__ */ new Map();
		this.sortedNodes = new AVLTree((a, b) => a.zIndex - b.zIndex, (node) => node.id);
		this.inputs = [];
		this.outputs = [];
		this.executionGraph = new Graph();
		flowConnect.on("tick", () => {
			if (this.state === "Running") this.call("tick", this);
		});
	}
	static create(flowConnect, options = DefaultFlowOptions()) {
		const flow = new Flow(flowConnect);
		const { name = "New Flow", rules = {}, ruleColors = {}, id = uuid() } = options;
		flow.name = name;
		flow.rules = {
			...DefaultRules(),
			...rules
		};
		flow.ruleColors = {
			...DefaultRuleColors(),
			...ruleColors
		};
		flow.id = id;
		return flow;
	}
	transform() {
		[...this.nodes.values()].forEach((node) => node.call("transform", node));
		[...this.groups.values()].forEach((group) => group.call("transform", group));
	}
	existsInFlow(flow) {
		for (let node of [...this.nodes.values()]) if (node.subFlow === flow) return true;
		else if (node.subFlow) return node.subFlow?.existsInFlow(flow);
		return false;
	}
	addInput(name, dataType, position = Vector.Zero()) {
		return this._addIO("input", name, position, dataType);
	}
	addOutput(name, dataType, position) {
		return this._addIO("output", name, position, dataType);
	}
	_addIO(type, name, position, dataType) {
		const ioNode = this.createNode("core/tunnel", position, {
			tunnelType: type,
			name: capitalize(type),
			tunnelName: name,
			tunnelDataType: dataType
		});
		(type === "input" ? this.inputs : this.outputs).push(ioNode);
		this.call(`add-${type}`, this, ioNode);
		return ioNode;
	}
	addSubFlow(subFlow, position = Vector.Zero()) {
		if (subFlow.parentFlow) {
			Log.error("Provided flow is already a sub-flow, a sub-flow cannot have multiple parent flows");
			return;
		}
		return this.createNode("core/subflow", position, {
			name: subFlow.name,
			width: 150,
			subFlow
		});
	}
	removeSubFlow(arg1) {
		let subFlowNode = void 0;
		if (arg1 instanceof Flow) {
			subFlowNode = [...this.nodes.values()].filter((node) => node.type === "core/subflow").find((node) => node.subFlow === arg1);
			this.removeNode(subFlowNode);
		} else if (arg1 instanceof SubFlowNode) subFlowNode = arg1;
		this.removeNode(subFlowNode);
	}
	createNode(type, position, options) {
		const node = Node.create(type, this, position, options);
		this.addNode(node);
		return node;
	}
	_createNode(type, position, options) {
		const node = Node.create(type, this, position, options, true);
		this.addNode(node);
		return node;
	}
	addNode(node) {
		this.nodes.set(node.id, node);
		this.sortedNodes.add(node);
		this.executionGraph.add(node);
	}
	removeNode(nodeOrID) {
		let node = typeof nodeOrID === "string" ? this.nodes.get(nodeOrID) : nodeOrID;
		if (!node) return;
		[
			...node.inputs,
			...node.outputs,
			...node.inputsUI,
			...node.outputsUI
		].filter((terminal) => terminal.isConnected()).forEach((terminal) => terminal.disconnect());
		if (node.group) node.group.nodes.splice(node.group.nodes.findIndex((currNode) => currNode.id === node.id), 1);
		this.nodes.delete(node.id);
		this.sortedNodes.remove(node);
		this.executionGraph.remove(node);
	}
	removeConnector(id) {
		if (this.connectors.get(id) === this.floatingConnector) this.floatingConnector = void 0;
		this.connectors.delete(id);
	}
	removeAllFocus() {
		this.nodes.forEach((node) => node.focused = false);
	}
	render() {
		this.groups.forEach((group) => group.render());
		this.connectors.forEach((connector) => connector.render());
		this.sortedNodes.forEach((node) => node.render());
		this.call("render", this);
	}
	start() {
		if (this.state !== "Stopped") return;
		this.executionGraph.start();
		this.call("start", this);
	}
	stop() {
		if (this.state === "Stopped") return;
		for (let order = this.executionGraph.nodes.length - 1; order >= 0; order -= 1) this.executionGraph.nodes[order].filter((graphNode) => graphNode.flowNode instanceof SubFlowNode).map((graphNode) => graphNode.flowNode).forEach((subFlowNode) => subFlowNode.subFlow?.stop());
		this.executionGraph.stop();
		this.call("stop", this);
	}
	setFloatingConnector(floatingPos, fixedEnd, type) {
		const start = type === "left" ? fixedEnd : void 0;
		const end = type === "left" ? void 0 : fixedEnd;
		let connector = Connector.create(this, start, end, { floatingTip: floatingPos });
		this.connectors.set(connector.id, connector);
		this.floatingConnector = connector;
	}
	removeFloatingConnector() {
		if (!this.floatingConnector) return;
		let terminal = void 0;
		if (this.floatingConnector.start) terminal = this.floatingConnector.start;
		else terminal = this.floatingConnector.end;
		if (terminal?.node?.currHitTerminal) {
			terminal.node.currHitTerminal.onExit();
			terminal.node.currHitTerminal = void 0;
		}
		this.removeConnector(this.floatingConnector.id);
		return terminal;
	}
	static async deSerializeState(state, receive) {
		for (let key in state) if (isVector(state[key])) state[key] = Vector.create(state[key].x, state[key].y);
		else if (Array.isArray(state[key]) && state[key].length > 0 && isVector(state[key][0])) state[key] = state[key].map((sv) => Vector.create(sv.x, sv.y));
		else if (typeof state[key] === "object" && state[key] && state[key].id?.startsWith("raw##")) if (receive) state[key] = await receive({
			...state[key],
			id: state[key].id.replace("raw##", "")
		});
		else state[key] = void 0;
		return state;
	}
	async serialize(persist) {
		const nodes = await Promise.all([...this.nodes.values()].map((node) => node.serialize(persist)));
		const inputs = await Promise.all(this.inputs.map((input) => input.serialize()));
		const outputs = await Promise.all(this.outputs.map((output) => output.serialize()));
		const ruleColors = {};
		Object.keys(this.ruleColors).forEach((key) => ruleColors[key] = (this.ruleColors[key] ?? Color.Random()).serialize());
		return Promise.resolve({
			version: this.flowConnect.version,
			id: this.id,
			name: this.name,
			rules: this.rules,
			ruleColors,
			nodes,
			groups: this.groups.map((group) => group.serialize()),
			connectors: [...this.connectors.values()].map((connector) => connector.serialize()),
			inputs,
			outputs
		});
	}
	static async deSerialize(flowConnect, data, receive) {
		if (!data.name || !data.rules) return;
		const ruleColors = {};
		Object.keys(data.ruleColors).forEach((key) => ruleColors[key] = Color.create(data.ruleColors[key]));
		let flow = Flow.create(flowConnect, {
			name: data.name,
			rules: data.rules,
			ruleColors,
			id: data.id
		});
		for (let serializedNode of data.nodes) {
			const state = await this.deSerializeState(serializedNode.state, receive);
			if (serializedNode.type === "core/subflow") serializedNode.subFlow = await Flow.deSerialize(flowConnect, serializedNode.subFlow, receive);
			const node = flow._createNode(serializedNode.type, Vector.create(serializedNode.position), {
				name: serializedNode.name,
				...serializedNode,
				state,
				hitColor: Color.create(serializedNode.hitColor)
			});
			if (serializedNode.type === "core/tunnel") (serializedNode.tunnelType === "input" ? flow.inputs : flow.outputs).push(node);
		}
		data.groups.forEach((serializedGroup) => {
			let group = Group.create(flow, Vector.create(serializedGroup.position), {
				name: serializedGroup.name,
				...serializedGroup,
				hitColor: Color.create(serializedGroup.hitColor)
			});
			serializedGroup.nodes?.forEach((nodeId) => group.add(flow.nodes.get(nodeId)));
			flow.groups.push(group);
		});
		data.connectors.forEach((serializedConnector) => {
			let startNode = flow.nodes.get(serializedConnector.startNodeId);
			let startTerminal;
			if (typeof serializedConnector.startId === "string") startTerminal = startNode?.outputs.find((terminal) => terminal.id === serializedConnector.startId);
			else startTerminal = startNode?.outputsUI[serializedConnector.startId];
			let endNode = flow.nodes.get(serializedConnector.endNodeId);
			let endTerminal;
			if (typeof serializedConnector.endId === "string") endTerminal = endNode?.inputs.find((terminal) => terminal.id === serializedConnector.endId);
			else endTerminal = endNode?.inputsUI[serializedConnector.endId];
			const connector = Connector.create(flow, startTerminal, endTerminal, {
				id: serializedConnector.id,
				style: serializedConnector.style
			});
			flow.connectors.set(serializedConnector.id, connector);
		});
		return Promise.resolve(flow);
	}
};
var FlowState = /* @__PURE__ */ function(FlowState) {
	FlowState["Running"] = "Running";
	FlowState["Idle"] = "Idle";
	FlowState["Stopped"] = "Stopped";
	return FlowState;
}({});
var DefaultFlowOptions = () => ({
	name: "New Flow",
	rules: DefaultRules(),
	ruleColors: {}
});
/** Default rules every Flow will have, for e.g. a string output can only be connected to string inputs.
*  ```javascript
*  {
*    string: ['string'],
*    number: ['number'],
*    boolean: ['boolean'],
*    file: ['file'],
*    event: ['event'], ...
*  }
*  ```
*/
var DefaultRules = () => ({
	string: ["string", "any"],
	number: [
		"number",
		"audioparam",
		"any"
	],
	boolean: ["boolean", "any"],
	array: ["array", "any"],
	file: ["file", "any"],
	event: ["event", "any"],
	vector: ["vector", "any"],
	"array-buffer": ["array-buffer", "any"],
	audio: ["audio", "audioparam"],
	audioparam: ["audioparam"],
	"audio-buffer": ["audio-buffer", "any"],
	any: ["any"]
});
var DefaultRuleColors = () => ({
	string: Color.create("#B2F77D"),
	number: Color.create("#C9A185"),
	boolean: Color.create("#EADFC7"),
	array: Color.create("#3484F0"),
	file: Color.create("#EEEFF7"),
	event: Color.create("#9E64E8"),
	vector: Color.create("#DCC68D"),
	"array-buffer": Color.create("#FF6BCE"),
	audio: Color.create("#FFD154"),
	audioparam: Color.create("#FF0F50"),
	"audio-buffer": Color.create("#C3D4C2"),
	any: Color.create("#FFFBF9")
});
//#endregion
//#region src/resource/audio-worklets.ts
var generateWorkletUtils = () => {
	return { CircularBuffer: URL.createObjectURL(new Blob([`export default class CircularBuffer {
        constructor(bufferLength, noOfChannels) {
          this.noOfChannels = noOfChannels;
          this.bufferLength = bufferLength;
  
          this.read = this.write = this.frames = 0;
          this.channels = [];
          for (let i = 0; i < this.noOfChannels; ++i) this.channels[i] = new Float32Array(bufferLength);
        }
        pushBlock(buffer) {
          let sourceLength = buffer[0].length;
          for (let i = 0; i < sourceLength; ++i) {
            let write = (this.write + i) % this.bufferLength;
            for (let c = 0; c < this.noOfChannels; ++c) {
              this.channels[c][write] = buffer[c][i];
            }
          }
          this.write += sourceLength;
          if (this.write >= this.bufferLength) this.write = 0;
          this.frames += sourceLength;
          if (this.frames > this.bufferLength) this.frames = this.bufferLength;
        }
        popBlock(buffer) {
          if (this.frames === 0) return;
          let destinationLength = buffer[0].length;
          for (let i = 0; i < destinationLength; ++i) {
            let read = (this.read + i) % this.bufferLength;
            for (let c = 0; c < this.noOfChannels; ++c) {
              buffer[c][i] = this.channels[c][read];
            }
          }
          this.read += destinationLength;
          if (this.read >= this.bufferLength) this.read = 0;
          this.frames -= destinationLength;
          if (this.frames < 0) this.frames = 0;
        }
      }`], { type: "application/javascript" })) };
};
var generateAudioWorklets = (utilsFileName) => {
	return {
		Worklet_ProxyParamForSource: URL.createObjectURL(new Blob([`registerProcessor('proxy-param-for-source', class ProxyParamForSource extends AudioWorkletProcessor {
          static get parameterDescriptors() {
            return [{
              name: 'detune',
              defaultValue: 0,
              minValue: -2400,
              maxValue: 2400,
              automationRate: 'k-rate'
            }, {
              name: 'playbackRate',
              defaultValue: 1,
              minValue: 0.25,
              maxValue: 3,
              automationRate: 'k-rate'
            }]
          }
          process(inputs, outputs, parameters) {
            outputs[0].forEach(channel => {
              for (let i = 0; i < channel.length; i++) {
                // wierd bug, the detune is off by 1200 cents, have to offset this anomaly
                channel[i] = (parameters['detune'].length > 1 ? parameters['detune'][i] : parameters['detune'][0]) - 1200;
              }
            });
            outputs[1].forEach(channel => {
              for (let i = 0; i < channel.length; i++) {
                channel[i] = (parameters['playbackRate'].length > 1 ? parameters['playbackRate'][i] : parameters['playbackRate'][0]);
              }
            });
            return true;
          }
        })`], { type: "application/javascript" })),
		Worklet_ProxyParam: URL.createObjectURL(new Blob([`registerProcessor('proxy-param', class ProxyParam extends AudioWorkletProcessor {
          static get parameterDescriptors() {
            return [{
              name: 'param',
              defaultValue: 0,
              minValue: -340282346638528859811704183484516925439,
              maxValue: 340282346638528859811704183484516925439,
              automationRate: 'k-rate'
            }]
          }

          min = -340282346638528859811704183484516925439;
          max =  340282346638528859811704183484516925439;
          offset = 0;
          constructor(...args) {
            super(...args);
            this.port.onmessage = (e) => {
              switch(e.data.type) {
                case 'set-range': {
                  this.min = e.data.value.min;
                  this.max = e.data.value.max;
                  break;
                }
                case 'set-offset': {
                  this.offset = -e.data.value
                  break;
                }
                default: break;
              }
            }
          }
          clamp(value) { return Math.min(Math.max(value, this.min), this.max); }
          process(inputs, outputs, parameters) {
            outputs[0].forEach(channel => {
              for (let i = 0; i < channel.length; i++) {
                channel[i] = this.clamp((parameters['param'].length > 1 ? parameters['param'][i] : parameters['param'][0]) + this.offset);
              }
            });
            return true;
          }
        })`], { type: "application/javascript" })),
		Worklet_Debug: URL.createObjectURL(new Blob([`registerProcessor('debug', class Debug extends AudioWorkletProcessor {
          throttle = 1000;
          lastPeak = -Infinity;
          constructor(...args) {
            super(...args);
            this.port.onmessage = (e) => {
              this.throttle = e.data;
            }
          }
          process(inputs, outputs, parameters) {
            if (Date.now() - this.lastPeak > this.throttle) {
              let data = {
                inputs: inputs.map(input => input.length),
                outputs: outputs.map(output => output.length)
              };
              this.port.postMessage(data);
              this.lastPeak = Date.now();
            }
            let input = inputs[0];
            let output = outputs[0]
            for (let i = 0 ; i < input.length ; i+=1) {
              for(let c = 0 ; c < input[i].length ; c+=1) {
                output[i][c] = input[i][c];
              }
            }
            return true;
          }
        })`], { type: "application/javascript" })),
		Worklet_MoogEffect: URL.createObjectURL(new Blob([`import CircularBuffer from '${utilsFileName}'
      registerProcessor('moog-effect', class MoogEffect extends AudioWorkletProcessor {
      cutoff = 0.065;
      resonance = 3.5;
      f = 0; inputFactor = 0; fb = 0;

      constructor(options) {
        super();
        this.bufferSize = options.processorOptions.bufferSize;

        this.setParams();
        this.port.onmessage = (e) => {
          this.cutoff = e.data.cutoff;
          this.resonance = e.data.resonance;
          this.setParams();
        }
      }
      setParams() {
        this.f = this.cutoff * 1.16;
        this.inputFactor = 0.35013 * (this.f * this.f) * (this.f * this.f);
        this.fb = this.resonance * (1.0 - 0.15 * this.f * this.f);
      }
      channelsChanged(newNoOfChannels) {
        this.channelCount = newNoOfChannels;
        this.inputBuffer = new CircularBuffer(this.bufferSize, this.channelCount);
        this.outputBuffer = new CircularBuffer(this.bufferSize, this.channelCount);
        this.in1 = new Array(this.channelCount).fill(0);
        this.in2 = new Array(this.channelCount).fill(0);
        this.in3 = new Array(this.channelCount).fill(0);
        this.in4 = new Array(this.channelCount).fill(0);
        this.out1 = new Array(this.channelCount).fill(0);
        this.out2 = new Array(this.channelCount).fill(0);
        this.out3 = new Array(this.channelCount).fill(0);
        this.out4 = new Array(this.channelCount).fill(0);
      }

      _process(input, output) {
        for (let c = 0; c < input.length; ++c) {
          for (let i = 0; i < input[c].length; ++i) {
            input[c][i] -= this.out4[c] * this.fb;
            input[c][i] *= this.inputFactor;
            this.out1[c] = input[c][i] + 0.3 * this.in1[c] + (1 - this.f) * this.out1[c];
            this.in1[c] = input[c][i];
            this.out2[c] = this.out1[c] + 0.3 * this.in2[c] + (1 - this.f) * this.out2[c];
            this.in2[c] = this.out1[c];
            this.out3[c] = this.out2[c] + 0.3 * this.in3[c] + (1 - this.f) * this.out3[c];
            this.in3[c] = this.out2[c];
            this.out4[c] = this.out3[c] + 0.3 * this.in4[c] + (1 - this.f) * this.out4[c];
            this.in4[c] = this.out3[c];
            output[c][i] = this.out4[c];
          }
        }
      }
      process(inputs, outputs, parameters) {
        let input = inputs[0], output = outputs[0];
        if (input.length === 0) return true;

        if (!this.prevChannelCount || this.prevChannelCount !== inputs[0].length) {
          this.channelsChanged(inputs[0].length);
          this.prevChannelCount = inputs[0].length;
        }

        this.inputBuffer.pushBlock(input);
        if (this.inputBuffer.frames >= this.bufferSize) {
          let tmpInBuffer = [], tmpOutBuffer = [];
          for (let i = 0; i < this.channelCount; ++i) {
            tmpInBuffer[i] = new Float32Array(this.bufferSize);
            tmpOutBuffer[i] = new Float32Array(this.bufferSize);
          }

          this.inputBuffer.popBlock(tmpInBuffer);
          this._process(tmpInBuffer, tmpOutBuffer);
          this.outputBuffer.pushBlock(tmpOutBuffer);
        }
        this.outputBuffer.popBlock(output);
        return true;
      }
    })`], { type: "application/javascript" })),
		Worklet_BitcrusherEffect: URL.createObjectURL(new Blob([`import CircularBuffer from '${utilsFileName}'
      registerProcessor('bitcrusher-effect', class BitcrusherEffect extends AudioWorkletProcessor {
      bits = 4;
      normFreq = 0.1;
      step = 0;

      constructor(options) {
        super();
        this.bufferSize = options.processorOptions.bufferSize;

        this.setParams();
        this.port.onmessage = (e) => {
          this.bits = e.data.bits;
          this.normFreq = e.data.normFreq;
          this.setParams();
        }
      }
      setParams() {
        this.step = Math.pow(1 / 2, this.bits);
      }
      channelsChanged(newNoOfChannels) {
        this.channelCount = newNoOfChannels;
        this.inputBuffer = new CircularBuffer(this.bufferSize, this.channelCount);
        this.outputBuffer = new CircularBuffer(this.bufferSize, this.channelCount);
        this.phaser = new Array(this.channelCount).fill(0);
        this.last = new Array(this.channelCount).fill(0);
      }

      _process(input, output) {
        for (let c = 0; c < input.length; ++c) {
          for (let i = 0; i < input[c].length; ++i) {
            this.phaser[c] += this.normFreq;
            if (this.phaser[c] >= 1.0) {
              this.phaser[c] -= 1.0;
              this.last[c] = this.step * Math.floor(input[c][i] / this.step + 0.5);
            }
            output[c][i] = this.last[c];
          }
        }
      }
      process(inputs, outputs, parameters) {
        let input = inputs[0], output = outputs[0];
        if (input.length === 0) return true;
        if (!this.prevChannelCount || this.prevChannelCount !== inputs[0].length) {
          this.channelsChanged(inputs[0].length);
          this.prevChannelCount = inputs[0].length;
        }
        this.inputBuffer.pushBlock(input);
        if (this.inputBuffer.frames >= this.bufferSize) {
          let tmpInBuffer = [], tmpOutBuffer = [];
          for (let i = 0; i < this.channelCount; ++i) {
            tmpInBuffer[i] = new Float32Array(this.bufferSize);
            tmpOutBuffer[i] = new Float32Array(this.bufferSize);
          }
          this.inputBuffer.popBlock(tmpInBuffer);
          this._process(tmpInBuffer, tmpOutBuffer);
          this.outputBuffer.pushBlock(tmpOutBuffer);
        }
        this.outputBuffer.popBlock(output);
        return true;
      }
    })`], { type: "application/javascript" })),
		Worklet_LFO: URL.createObjectURL(new Blob([`registerProcessor('lfo', class LFO extends AudioWorkletProcessor {
      oscillation = 0.3;
      offset = 0.85;
      phaseInc = 0;
      phase = 0;

      constructor(options) {
        super();
        this.sampleRate = options.processorOptions.sampleRate;

        this.port.onmessage = (e) => {
          switch(e.data.type) {
            case 'set-oscillation': { this.oscillation = e.data.value; break; }
            case 'set-offset': { this.offset = e.data.value; break; }
            case 'set-phase': { this.phase = e.data.value; break; }
            case 'set-frequency': { this.phaseInc = 2 * Math.PI * e.data.value * 128 / this.sampleRate; break; }
            case 'set-all': {
              this.oscillation = e.data.value.oscillation;
              this.offset = e.data.value.offset;
              this.phase = e.data.value.phase;
              this.phaseInc = 2 * Math.PI * e.data.value.frequency * 128 / this.sampleRate;
              break;
            }
            default: return;
          }
        }
      }
      process(inputs, outputs, parameters) {
        let output = outputs[0][0];

        for (let i = 0; i < output.length; ++i) {
          this.phase += this.phaseInc;
          if (this.phase > 2 * Math.PI) this.phase = 0;
          output[i] = this.offset + this.oscillation * Math.sin(this.phase);
        }

        return true;
      }
    })`], { type: "application/javascript" })),
		Worklet_Noise: URL.createObjectURL(new Blob([`registerProcessor('noise', class Noise extends AudioWorkletProcessor {
      type = 'white';
      types = ['white', 'pink', 'brownian'];

      constructor(options) {
        super();
        this.port.onmessage = (e) => {
          if (!this.types.includes(e.data)) this.type = 'white';
          else this.type = e.data;
        }

        this.generate = {
          'white': (output) => {
            for (let i = 0; i < output.length; ++i) output[i] = Math.random() * 2 - 1;
          },
          'pink': (output) => {
            let b0, b1, b2, b3, b4, b5, b6;
            b0 = b1 = b2 = b3 = b4 = b5 = b6 = 0.0;

            for (let i = 0; i < output.length; ++i) {
              let white = Math.random() * 2 - 1;
              b0 = 0.99886 * b0 + white * 0.0555179;
              b1 = 0.99332 * b1 + white * 0.0750759;
              b2 = 0.96900 * b2 + white * 0.1538520;
              b3 = 0.86650 * b3 + white * 0.3104856;
              b4 = 0.55000 * b4 + white * 0.5329522;
              b5 = -0.7616 * b5 - white * 0.0168980;
              output[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
              output[i] *= 0.11;
              b6 = white * 0.115926;
            }
          },
          'brownian': (output) => {
            let lastOut = 0.0;
            for (let i = 0; i < output.length; i++) {
              let white = Math.random() * 2 - 1;
              output[i] = (lastOut + (0.02 * white)) / 1.02;
              lastOut = output[i];
              output[i] *= 3.5;
            }
          }
        }
      }
      process(inputs, outputs, parameters) {
        this.generate[this.type](outputs[0][0]);
        return true;
      }
    })`], { type: "application/javascript" })),
		Worklet_Proxy: URL.createObjectURL(new Blob([`registerProcessor('proxy', class Proxy extends AudioWorkletProcessor {

      constructor() { super(); }
      process(inputs, outputs, parameters) {
        inputs.forEach((input, index) => input.forEach((channel, cIndex) => {
          outputs[index][cIndex].set(channel);
        }));
        return true;
      }
    })`], { type: "application/javascript" }))
	};
};
//#endregion
//#region src/core/empty-node.ts
var EmptyNode = class extends Node {
	constructor() {
		super();
	}
	setupIO() {}
	created() {}
	process() {}
};
//#endregion
//#region src/core/tunnel-node.ts
var TunnelNode = class extends Node {
	get proxyTerminal() {
		return this._proxyTerminal;
	}
	set proxyTerminal(terminal) {
		this._proxyTerminal = terminal;
		this._proxyTerminal.on("data", (_, data) => this.outputs[0].setData(data));
	}
	constructor(_flow, options) {
		super();
		this.tunnelType = options.tunnelType;
	}
	setupIO(options) {
		if (!options.tunnelName || !options.tunnelDataType) return;
		this.addTerminal({
			type: options.tunnelType === "input" ? TerminalType.OUT : TerminalType.IN,
			name: options.tunnelName,
			dataType: options.tunnelDataType
		});
	}
	created() {
		if (this.tunnelType === "input") this.outputs[0].on("connect", (_, connector) => {
			if (this.proxyTerminal && this.proxyTerminal.connectors.length > 0) connector.data = this.proxyTerminal.connectors[0].data;
		});
		else this.inputs[0].on("data", (_, data) => this.proxyTerminal?.setData(data));
	}
	process() {
		this.outputs[0]?.setData(this.proxyTerminal?.getData());
	}
	async serialize(persist) {
		return {
			...await super.serialize(persist),
			tunnelType: this.tunnelType
		};
	}
};
//#endregion
//#region src/ui/slider-2d.ts
var Slider2D = class extends UINode {
	get value() {
		if (this.propName) return this.getProp();
		return this._value;
	}
	set value(value) {
		value = value.clamp(0, 1);
		let oldVal = this.value;
		let newVal = value;
		if (this.propName) this.setProp(newVal);
		else this._value = newVal;
		this.renderOffThumb();
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, oldVal, newVal);
	}
	constructor(node, _options = DefaultSlider2DOptions(node)) {
		super();
		this.draggable = true;
	}
	created(options) {
		options = {
			...DefaultSlider2DOptions(this.node),
			...options
		};
		const { height, style = {}, value = Vector.create(.5, .5), input, output } = options;
		this.style = {
			...DefaultSlider2DStyle(),
			...style
		};
		this.style.pointDiameter = clampMin(this.style.pointDiameter, 5);
		this.height = height ?? this.node.style?.rowHeight * 4;
		this._value = this.propName ? this.getProp() : value;
		this._value.clampInPlace(0, 1);
		if (input) {
			const terminal = this.createTerminal(TerminalType.IN, "vector");
			terminal.on("connect", (_, connector) => {
				if (connector.data) this.value = connector.data;
			});
			terminal.on("data", (_, data) => {
				if (data) this.value = data;
			});
		}
		if (output) this.createTerminal(TerminalType.OUT, "vector").on("connect", (_, connector) => connector.data = this.value);
		this.node.on("process", () => {
			this.output?.setData(this.value);
		});
		this.thumbHitColor = Color.Random().hexValue;
		if (typeof OffscreenCanvas !== "undefined") this.offThumbCanvas = new OffscreenCanvas(this.width, this.height);
		else {
			this.offThumbCanvas = document.createElement("canvas");
			this.offThumbCanvas.width = this.width;
			this.offThumbCanvas.height = this.height;
		}
		this.offThumbContext = this.offThumbCanvas.getContext("2d");
		this.renderOffThumb();
	}
	renderOffThumb() {
		let [width, height] = [this.width - this.style.pointDiameter, this.height - this.style.pointDiameter];
		this.offThumbContext.clearRect(0, 0, width, height);
		let coord = Vector.create(this.value.x, 1 - this.value.y).multiply(width, height).add(this.style.pointDiameter / 2);
		this.offThumbContext.fillStyle = this.thumbHitColor;
		this.offThumbContext.beginPath();
		this.offThumbContext.arc(coord.x, coord.y, this.style.pointDiameter / 2, 0, Constant.TAU);
		this.offThumbContext.fill();
	}
	paint() {
		let context = this.node.context;
		context.fillStyle = this.style.backgroundColor ?? "";
		context.strokeStyle = this.style.borderColor ?? "";
		context.lineWidth = this.style.borderWidth ?? 0;
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		let [width, height] = [this.width - this.style.pointDiameter, this.height - this.style.pointDiameter];
		let point = Vector.create(this.value.x, 1 - this.value.y).multiplyInPlace(width, height).addInPlace(this.position).addInPlace(this.style.pointDiameter / 2);
		context.fillStyle = this.style.thumbColor ?? "";
		context.beginPath();
		context.arc(point.x, point.y, this.style.pointDiameter / 2, 0, Constant.TAU);
		context.fill();
	}
	paintLOD1() {
		let context = this.context;
		context.strokeStyle = "#000";
		context.fillStyle = this.style.backgroundColor ?? "";
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		let [newWidth, newHeight] = [this.width, this.height];
		if (Math.floor(this.offThumbCanvas.width) !== Math.floor(newWidth) || Math.floor(this.offThumbCanvas.height) !== Math.floor(newHeight)) {
			this.offThumbCanvas.width = newWidth;
			this.offThumbCanvas.height = newHeight;
			this.renderOffThumb();
		}
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
	}
	getHitPoint(realPosition) {
		let coord = realPosition.subtract(this.position);
		let hitColor = Color.rgbaToHex(this.offThumbContext.getImageData(coord.x, coord.y, 1, 1).data);
		return this.thumbHitColor === hitColor;
	}
	movePoint(realPosition) {
		let [width, height] = [this.width - this.style.pointDiameter, this.height - this.style.pointDiameter];
		this._value = realPosition.clamp(this.position.x + this.style.pointDiameter / 2, this.position.x + this.width - this.style.pointDiameter / 2, this.position.y + this.style.pointDiameter / 2, this.position.y + this.height - this.style.pointDiameter / 2).subtractInPlace(this.position.add(this.style.pointDiameter / 2)).clampInPlace(0, width, 0, height).normalizeInPlace(0, width, 0, height);
		this.value = Vector.create(this._value.x, 1 - this._value.y);
	}
	onPropChange(_oldVal, newVal) {
		this._value = newVal;
		this.renderOffThumb();
		this.output?.setData(this.value);
	}
	onUp() {
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, null, this._value);
		this.isHit = false;
	}
	onDrag(event) {
		this.movePoint(event.realPos);
	}
	onExit(event) {
		if (this.isHit) {
			this.movePoint(event.realPos);
			if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, null, this._value);
		}
		this.isHit = false;
	}
};
var DefaultSlider2DStyle = () => ({
	pointDiameter: 20,
	backgroundColor: "#666",
	thumbColor: "#fff",
	borderColor: "#000",
	borderWidth: 1
});
var DefaultSlider2DOptions = (node) => ({
	value: Vector.create({
		x: .5,
		y: .5
	}),
	height: node.style?.rowHeight * 4
});
//#endregion
//#region src/ui/button.ts
var Button = class extends UINode {
	constructor(_node, _options) {
		super();
	}
	created(options) {
		options = {
			...DefaultButtonOptions(this.node),
			...options
		};
		const { height, style = {}, input, output, text = "Button" } = options;
		this.style = {
			...DefaultButtonStyle(),
			...style
		};
		this.height = (height ?? this.node.style?.rowHeight ?? 0) + 2 * this.style.padding;
		if (input) this.createTerminal(TerminalType.IN, "event").on("event", () => this.call("click", this));
		if (output) this.createTerminal(TerminalType.OUT, "event");
		this.label = this.node.createUI("core/label", {
			text,
			style: {
				fontSize: this.style.fontSize,
				font: this.style.font,
				align: Align.Center,
				color: this.style.color
			},
			height
		});
		this.label.on("click", (_, position) => this.call("click", this, position));
		this.children.push(this.label);
	}
	paint() {
		Object.assign(this.context, {
			fillStyle: this.style.backgroundColor,
			shadowColor: this.style.shadowColor,
			shadowBlur: this.style.shadowBlur,
			shadowOffsetX: this.style.shadowOffset?.x,
			shadowOffsetY: this.style.shadowOffset?.y
		});
		this.context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		this.context.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	paintLOD1() {
		this.context.strokeStyle = "#000";
		this.paint();
		this.context.strokeRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		this.label.position = this.position;
		this.label.height = this.height;
		this.label.width = this.width;
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
	}
	onPropChange() {}
};
var DefaultButtonStyle = () => ({
	backgroundColor: "#666",
	padding: 5,
	color: "#fff",
	font: "arial",
	fontSize: "11px",
	shadowColor: "#555",
	shadowBlur: 3,
	shadowOffset: Vector.create(3, 3)
});
var DefaultButtonOptions = (node) => ({
	text: "Button",
	height: node.style?.rowHeight
});
//#endregion
//#region src/ui/dial.ts
var Dial = class extends UINode {
	get value() {
		if (this.propName) return this.getProp();
		return this._value;
	}
	set value(value) {
		let oldVal = this.value;
		let newVal = clamp(value, this.min, this.max);
		if (this.propName) this.setProp(newVal);
		else this._value = newVal;
		this.temp = normalize(newVal, this.min, this.max);
		if (this.node.flow?.state !== FlowState.Stopped) this.call("change", this, oldVal, newVal);
	}
	constructor(node, options) {
		super();
		this.thumbStart = Vector.Zero();
		this.thumbEnd = Vector.Zero();
		options = {
			...DefaultDialOptions(node),
			...options
		};
		const { min = 0, max = 100, height = 50 + node.style?.padding * 2, style = {}, value = min } = options;
		this.draggable = true;
		this.height = height;
		this.min = min;
		this.max = max;
		this._value = value;
		this.temp = normalize(this._value, min, max);
		this.style = {
			...DefaultDialStyle(),
			...style
		};
	}
	created(options) {
		if (options.input) {
			const terminal = this.createTerminal(TerminalType.IN, "number");
			terminal.on("connect", (_, connector) => {
				if (connector.data) this.value = connector.data;
			});
			terminal.on("data", (_, data) => {
				if (data) this.value = data;
			});
		}
		if (options.output) this.createTerminal(TerminalType.OUT, "number").on("connect", (_, connector) => connector.data = this.value);
		this.node.on("process", () => {
			if (this.output) this.output.setData(this.value);
		});
	}
	paint() {
		let context = this.context;
		let size = Math.min(this.width, this.height);
		context.fillStyle = this.style.color ?? "";
		context.strokeStyle = this.style.borderColor ?? "";
		context.lineWidth = this.style.borderWidth ?? 0;
		context.shadowColor = this.style.shadowColor ?? "";
		context.shadowBlur = this.style.shadowBlur ?? 0;
		context.shadowOffsetX = 3;
		context.shadowOffsetY = 3;
		context.beginPath();
		context.arc(this.position.x + this.width / 2, this.position.y + this.height / 2, size / 2, 0, Constant.TAU);
		context.stroke();
		context.fill();
		context.lineCap = "round";
		context.lineWidth = 10;
		context.strokeStyle = this.style.thumbColor ?? "";
		context.shadowColor = this.style.thumbShadowColor ?? "";
		context.shadowBlur = this.style.thumbShadowBlur ?? 0;
		context.beginPath();
		context.moveTo(this.thumbStart.x, this.thumbStart.y);
		context.lineTo(this.thumbEnd.x, this.thumbEnd.y);
		context.stroke();
	}
	paintLOD1() {
		let context = this.context;
		context.strokeStyle = "#000";
		context.fillStyle = this.style.color ?? "";
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		let context = this.offUIContext;
		let size = Math.min(this.width, this.height);
		context.fillStyle = this.hitColor.hexValue;
		context.beginPath();
		context.arc(this.position.x + this.width / 2, this.position.y + this.height / 2, size / 2, 0, Constant.TAU);
		context.fill();
	}
	reflow() {
		let center = this.position.add(this.width / 2, this.height / 2);
		let angle = normalize(this.value, this.min, this.max) * Constant.TAU + Math.PI / 2;
		let size = Math.min(this.width, this.height);
		this.thumbStart.assign(center.x + Math.cos(angle) * (size / 5), center.y + Math.sin(angle) * (size / 5));
		this.thumbEnd.assign(center.x + Math.cos(angle) * (size / 2 - 10), center.y + Math.sin(angle) * (size / 2 - 10));
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
	}
	pointToValue(position) {
		let diff = position.subtract(this.position.add(this.width / 2, this.height / 2));
		let angle = Math.atan2(diff.y, diff.x);
		if (angle < 0) angle += Constant.TAU;
		return (angle / Constant.TAU - .25 + 1) % 1;
	}
	onPropChange(_oldVal, newVal) {
		this._value = newVal;
		this.temp = normalize(newVal, this.min, this.max);
		this.reflow();
		this.output && this.output.setData(this._value);
	}
	onDown(event) {
		this.lastAngle = void 0;
		this.deltaValue = this.pointToValue(event.realPos);
		this.temp = normalize(this.value, this.min, this.max);
		this.onDrag(event);
	}
	onDrag(event) {
		let currValue = this.pointToValue(event.realPos);
		let diffValue = currValue - this.deltaValue;
		diffValue = Math.abs(diffValue) > .5 ? 0 : diffValue;
		this.deltaValue = currValue;
		this.temp = this.temp + diffValue;
		this.temp = clamp(this.temp, 0, 1);
		let angle = this.temp * Constant.TAU;
		if (angle < 0) angle += Constant.TAU;
		if (typeof this.lastAngle !== "undefined" && Math.abs(this.lastAngle - angle) > 2) angle = this.lastAngle > 3 ? Constant.TAU : 0;
		this.lastAngle = angle;
		let normalizedValue = angle / Constant.TAU;
		this.value = denormalize(clamp(normalizedValue, 0, 1), this.min, this.max);
		this.temp = normalizedValue;
		this.reflow();
	}
	onExit() {
		this.lastAngle = void 0;
	}
};
var DefaultDialStyle = () => ({
	color: "#e3e3e3",
	borderColor: "#000",
	borderWidth: 1,
	shadowColor: "grey",
	shadowBlur: 5,
	thumbColor: "#c9c9c9",
	thumbShadowColor: "#858585",
	thumbShadowBlur: 5
});
var DefaultDialOptions = (node) => ({
	height: 50 + node.style?.padding * 2,
	min: 0,
	max: 100
});
//#endregion
//#region src/ui/display.ts
var Display = class extends UINode {
	constructor(_node, options = DefaultDisplayOptions()) {
		super();
		this.displayConfigs = [];
		options = {
			...DefaultDisplayOptions(),
			...options
		};
		const { height, style = {} } = options;
		this.height = height;
		this.draggable = true;
		this.zoomable = true;
		this.style = {
			...DefaultDisplayStyle(),
			...style
		};
	}
	created(options) {
		options = {
			...DefaultDisplayOptions(),
			...options
		};
		const { clear, customRenderers } = options;
		if (clear) this.createTerminal(TerminalType.IN, "event").on("event", () => {
			this.displayConfigs.filter((config) => !config.rendererConfig.auto).forEach((config) => {
				config.context.clearRect(0, 0, config.canvas.width, config.canvas.height);
			});
		});
		if (typeof OffscreenCanvas !== "undefined") customRenderers?.forEach((rendererConfig) => {
			let offCanvas, offContext, newOffCanvasConfig;
			if (rendererConfig.canvasType === "HTMLCanvasElement") {
				offCanvas = document.createElement("canvas");
				offCanvas.width = this.node.width - 2 * this.node.style?.padding;
				offCanvas.height = this.height;
			} else offCanvas = new OffscreenCanvas(this.node.width - 2 * this.node.style?.padding, this.height);
			offContext = offCanvas.getContext("2d");
			newOffCanvasConfig = {
				canvas: offCanvas,
				context: offContext,
				rendererConfig,
				shouldRender: true
			};
			this.displayConfigs.push(newOffCanvasConfig);
		});
		else customRenderers?.forEach((rendererConfig) => {
			const offCanvas = document.createElement("canvas");
			offCanvas.width = this.node.width - 2 * this.node.style?.padding;
			offCanvas.height = this.height;
			const newOffCanvasConfig = {
				canvas: offCanvas,
				context: offCanvas.getContext("2d"),
				rendererConfig,
				shouldRender: true
			};
			this.displayConfigs.push(newOffCanvasConfig);
		});
		this.node.flow?.flowConnect.on("scale", () => this.reflow());
	}
	customRender(canvasConfigs) {
		return Promise.all(canvasConfigs.map((config) => {
			return new Promise((resolve) => {
				if (config.shouldRender) {
					if (config.rendererConfig.clear) config.context.clearRect(0, 0, config.canvas.width, config.canvas.height);
					if (this.style.backgroundColor) {
						config.context.fillStyle = this.style.backgroundColor;
						config.context.fillRect(0, 0, config.canvas.width, config.canvas.height);
					}
					resolve(config.rendererConfig.renderer?.(config.context, config.canvas.width, config.canvas.height));
				} else resolve(false);
			});
		}));
	}
	paint() {
		let context = this.context;
		context.strokeStyle = this.style.borderColor ?? "";
		context.lineWidth = 1;
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		let autoOffCanvasConfigs = this.displayConfigs.filter((config) => config.rendererConfig.auto);
		this.customRender(autoOffCanvasConfigs).then((results) => {
			results.forEach((result, index) => autoOffCanvasConfigs[index].shouldRender = result);
		});
		this.displayConfigs.forEach((offCanvas) => {
			context.drawImage(offCanvas.canvas, 0, 0, offCanvas.canvas.width, offCanvas.canvas.height, this.position.x, this.position.y, this.node.width - 2 * this.node.style?.padding, this.height);
		});
	}
	paintLOD1() {
		let context = this.context;
		context.strokeStyle = this.style.borderColor ?? "";
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		context.fillStyle = "lightgrey";
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		let newWidth = (this.node.width - 2 * this.node.style?.padding) * this.node.flow.flowConnect.scale;
		let newHeight = this.height * this.node.flow.flowConnect.scale;
		this.displayConfigs.forEach((offCanvas) => {
			if (Math.floor(offCanvas.canvas.width) !== Math.floor(newWidth) || Math.floor(offCanvas.canvas.height) !== Math.floor(newHeight)) {
				offCanvas.canvas.width = newWidth;
				offCanvas.canvas.height = newHeight;
				if (!offCanvas.shouldRender) {
					offCanvas.rendererConfig.renderer?.(offCanvas.context, offCanvas.canvas.width, offCanvas.canvas.height);
					offCanvas.shouldRender = false;
				}
			}
		});
		this.input?.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
	}
	onPropChange() {}
};
var CanvasType = /* @__PURE__ */ function(CanvasType) {
	CanvasType["OffscreenCanvas"] = "OffscreenCanvas";
	CanvasType["HTMLCanvasElement"] = "HTMLCanvasElement";
	return CanvasType;
}({});
var DefaultDisplayStyle = () => ({ borderColor: "#000" });
var DefaultDisplayOptions = () => ({
	height: 70,
	customRenderers: []
});
//#endregion
//#region src/ui/envelope.ts
var Envelope = class extends UINode {
	get value() {
		let value;
		if (this.propName) value = this.getProp().map((vec) => vec.clone());
		else value = this._value.toArray().map((vec) => vec.clone());
		return value;
	}
	set value(value) {
		let oldVal = this._value.toArray().map((vec) => vec.clone());
		let newVal = value;
		if (this.propName) this.setProp(newVal);
		else this.handleEnvelopeChange(newVal);
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, oldVal, newVal);
	}
	constructor(_node, options = DefaultEnvelopeOptions()) {
		super();
		this.pointHitColorPoint = new BiMap();
		options = {
			...DefaultEnvelopeOptions(),
			...options
		};
		const { height = 100, style = {} } = options;
		this.draggable = true;
		this.height = height;
		this.style = {
			...DefaultEnvelopeStyle(),
			...style
		};
		this.style.pointDiameter = clampMin(this.style.pointDiameter, 2);
	}
	created(options) {
		const { values = [] } = options;
		if (options.input) {
			const terminal = this.createTerminal(TerminalType.IN, "array");
			terminal.on("connect", (_, connector) => {
				if (connector.data) this.value = connector.data;
			});
			terminal.on("data", (_, data) => {
				if (data) this.value = data;
			});
		}
		if (options.output) this.createTerminal(TerminalType.OUT, "array").on("connect", (_, connector) => connector.data = this.value);
		if (typeof OffscreenCanvas !== "undefined") this.offPointsCanvas = new OffscreenCanvas(this.width, this.height);
		else {
			this.offPointsCanvas = document.createElement("canvas");
			this.offPointsCanvas.width = this.width;
			this.offPointsCanvas.height = this.height;
		}
		this.offPointsContext = this.offPointsCanvas.getContext("2d");
		this.handleEnvelopeChange(this.getProp() ?? values);
	}
	handleEnvelopeChange(values) {
		values.forEach((vec) => vec.clampInPlace(0, 1, 0, 1));
		values.sort((a, b) => a.x - b.x);
		this._value = new List((a, b) => a.x - b.x, values);
		this.pointHitColorPoint.clear();
		this._value.forEach((node) => this.pointHitColorPoint.set(Color.Random().hexValue, node));
		this.renderOffPoints();
	}
	renderOffPoints() {
		let [width, height] = [this.width - this.style.pointDiameter, this.height - this.style.pointDiameter];
		this.offPointsContext.clearRect(0, 0, width, height);
		this._value.forEach((node) => {
			let coord = Vector.create(node.data.x, 1 - node.data.y).multiply(width, height).add(this.style.pointDiameter / 2);
			this.offPointsContext.fillStyle = this.pointHitColorPoint.get(node);
			this.offPointsContext.beginPath();
			this.offPointsContext.arc(coord.x, coord.y, this.style.pointDiameter / 2, 0, Constant.TAU);
			this.offPointsContext.fill();
		});
	}
	paint() {
		let context = this.node.context;
		context.fillStyle = this.style.backgroundColor ?? "";
		context.strokeStyle = this.style.borderColor ?? "";
		context.lineWidth = this.style.borderWidth ?? 0;
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		let [width, height] = [this.width - this.style.pointDiameter, this.height - this.style.pointDiameter];
		let points = this._value.map((node) => Vector.create(node.data.x, 1 - node.data.y).multiplyInPlace(width, height).addInPlace(this.position).addInPlace(this.style.pointDiameter / 2));
		if (points.length > 0) {
			context.strokeStyle = this.style.lineColor ?? "";
			context.lineWidth = 2;
			context.beginPath();
			context.moveTo(this.position.x, points[0].y);
			points.forEach((point) => context.lineTo(point.x, point.y));
			context.lineTo(this.position.x + this.width, points[points.length - 1].y);
			context.stroke();
		}
		context.fillStyle = this.style.pointColor ?? "";
		points.forEach((point) => {
			context.beginPath();
			context.arc(point.x, point.y, this.style.pointDiameter / 2, 0, Constant.TAU);
			context.fill();
		});
	}
	paintLOD1() {
		this.context.fillStyle = this.style.backgroundColor ?? "";
		this.context.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		let [newWidth, newHeight] = [this.width, this.height];
		if (Math.floor(this.offPointsCanvas.width) !== Math.floor(newWidth) || Math.floor(this.offPointsCanvas.height) !== Math.floor(newHeight)) {
			this.offPointsCanvas.width = newWidth;
			this.offPointsCanvas.height = newHeight;
			this.renderOffPoints();
		}
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
	}
	getHitPoint(realPosition) {
		let coord = realPosition.subtract(this.position);
		let hitColor = Color.rgbaToHex(this.offPointsContext.getImageData(coord.x, coord.y, 1, 1).data);
		return this.pointHitColorPoint.get(hitColor);
	}
	movePoint(realPosition) {
		let [width, height] = [this.width - this.style.pointDiameter, this.height - this.style.pointDiameter];
		this.currHitPoint.data = realPosition.clamp(this.position.x + this.style.pointDiameter / 2, this.position.x + this.width - this.style.pointDiameter / 2, this.position.y + this.style.pointDiameter / 2, this.position.y + this.height - this.style.pointDiameter / 2).subtractInPlace(this.position.add(this.style.pointDiameter / 2)).clampInPlace(0, width, 0, height).normalizeInPlace(0, width, 0, height).clampInPlace(this.currHitPoint?.prev?.data.x || 0, this.currHitPoint?.next?.data.x || 1, -Infinity, Infinity);
		this.currHitPoint.data = Vector.create(this.currHitPoint.data.x, 1 - this.currHitPoint.data.y);
		this.updateState();
		this.renderOffPoints();
	}
	newPoint(realPosition, width, height) {
		let oldVal = this._value.toArray();
		let newPoint = realPosition.subtract(this.position.add(this.style.pointDiameter / 2)).normalizeInPlace(0, width, 0, height);
		newPoint = Vector.create(newPoint.x, 1 - newPoint.y);
		let newPointNode;
		let anchor = this._value.searchTail((node) => node.data.x <= newPoint.x);
		if (!anchor) newPointNode = this._value.prepend(newPoint);
		else newPointNode = this._value.addAfter(newPoint, anchor);
		this.pointHitColorPoint.set(Color.Random().hexValue, newPointNode);
		this.renderOffPoints();
		this.updateState();
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, oldVal, this._value.toArray());
	}
	deletePoint() {
		let oldVal = this._value.toArray();
		this._value.delete(this.currHitPoint);
		this.pointHitColorPoint.delete(this.currHitPoint);
		this.renderOffPoints();
		this.updateState();
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, oldVal, this._value.toArray());
	}
	updateState() {
		if (this.propName && this.node.state[this.propName]) {
			this.node.state[this.propName].length = 0;
			const updatedVal = this._value.toArray().map((vec) => vec.clone());
			this.node.state[this.propName].push(...updatedVal);
		}
	}
	onPropChange(_oldVal, newVal) {
		this.handleEnvelopeChange(newVal);
		this.output && this.output.setData(newVal);
	}
	onDown(event) {
		this.currHitPoint = this.getHitPoint(event.realPos);
		this.lastDownPosition = event.realPos;
	}
	onUp(event) {
		let [width, height] = [this.width - this.style.pointDiameter, this.height - this.style.pointDiameter];
		if (!this.currHitPoint && this.lastDownPosition) {
			if (Vector.Distance(this.lastDownPosition, event.realPos) <= 2) this.newPoint(event.realPos, width, height);
		} else if (this.currHitPoint && this.currHitPoint === this.getHitPoint(event.realPos) && this.lastDownPosition && this.lastDownPosition.isEqual(event.realPos, .5)) this.deletePoint();
		else if (this.currHitPoint) {
			if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, null, this._value.toArray());
		}
		this.currHitPoint = void 0;
		this.lastDownPosition = void 0;
	}
	onDrag(event) {
		if (this.currHitPoint) this.movePoint(event.realPos);
	}
	onExit(event) {
		if (this.currHitPoint) {
			this.movePoint(event.realPos);
			if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, null, this._value.toArray());
		}
		this.currHitPoint = void 0;
		this.lastDownPosition = void 0;
	}
};
var DefaultEnvelopeStyle = () => ({
	backgroundColor: "#4a4a4a",
	pointDiameter: 10,
	pointColor: "#9cffee",
	lineColor: "#9cd6ff",
	borderColor: "#000",
	borderWidth: 1
});
var DefaultEnvelopeOptions = () => ({
	height: 100,
	values: []
});
//#endregion
//#region src/resource/icons.ts
var fileIcon = "data:image/svg+xml;charset=UTF-8,%3c?xml version='1.0' encoding='iso-8859-1'?%3e%3c!-- --%3e%3csvg version='1.1' id='Capa_1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' viewBox='0 0 477.867 477.867' style='enable-background:new 0 0 477.867 477.867;' xml:space='preserve'%3e%3cg%3e%3cg%3e%3cpath d='M324.267,119.467c-9.426,0-17.067-7.641-17.067-17.067V0H102.4C74.123,0,51.2,22.923,51.2,51.2v375.467 c0,28.277,22.923,51.2,51.2,51.2h273.067c28.277,0,51.2-22.923,51.2-51.2v-307.2H324.267z'/%3e%3c/g%3e%3c/g%3e%3cg%3e%3cg%3e%3cpolygon points='341.333,10.001 341.333,85.333 416.666,85.333 '/%3e%3c/g%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3cg%3e%3c/g%3e%3c/svg%3e";
//#endregion
//#region src/ui/image.ts
var Image = class extends UINode {
	get src() {
		if (this.propName) return this.getProp();
		return this._src;
	}
	set src(src) {
		let oldVal = this._src;
		let newVal = src;
		if (this.propName) this.setProp(newVal);
		else this._src = newVal;
		this.setupImage();
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, oldVal, newVal);
	}
	constructor(_node, _options = DefaultImageOptions()) {
		super();
	}
	created(options) {
		options = {
			...DefaultImageOptions(),
			...options
		};
		const { style, src } = options;
		this.style = {
			...DefaultImageStyle(),
			...style
		};
		this._src = src ?? "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAQAAAAAYLlVAAAABGdBTUEAALGPC/xhBQAAACBjSFJNAAB6JgAAgIQAAPoAAACA6AAAdTAAAOpgAAA6mAAAF3CculE8AAAAAmJLR0QAAKqNIzIAAAAJcEhZcwAADsQAAA7EAZUrDhsAAAAHdElNRQfjDAMLKTYM6GTwAAACaElEQVRo3u2ZP2gUQRSHv90zGKMY/IPxBBGSEGKUixGsFFSQnAZEbUQrEXuvCIiIiKWCbbQXLBRsgpVgockhIhYJGg0oJEWwuNOYYJI7E29scufuZndvNjs30+yb5vbd2/l9Ozvv7cyuRdU6ybATPVZknK9Ox2FGEZrbCH1V+dMsapcXCBbJgkWaL2zVNPRem6M7xV1OGpKHZrCYpMsYAExalNhYO8yzpEF0E0drv8u4pkW7lqtud2raWiRDLAEwDrBBOrKFixxnFwXe8JQFdQhyWZBlxhH3nYEYiq4skAMYYMVTx1c4pw+glaLPo+Qn21UAyEzCq+zw8W7jWozbUDMZgFMR/coBdgf407oAZiP6lQPkA/yjKgBksmAfJZ8sKNOxTsXIWTDNLR/vHb6FnJOihzZVIwBwk2VXGbqNFdLn+dW6+ZYD9UZAfkGynyE+UuQTj3y7/W/9Dth5TqgCkLW9FFx9lrigE6CJ/JrpuswVfQBDvluQCtf1AFwO3AVVuNF4gIP8Dt2K3WsswBYm6u4GH2I3CsDiWV15geAJTY0BGJSSFwiG6YkCkJYqqMf4Iw0gGJMDaOEBPxAIZsiFrp7bXAvWqC0AYA/vXf+8C7w9KV7GkA8AyDC9JnCOS74A92PJ+wKcYT4g+DGbPfJnqagGyPE3JHyCjEO+k9mY8h6AroBa7mxL5Fblm/kQW94DMCZ50nMOcYRXCuSFhVh38VFixrfnCUACkAAkAAlAApAA2JSN6pdtpowCTNm8MAowbJHmM62G5H/RDZA19Ol2gf4qSR8j2uVf0ws43vZ10Kvt832B8eprzn/ptwK4iL9QVQAAACV0RVh0ZGF0ZTpjcmVhdGUAMjAxOS0xMi0wM1QxMTo0MTo1NCswMDowME8knnIAAAAldEVYdGRhdGU6bW9kaWZ5ADIwMTktMTItMDNUMTE6NDE6NTQrMDA6MDA+eSbOAAAAGXRFWHRTb2Z0d2FyZQB3d3cuaW5rc2NhcGUub3Jnm+48GgAAAABJRU5ErkJggg==";
		this.setupImage();
	}
	setupImage() {
		if (!this.source) {
			this.source = document.createElement("img");
			this.source.onerror = (error) => Log.error(error);
			this.source.onload = () => {
				this.imageCanvas.width = this.source.width;
				this.imageCanvas.height = this.source.height;
				this.imageContext.drawImage(this.source, 0, 0);
				this.ratio = this.source.width / this.source.height;
				this.reflow();
				this.node.ui?.update();
			};
		}
		if (!this.imageCanvas) {
			if (typeof OffscreenCanvas !== "undefined") this.imageCanvas = new OffscreenCanvas(0, 0);
			else {
				this.imageCanvas = document.createElement("canvas");
				this.imageCanvas.width = 0;
				this.imageCanvas.height = 0;
			}
			this.imageContext = this.imageCanvas.getContext("2d");
		}
		this.source.src = this._src;
	}
	paint() {
		if (this.imageCanvas && this.imageCanvas.width > 0 && this.imageCanvas.height > 0) {
			let x = this.position.x;
			this.context.drawImage(this.imageCanvas, 0, 0, this.source.width, this.source.height, x, this.position.y, this.source.width < this.width ? this.source.width : this.width, this.height);
		}
	}
	paintLOD1() {
		if (this.imageCanvas) {
			let x = this.position.x;
			if (this.source.width < this.node.ui.contentWidth) {
				if (this.style.align === Align.Center) x += this.node.ui.contentWidth / 2 - this.source.width / 2;
				else if (this.style.align === Align.Right) x += +(this.node.ui.contentWidth - this.source.width);
			}
			let context = this.context;
			context.fillStyle = "lightgrey";
			context.strokeStyle = "#000";
			context.fillRect(x, this.position.y, this.source.width < this.width ? this.source.width : this.width, this.height);
			context.strokeRect(x, this.position.y, this.source.width < this.width ? this.source.width : this.width, this.height);
		}
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		let x = this.position.x;
		this.offUIContext.fillRect(x, this.position.y, this.source.width < this.width ? this.source.width : this.width, this.height);
	}
	reflow() {
		if (!this.source.width || !this.source.height) return;
		if (this.source.width > this.width) this.height = this.width / this.ratio;
		else {
			this.width = this.source.width;
			this.height = this.source.height;
		}
	}
	onPropChange() {}
};
var DefaultImageStyle = () => ({ align: Align.Left });
var DefaultImageOptions = () => ({});
//#endregion
//#region src/ui/input.ts
var Input = class extends UINode {
	get value() {
		if (this.propName) return this.getProp();
		return this._value;
	}
	set value(value) {
		let val;
		if (this.style.type === "number" && typeof value === "string") val = parseFloat(value);
		else val = value;
		let oldVal = this.value;
		let newVal = val;
		if (this.propName) this.setProp(newVal);
		else {
			this._value = newVal;
			this.setLabelText(this._value);
			this.inputEl.value = this._value.toString();
		}
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, newVal, oldVal);
	}
	constructor(node, _options = DefaultInputOptions(node)) {
		super();
	}
	created(options) {
		options = {
			...DefaultInputOptions(this.node),
			...options
		};
		const { style = {}, height, input, output } = options;
		this.height = height ?? this.node.style?.rowHeight ?? 0;
		this.style = {
			...DefaultInputStyle(),
			...style
		};
		if (this.style.type === "number" && typeof options.value === "string") options.value = parseFloat(options.value);
		this._value = options.value;
		this.setupLabel();
		this.setupInputElement();
		if (input) {
			const terminal = this.createTerminal(TerminalType.IN, options.style?.type);
			terminal.on("connect", (_, connector) => {
				if (connector.data) this.value = connector.data;
			});
			terminal.on("data", (_, data) => {
				if (data) this.value = data;
			});
		}
		if (output) this.createTerminal(TerminalType.OUT, options.style?.type).on("connect", (_, connector) => connector.data = this.value);
		this.node.on("process", () => {
			this.output?.setData(this.value);
		});
	}
	setupLabel() {
		this.label = this.node.createUI("core/label", {
			text: this.value.toString(),
			style: {
				fontSize: this.style.fontSize,
				font: this.style.font,
				align: this.style.align,
				color: this.style.color,
				padding: 5,
				precision: this.style.precision ?? void 0
			},
			height: this.height
		});
		this.label.on("click", () => {
			if (document.activeElement !== this.inputEl) {
				const realPosition = this.position.transform(this.node.flow.flowConnect.transform);
				Object.assign(this.inputEl.style, {
					visibility: "visible",
					pointerEvents: "all",
					top: realPosition.y + this.node.flow.flowConnect.canvasDimensions.top + 1 + "px",
					left: realPosition.x + this.node.flow.flowConnect.canvasDimensions.left + 1 + "px",
					width: (this.width - 1) * this.node.flow.flowConnect.scale + "px",
					height: (this.height - 1) * this.node.flow.flowConnect.scale + "px",
					fontFamily: this.style.font,
					fontSize: parseInt(this.style.fontSize?.replace("px", "")) * this.node.flow.flowConnect.scale + "px",
					color: this.style.color,
					backgroundColor: this.inputEl.validity.patternMismatch ? "red" : this.style.backgroundColor,
					textAlign: this.style.align
				});
				if (this.style.type === "number") this.inputEl.step = this.style.step;
				this.inputEl.focus();
			}
		});
		this.children.push(this.label);
	}
	setupInputElement() {
		this.inputEl = document.createElement("input");
		this.inputEl.className = "flow-connect-input";
		this.inputEl.spellcheck = false;
		const inputType = this.style.type === "string" ? "text" : this.style.type;
		this.inputEl.type = this.style.pattern ? "text" : inputType;
		this.inputEl.value = this.value.toString();
		this.inputEl.style.color = this.style.color ?? "";
		this.inputEl.style.backgroundColor = this.style.backgroundColor ?? "";
		this.inputEl.style.border = this.style.border ?? "";
		if (this.style.pattern) this.inputEl.pattern = this.style.pattern;
		if (this.style.type === "number" && this.style.step) this.inputEl.step = this.style.step;
		if (exists(this.style.maxLength)) this.inputEl.maxLength = this.style.maxLength;
		this.inputEl.addEventListener("blur", () => {
			this.inputEl.style.visibility = "hidden";
			this.inputEl.style.pointerEvents = "none";
			this.value = this.inputEl.value;
			this.setLabelText(this.style.type === "number" ? parseFloat(this.value) : this.value.toString());
			this.call("blur", this);
		});
		this.inputEl.oninput = () => {
			if (this.style.pattern) {
				this.inputEl.style.backgroundColor = this.inputEl.validity.patternMismatch ? "orange" : this.style.backgroundColor ?? "";
				this.label.style.color = this.inputEl.validity.patternMismatch ? "orange" : this.style.color;
			}
			this.call("input", this);
		};
		document.body.appendChild(this.inputEl);
	}
	setLabelText(value) {
		this.label.text = typeof value === "number" && exists(this.style.precision) ? value.toFixed(this.style.precision) : value;
	}
	paint() {
		this.context.strokeStyle = this.style.border ?? "";
		this.context.strokeRect(this.position.x, this.position.y, this.width, this.height);
	}
	paintLOD1() {
		let context = this.context;
		context.strokeStyle = this.style.border ?? "";
		context.fillStyle = this.style.backgroundColor ?? "";
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		this.label.position = this.position;
		this.label.height = this.height;
		this.label.width = this.width;
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
	}
	onPropChange(_oldVal, newVal) {
		if (this.style.type === "number" && typeof newVal === "string") newVal = parseFloat(newVal);
		this._value = newVal;
		this.setLabelText(this._value);
		this.inputEl.value = this._value.toString();
		this.output?.setData(this._value);
	}
};
var InputType = /* @__PURE__ */ function(InputType) {
	InputType["Text"] = "string";
	InputType["Number"] = "number";
	return InputType;
}({});
var DefaultInputStyle = () => ({
	backgroundColor: "#eee",
	color: "#000",
	fontSize: "11px",
	font: "arial",
	border: "black",
	align: Align.Left,
	type: "string"
});
var DefaultInputOptions = (node) => ({ height: node.style?.rowHeight * 1.5 });
//#endregion
//#region src/ui/label.ts
var Label = class extends UINode {
	get text() {
		let value;
		if (this.propName) value = this.getProp();
		else value = this._text;
		if (typeof value === "number") value = this.format(value);
		return value;
	}
	set text(text) {
		let oldVal = this._text;
		let newVal = text;
		if (this.propName) this.setProp(newVal);
		else {
			this._text = newVal;
			this.reflow();
		}
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, oldVal, newVal);
	}
	constructor(_node, options) {
		super();
		options = {
			...DefaultLabelOptions(),
			...options
		};
		const { style = {} } = options;
		this.style = {
			...DefaultLabelStyle(),
			...style
		};
	}
	created(options) {
		const { text = "Label", input, output, height } = options;
		if (input) {
			const terminal = this.createTerminal(TerminalType.IN, "any");
			terminal.on("connect", (_, connector) => {
				if (connector.data) this.text = connector.data;
			});
			terminal.on("data", (_, data) => {
				if (data) this.text = data;
			});
		}
		if (output) this.createTerminal(TerminalType.OUT, "string").on("connect", (_, connector) => connector.data = this.text);
		this._text = this.propName ? this.getProp() : text;
		this.reflow();
		this.height = height ?? this.textHeight + 5;
		if (!this.style.grow) this.width = this.orgTextWidth;
		this.node.on("process", () => this.output?.setData(this.text));
	}
	paint() {
		let context = this.context;
		context.fillStyle = this.style.backgroundColor ?? "";
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
		context.fillStyle = this.style.color ?? "";
		context.font = this.style.fontSize + " " + this.style.font;
		context.textBaseline = "middle";
		let y = this.position.y + this.height / 2;
		let x = this.position.x;
		if (this.style.align === Align.Center) x += this.width / 2 - this.textWidth / 2;
		else if (this.style.align === Align.Right) x += this.width - this.textWidth - this.style.padding;
		else x += this.style.padding;
		context.fillText(this.displayText, x, y);
	}
	paintLOD1() {
		let context = this.context;
		context.strokeStyle = "#000";
		context.fillStyle = this.style.color ?? "";
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		let context = this.context;
		context.font = this.style.fontSize + " " + this.style.font;
		this.orgTextWidth = context.measureText(this.text).width;
		this.displayText = this.getBestFitString();
		let metrics = context.measureText(this.displayText);
		context.font = "";
		this.textWidth = metrics.width;
		this.textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent + 5;
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
	}
	getBestFitString() {
		let text = this.text;
		if (typeof text !== "string") text = text.toString();
		let width = this.context.measureText(text).width;
		const ellipsis = "…";
		const ellipsisWidth = this.context.measureText(ellipsis).width;
		if (width <= this.width || width <= ellipsisWidth) return text;
		const index = binarySearch({
			max: text.length,
			getValue: (idx) => this.context.measureText(text.substring(0, idx)).width,
			match: this.width - ellipsisWidth
		});
		return text.substring(0, index) + ellipsis;
	}
	format(value) {
		return exists(this.style.precision) ? value.toFixed(this.style.precision) : value.toString();
	}
	onPropChange(_oldVal, newVal) {
		this._text = newVal;
		this.reflow();
		this.output && this.output.setData(this._text);
	}
};
var DefaultLabelStyle = () => ({
	color: "#000",
	backgroundColor: "transparent",
	fontSize: "11px",
	font: "arial",
	align: Align.Left,
	padding: 0
});
var DefaultLabelOptions = () => ({ text: "Label" });
//#endregion
//#region src/ui/radio-group.ts
var RadioGroup = class extends UINode {
	get selected() {
		if (this.propName) return this.getProp();
		return this._selected;
	}
	set selected(selected) {
		if (!this._values.includes(selected)) selected = this._values[0];
		let oldVal = this.selected;
		let newVal = selected;
		if (this.propName) this.setProp(newVal);
		else this._selected = newVal;
		this.setLabelStyle();
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, oldVal, newVal);
	}
	get values() {
		return [...this._values];
	}
	constructor(node, _options = DefaultRadioGroupOptions(node)) {
		super();
	}
	created(options) {
		options = {
			...DefaultRadioGroupOptions(this.node),
			...options
		};
		const { height, style = {}, values = ["Option1", "Option2"], selected = values[0], input, output } = options;
		this._values = values;
		const selectedValue = this.propName ? this.getProp() : selected;
		this._selected = this._values.includes(selectedValue) ? selectedValue : this._values[0];
		this.style = {
			...DefaultRadioGroupStyle(),
			...style
		};
		this.height = height ?? this.node.style?.rowHeight ?? 0;
		if (input) {
			const terminal = this.createTerminal(TerminalType.IN, "string");
			terminal.on("connect", (_, connector) => {
				if (connector.data) this._selected = connector.data;
			});
			terminal.on("data", (_, data) => {
				if (typeof data !== "undefined") this._selected = data;
			});
		}
		if (output) this.createTerminal(TerminalType.OUT, "string").on("connect", (_, connector) => connector.data = this._selected);
		this.setupLabels();
		this.node.on("process", () => {
			this.output?.setData(this._selected);
		});
	}
	setupLabels() {
		this.children.push(...this._values.map((option) => {
			const label = this.node.createUI("core/label", {
				text: option,
				style: {
					align: Align.Center,
					backgroundColor: this.style.backgroundColor,
					color: this.style.color
				}
			});
			label.on("click", (event) => {
				if (event.target.text === this.selected) return;
				this.selected = event.target.text;
			});
			return label;
		}));
		Object.assign(this.children[this._values.indexOf(this._selected)].style, {
			backgroundColor: this.style.selectedBackgroundColor,
			color: this.style.selectedColor
		});
	}
	setLabelStyle() {
		this.children.forEach((label) => {
			Object.assign(label.style, {
				backgroundColor: this.style.backgroundColor,
				color: this.style.color
			});
		});
		const selectedLabel = this.children[this._values.indexOf(this.selected)];
		Object.assign(selectedLabel.style, {
			backgroundColor: this.style.selectedBackgroundColor,
			color: this.style.selectedColor
		});
	}
	paint() {
		let context = this.context;
		context.strokeStyle = this.style.borderColor ?? "";
		context.lineWidth = this.style.borderWidth ?? 0;
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		let commonWidth = this.width / this.children.length;
		let x = this.position.x + commonWidth;
		for (let i = 0; i < this.children.length - 1; i += 1) {
			context.beginPath();
			context.moveTo(x, this.position.y);
			context.lineTo(x, this.position.y + this.height);
			context.stroke();
			x += commonWidth;
		}
	}
	paintLOD1() {
		let context = this.context;
		context.strokeStyle = this.style.borderColor ?? "";
		context.fillStyle = this.style.backgroundColor ?? "";
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		let x = this.position.x;
		this.children.forEach((child) => {
			child.height = this.height;
			child.width = this.width / this.children.length;
			child.position.assign(x, this.position.y);
			x += child.width;
		});
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
	}
	onPropChange(_oldVal, newVal) {
		if (!this._values.includes(newVal)) newVal = this._values[0];
		this._selected = newVal;
		this.setLabelStyle();
		this.output?.setData(this._selected);
	}
};
var DefaultRadioGroupStyle = () => ({
	color: "#000",
	selectedColor: "#fff",
	backgroundColor: "transparent",
	selectedBackgroundColor: "#555",
	borderColor: "#000",
	borderWidth: 1
});
var DefaultRadioGroupOptions = (node) => ({
	values: ["Option1", "Option2"],
	height: node.style?.rowHeight * 1.5
});
//#endregion
//#region src/ui/select.ts
var Select = class extends UINode {
	get selected() {
		if (this.propName) {
			let value = this.getProp();
			let slctdVal = this.values.includes(value) ? value : this.values[0];
			value = this.values.length === 0 ? "None" : slctdVal;
			return value;
		}
		return this._selected;
	}
	set selected(selected) {
		let slctdVal = this.values.includes(selected) ? selected : this.values[0];
		let oldVal = this.selected;
		let newVal = this.values.length === 0 ? "None" : slctdVal;
		if (this.propName) this.setProp(newVal);
		else {
			this._selected = newVal;
			this.label.text = newVal;
		}
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, oldVal, newVal);
	}
	constructor(node, _options = DefaultSelectOptions(node)) {
		super();
	}
	created(options) {
		options = {
			...DefaultSelectOptions(this.node),
			...options
		};
		const { height, style = {}, values = ["Option1", "Option2"], selected = values[0], input, output } = options;
		this.values = values;
		this.style = {
			...DefaultSelectStyle(),
			...style
		};
		this.height = height ?? this.node.style?.rowHeight;
		this._selected = selected ?? this.values[0];
		this.label = this.node.createUI("core/label", {
			text: this.selected,
			style: {
				align: Align.Center,
				...this.style
			},
			height: this.height
		});
		this.children.push(this.label);
		if (input) {
			const terminal = this.createTerminal(TerminalType.IN, "any");
			terminal.on("connect", (_, connector) => {
				if (typeof connector.data === "number") this.selected = this.values[connector.data];
				else if (typeof connector.data === "string") this.selected = connector.data;
			});
			terminal.on("data", (_, data) => {
				if (typeof data === "number") this.selected = this.values[data];
				else if (typeof data === "string") this.selected = data;
			});
		}
		if (output) this.createTerminal(TerminalType.OUT, "string").on("connect", (_, connector) => connector.data = this.selected);
		this.node.on("process", () => {
			this.output?.setData(this.selected);
		});
	}
	paint() {
		let context = this.context;
		context.fillStyle = this.style.arrowColor ?? "";
		context.beginPath();
		context.moveTo(this.position.x, this.position.y + this.height / 2);
		context.lineTo(this.position.x + this.height * Constant.SIN_60, this.position.y);
		context.lineTo(this.position.x + this.height * Constant.SIN_60, this.position.y + this.height);
		context.lineTo(this.position.x, this.position.y + this.height / 2);
		context.closePath();
		context.fill();
		context.fillStyle = this.style.arrowColor ?? "";
		context.beginPath();
		context.moveTo(this.position.x + this.width, this.position.y + this.height / 2);
		context.lineTo(this.position.x + this.width - this.height * Constant.SIN_60, this.position.y);
		context.lineTo(this.position.x + this.width - this.height * Constant.SIN_60, this.position.y + this.height);
		context.lineTo(this.position.x + this.width, this.position.y + this.height / 2);
		context.closePath();
		context.fill();
	}
	paintLOD1() {
		let context = this.context;
		context.fillStyle = this.style.arrowColor ?? "";
		context.strokeStyle = "#000";
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		this.label.width = this.width * .7;
		this.label.position.assign(this.position.x + this.width * .15, this.position.y + this.height / 2 - this.label.height / 2);
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
	}
	onPropChange(_oldVal, newValue) {
		let slctdVal = this.values.includes(newValue) ? newValue : this.values[0];
		let value = this.values.length === 0 ? "None" : slctdVal;
		this._selected = value;
		this.label.text = this._selected;
		this.output?.setData(this.selected);
	}
	onClick(event) {
		if (this.values.length === 0) return;
		let direction;
		if (event.realPos.x < this.position.x + this.width * .15) direction = -1;
		else if (event.realPos.x > this.position.x + this.width * .85) direction = 1;
		else return;
		let length = this.values.length;
		this.selected = this.values[((this.values.indexOf(this.selected) + direction) % length + length) % length];
		this.label.text = this.selected;
	}
};
var DefaultSelectStyle = () => ({ arrowColor: "#000" });
var DefaultSelectOptions = (node) => ({
	values: ["Option1", "Option2"],
	height: node.style?.rowHeight * 1.5
});
//#endregion
//#region src/ui/slider.ts
var Slider = class extends UINode {
	get value() {
		if (this.propName) return this.getProp();
		return this._value;
	}
	set value(value) {
		value = clamp(value, this.min, this.max);
		let oldVal = this.value;
		let newVal = value;
		newVal = exists(this.style.precision) ? parseFloat(newVal.toFixed(this.style.precision)) : newVal;
		if (this.propName) this.setProp(newVal);
		else {
			this._value = newVal;
			this.reflow();
		}
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, oldVal, newVal);
	}
	constructor(node, _options = DefaultSliderOptions(node)) {
		super();
		this.draggable = true;
	}
	created(options) {
		options = {
			...DefaultSliderOptions(this.node),
			...options
		};
		const { style = {}, height, min = 0, max = 100, value = min, input, output } = options;
		this.min = min;
		this.max = max;
		this.height = height ?? this.node.style?.rowHeight ?? 0;
		this.style = {
			...DefaultSliderStyle(this.node, height),
			...style
		};
		this._value = this.propName ? this.getProp() : value;
		this._value = clamp(this._value, this.min, this.max);
		if (input) {
			const terminal = this.createTerminal(TerminalType.IN, "number");
			terminal.on("connect", (_, connector) => {
				if (connector.data) this.value = connector.data;
			});
			terminal.on("data", (_, data) => {
				if (data) this.value = data;
			});
		}
		if (output) this.createTerminal(TerminalType.OUT, "number").on("connect", (_, connector) => connector.data = this.value);
		this.node.on("process", () => {
			this.output?.setData(this.value);
		});
	}
	paint() {
		let context = this.context;
		context.lineWidth = this.style.railHeight ?? 0;
		context.strokeStyle = this.style.color ?? "";
		context.lineCap = "butt";
		let start = Math.max(this.position.x, this.position.x + this.thumbFill - 3);
		if (start !== this.position.x) {
			context.beginPath();
			context.moveTo(this.position.x, this.position.y + this.height / 2);
			context.lineTo(start, this.position.y + this.height / 2);
			context.stroke();
		}
		start = Math.min(this.position.x + 2 * this.style.thumbRadius + this.thumbFill + 3, this.position.x + this.width);
		if (start !== this.position.x + this.width) {
			context.beginPath();
			context.moveTo(start, this.position.y + this.height / 2);
			context.lineTo(this.position.x + this.width, this.position.y + this.height / 2);
			context.stroke();
		}
		context.fillStyle = this.style.thumbColor ?? "";
		context.beginPath();
		context.arc(this.position.x + this.style.thumbRadius + this.thumbFill, this.position.y + this.height / 2, this.style.thumbRadius, 0, Constant.TAU);
		context.fill();
	}
	paintLOD1() {
		let context = this.context;
		context.strokeStyle = "#000";
		context.fillStyle = this.style.color ?? "";
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		this.thumbFill = denormalize(normalize(this.value, this.min, this.max), 0, this.width - 2 * this.style.thumbRadius);
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
	}
	onPropChange(_oldVal, newVal) {
		this._value = newVal;
		this.reflow();
		this.output?.setData(this.value);
	}
	onDrag(event) {
		let y = this.position.y + this.height / 2 - this.style.railHeight / 2;
		this.thumbFill = event.realPos.clamp(this.position.x + this.style.thumbRadius, this.position.x + this.width - this.style.thumbRadius, y, y).subtractInPlace(this.position.x + this.style.thumbRadius, 0).x;
		this.value = denormalize(normalize(this.thumbFill, 0, this.width - 2 * this.style.thumbRadius), this.min, this.max);
	}
};
var DefaultSliderStyle = (node, height) => ({
	color: "#444",
	thumbColor: "#000",
	railHeight: 3,
	thumbRadius: exists(height) ? height / 2 : node.style?.rowHeight * 1.5 / 2
});
var DefaultSliderOptions = (node) => ({
	min: 0,
	max: 100,
	height: node.style?.rowHeight * 1.5
});
//#endregion
//#region src/ui/source.ts
var Source = class extends UINode {
	get file() {
		if (this.propName) return this.getProp();
		return this._file;
	}
	set file(file) {
		let oldVal = this.file;
		let newVal = file;
		if (this.propName) this.setProp(newVal);
		else {
			this._file = newVal;
			this.label.text = newVal.name.substring(0, this._file.name.toString().lastIndexOf("."));
		}
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, oldVal, newVal);
	}
	constructor(node, _options = DefaultSourceOptions(node)) {
		super();
		this.actionOverride = false;
	}
	created(options) {
		options = {
			...DefaultSourceOptions(this.node),
			...options
		};
		const { style = {}, height, accept, input, output, file, actionOverride } = options;
		this.style = {
			...DefaultSourceStyle(),
			...style
		};
		this.accept = accept;
		this.actionOverride = actionOverride;
		this.height = height ?? this.node.style?.rowHeight;
		this.setupInputElement(options);
		this.label = this.node.createUI("core/label", {
			text: "Select",
			style: {
				align: Align.Center,
				...this.style
			},
			height: this.height
		});
		this.fileIcon = this.node.createUI("core/image", { src: fileIcon });
		this.label.on("click", () => !this.actionOverride && this.htmlInput.click());
		this.children.push(this.label, this.fileIcon);
		if (input) {
			const terminal = this.createTerminal(TerminalType.IN, "file");
			terminal.on("connect", (_, connector) => {
				if (connector.data) this.file = connector.data;
			});
			terminal.on("data", (_, data) => {
				if (data) this.file = data;
			});
		}
		if (output) this.createTerminal(TerminalType.OUT, "file").on("connect", (_, connector) => connector.data = this.file);
		this.node.on("process", () => {
			this.output?.setData(this.file);
		});
		if (file) this.file = options.file;
	}
	setupInputElement(options) {
		this.htmlInput = document.createElement("input");
		this.htmlInput.type = "file";
		if (options.accept) this.htmlInput.accept = options.accept;
		this.htmlInput.onchange = () => {
			if (this.htmlInput.files.length > 0) {
				let oldVal = this.file;
				this.file = this.htmlInput.files[0];
				if (this.node.flow.state === FlowState.Stopped) this.call("upload", this, oldVal, this.file);
			}
		};
	}
	paint() {
		this.context.strokeStyle = this.style.borderColor ?? "";
		this.context.strokeRect(this.position.x, this.position.y, this.width, this.height);
	}
	paintLOD1() {
		let context = this.context;
		context.strokeStyle = this.style.borderColor ?? "";
		context.fillStyle = this.style.color ?? "";
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		this.label.width = this.width;
		this.label.height = this.height;
		this.label.position = this.position;
		this.fileIcon.width = this.width * .1;
		this.fileIcon.position.assign(this.position.x + 5, this.position.y + this.height / 2 - this.fileIcon.height / 2);
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
	}
	onPropChange(_oldVal, newVal) {
		this._file = newVal;
		this.label.text = this._file ? this._file.name.substring(0, this._file.name.toString().lastIndexOf(".")) : "Select";
		this.output?.setData(this._file);
	}
};
var DefaultSourceStyle = () => ({ borderColor: "#000" });
var DefaultSourceOptions = (node) => ({ height: node.style?.rowHeight * 1.5 });
//#endregion
//#region src/ui/toggle.ts
var Toggle = class extends UINode {
	get checked() {
		if (this.propName) return this.getProp();
		return this._checked;
	}
	set checked(checked) {
		let oldVal = this.checked;
		let newVal = checked;
		if (this.propName) this.setProp(newVal);
		else this._checked = newVal;
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, oldVal, newVal);
	}
	constructor(node, _options = DefaultToggleOptions(node)) {
		super();
		this._checked = false;
	}
	created(options) {
		options = {
			...DefaultToggleOptions(this.node),
			...options
		};
		const { style = {}, height, value, input, output } = options;
		this.style = {
			...DefaultToggleStyle(),
			...style
		};
		this._checked = this.propName ? this.getProp() : value;
		this.height = height ?? this.node.style?.rowHeight;
		if (!this.style.grow) this.width = this.height * 2.5;
		if (input) {
			const terminal = this.createTerminal(TerminalType.IN, "boolean");
			terminal.on("connect", (_, connector) => {
				if (connector.data) this.checked = connector.data;
			});
			terminal.on("data", (_, data) => {
				if (typeof data !== "undefined") this.checked = data;
			});
		}
		if (output) this.createTerminal(TerminalType.OUT, "boolean").on("connect", (_, connector) => connector.data = this.checked);
		this.node.on("process", () => {
			this.output?.setData(this.checked);
		});
	}
	paint() {
		let context = this.context;
		context.strokeStyle = this.style.backgroundColor ?? "";
		context.lineWidth = this.height * .75;
		context.lineCap = "round";
		context.beginPath();
		context.moveTo(this.position.x + this.context.lineWidth / 2, this.position.y + this.height / 2);
		context.lineTo(this.position.x + this.width - this.context.lineWidth / 2, this.position.y + this.height / 2);
		context.stroke();
		context.fillStyle = this.style.color ?? "";
		context.beginPath();
		context.arc(this.checked ? this.position.x + this.width - this.height / 2 : this.position.x + this.height / 2, this.position.y + this.height / 2, this.height / 2, 0, Constant.TAU);
		context.fill();
	}
	paintLOD1() {
		let context = this.context;
		context.strokeStyle = this.style.color ?? "";
		context.fillStyle = this.style.backgroundColor ?? "";
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
	}
	onPropChange(_oldVal, newVal) {
		this._checked = newVal;
		this.output?.setData(this.checked);
	}
	onClick() {
		this.checked = !this.checked;
	}
};
var DefaultToggleOptions = (node) => ({
	value: false,
	height: node.style?.rowHeight * 1.5
});
var DefaultToggleStyle = () => ({
	backgroundColor: "#999",
	color: "#000"
});
//#endregion
//#region src/ui/v-slider.ts
var VSlider = class extends UINode {
	get value() {
		if (this.propName) return this.getProp();
		return this._value;
	}
	set value(value) {
		value = clamp(value, this.min, this.max);
		let oldVal = this.value;
		let newVal = value;
		if (this.propName) this.setProp(newVal);
		else {
			this._value = newVal;
			this.reflow();
		}
		if (this.node.flow.state !== FlowState.Stopped) this.call("change", this, oldVal, newVal);
	}
	constructor(node, _options = DefaultVSliderOptions(node)) {
		super();
		this.draggable = true;
	}
	created(options) {
		options = {
			...DefaultVSliderOptions(this.node),
			...options
		};
		const { height, width, style = {}, min = 0, max = 100, value = min, input, output } = options;
		this.min = min;
		this.max = max;
		this.style = {
			...DefaultVSliderStyle(this.node, width),
			...style
		};
		this.height = height ?? this.node.style?.rowHeight * 5;
		this.width = width ?? this.node.style?.rowHeight;
		this._value = this.propName ? this.getProp() : value;
		this._value = clamp(this._value, this.min, this.max);
		if (input) {
			const terminal = this.createTerminal(TerminalType.IN, "number");
			terminal.on("connect", (_, connector) => {
				if (connector.data) this.value = connector.data;
			});
			terminal.on("data", (_, data) => {
				if (data) this.value = data;
			});
		}
		if (output) this.createTerminal(TerminalType.IN, "number").on("connect", (_, connector) => connector.data = this.value);
		this.node.on("process", () => {
			this.output?.setData(this.value);
		});
	}
	paint() {
		let context = this.context;
		context.lineWidth = this.style.railWidth ?? 0;
		context.strokeStyle = this.style.color ?? "";
		context.lineCap = "butt";
		let start = Math.max(this.position.y, this.position.y + this.thumbFill - 3);
		if (start !== this.position.y) {
			context.beginPath();
			context.moveTo(this.position.x + this.width / 2, this.position.y);
			context.lineTo(this.position.x + this.width / 2, start);
			context.stroke();
		}
		start = Math.min(this.position.y + 2 * this.style.thumbRadius + this.thumbFill + 3, this.position.y + this.height);
		if (start !== this.position.y + this.height) {
			context.beginPath();
			context.moveTo(this.position.x + this.width / 2, start);
			context.lineTo(this.position.x + this.width / 2, this.position.y + this.height);
			context.stroke();
		}
		context.fillStyle = this.style.thumbColor ?? "";
		context.beginPath();
		context.arc(this.position.x + this.width / 2, this.position.y + this.style.thumbRadius + this.thumbFill, this.style.thumbRadius, 0, Constant.TAU);
		context.fill();
	}
	paintLOD1() {
		let context = this.context;
		context.strokeStyle = "#000";
		context.fillStyle = this.style.color ?? "";
		context.strokeRect(this.position.x, this.position.y, this.width, this.height);
		context.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		this.thumbFill = denormalize(1 - normalize(this.value, this.min, this.max), 0, this.height - 2 * this.style.thumbRadius);
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
	}
	onPropChange(_oldVal, newVal) {
		this._value = newVal;
		this.reflow();
		this.output?.setData(this.value);
	}
	onDrag(event) {
		let x = this.position.x + this.width / 2 - this.style.railWidth / 2;
		this.thumbFill = event.realPos.clamp(x, x, this.position.y + this.style.thumbRadius, this.position.y + this.height - this.style.thumbRadius).subtractInPlace(0, this.position.y + this.style.thumbRadius).y;
		this.value = denormalize(1 - normalize(this.thumbFill, 0, this.height - 2 * this.style.thumbRadius), this.min, this.max);
	}
};
var DefaultVSliderStyle = (node, width) => ({
	color: "#444",
	thumbColor: "#000",
	railWidth: 4,
	thumbRadius: typeof width !== "undefined" ? width / 2 : node.style?.rowHeight * 1.5 / 2
});
var DefaultVSliderOptions = (node) => {
	return {
		min: 0,
		max: 100,
		value: 0,
		width: node.style?.rowHeight,
		height: node.style?.rowHeight * 5
	};
};
//#endregion
//#region src/ui/stack.ts
var Stack = class extends UINode {
	constructor(node, _options = DefaultStackOptions(node)) {
		super();
	}
	created(options) {
		options = {
			...DefaultStackOptions(this.node),
			...options
		};
		const { height, style = {}, childs = [] } = options;
		this.style = {
			...DefaultStackStyle(),
			...style
		};
		this.height = height ?? this.node.style?.rowHeight;
		if (childs) this.children.push(...childs);
	}
	paint() {}
	paintLOD1() {}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		let children = this.children.filter((child) => child.visible);
		let actualTotalHeight = children.reduce((acc, curr) => acc + curr.height, 0);
		let effectiveSpacing = (children.length - 1) * this.style.spacing;
		this.height = actualTotalHeight + effectiveSpacing;
		let y = this.position.y;
		children.forEach((child) => {
			child.position.assign(this.position.x, y);
			child.width = this.width;
			y += child.height + this.style.spacing;
		});
	}
	onPropChange() {}
};
var DefaultStackStyle = () => ({ spacing: 0 });
var DefaultStackOptions = (node) => ({ height: node.style?.rowHeight });
//#endregion
//#region src/ui/horizontal-layout.ts
var HorizontalLayout = class extends UINode {
	constructor(_node, _options = DefaultHorizontalLayoutOptions()) {
		super();
	}
	created(options) {
		options = {
			...DefaultHorizontalLayoutOptions(),
			...options
		};
		const { style = {}, childs = [], height = this.node.style.rowHeight } = options;
		this.style = {
			...DefaultHorizontalLayoutStyle(),
			...style
		};
		this.height = height;
		if (childs && childs.length > 0) this.children.push(...childs);
	}
	paint() {}
	paintLOD1() {}
	offPaint() {
		this.offUIContext.fillStyle = this.hitColor.hexValue;
		this.offUIContext.fillRect(this.position.x, this.position.y, this.width, this.height);
	}
	reflow() {
		let children = this.children.filter((child) => child.visible);
		let availableWidth = this.width - (children.length > 0 ? children.length - 1 : 0) * this.style.spacing;
		let originalWidth = availableWidth;
		let maxHeight = 0;
		children.forEach((child) => maxHeight = Math.max(maxHeight, child.height));
		this.height = maxHeight;
		let fixedWidthChilds = this.children.filter((child) => !!child.width);
		fixedWidthChilds.forEach((child) => child.width = clamp(child.width, 0, availableWidth));
		let fixedWidth = fixedWidthChilds.reduce((acc, curr) => acc + curr.width, 0);
		let remainingWidth = originalWidth;
		fixedWidth = clamp(fixedWidth, 0, remainingWidth);
		remainingWidth -= fixedWidth;
		let flexWidth = remainingWidth;
		let x = this.position.x;
		children.forEach((child) => {
			let childWidth;
			if (!!child.width) childWidth = child.width;
			else if (typeof child.style.grow === "number") childWidth = child.style.grow * flexWidth;
			else childWidth = 0;
			childWidth = clamp(childWidth, 0, availableWidth);
			child.width = childWidth;
			let y;
			if (child.height < this.height) y = this.position.y + this.height / 2 - child.height / 2;
			else {
				child.height = this.height;
				y = this.position.y;
			}
			child.position.assign(x, y);
			availableWidth -= childWidth;
			x += childWidth + this.style.spacing;
		});
		if (this.input) this.input.position.assign(this.node.position.x - this.node.style?.terminalStripMargin - this.input.style?.radius, this.position.y + this.height / 2);
		if (this.output) this.output.position.assign(this.node.position.x + this.node.width + this.node.style?.terminalStripMargin + this.output.style?.radius, this.position.y + this.height / 2);
		this.node.ui?.reflow();
	}
	onPropChange() {}
};
var DefaultHorizontalLayoutStyle = () => ({ spacing: 0 });
var DefaultHorizontalLayoutOptions = () => ({ childs: [] });
//#endregion
//#region src/flow-connect.ts
/**
* FlowConnect is like a placeholder that contains references to all Flows created by this instance and can render one flow at a time on-screen
*
* One FlowConnect instance is bound to exactly one HTMLCanvasElement, this instance maintains/tracks the dimensions of HTMLCanvasElement,
* it registers user-interaction events (mouse, keyboard, touch) and creates additional OffScreenCanvas's to track/act-upon these events
*/
var FlowConnect = class FlowConnect extends Hooks {
	static {
		this.audioWorkletsRegistered = false;
	}
	static async create(mount) {
		let flowConnect = new FlowConnect(mount);
		await flowConnect.setupAudioContext();
		await Promise.all(Object.values(this.plugins["setup"]).map((setupFn) => setupFn(flowConnect)));
		return flowConnect;
	}
	static {
		this.plugins = {
			node: {
				"core/empty": EmptyNode,
				"core/subflow": SubFlowNode,
				"core/tunnel": TunnelNode
			},
			ui: {
				"core/container": Container,
				"core/button": Button,
				"core/dial": Dial,
				"core/display": Display,
				"core/envelope": Envelope,
				"core/x-layout": HorizontalLayout,
				"core/image": Image,
				"core/input": Input,
				"core/label": Label,
				"core/radio-group": RadioGroup,
				"core/select": Select,
				"core/2d-slider": Slider2D,
				"core/slider": Slider,
				"core/source": Source,
				"core/stack": Stack,
				"core/toggle": Toggle,
				"core/v-slider": VSlider
			},
			setup: {}
		};
	}
	static register(metadata, executor) {
		if (!metadata.name) return false;
		if (this.plugins[metadata.type][metadata.name]) return false;
		this.plugins[metadata.type][metadata.name] = executor;
		return true;
	}
	static getRegistered(type, name) {
		return this.plugins[type][name];
	}
	get context() {
		return this._context;
	}
	get audioContext() {
		return this._audioContext;
	}
	setCache(type, key, cache) {
		if (type === "array") this.caches[type].set(key, cache.slice(0));
		else if (type === "audio") this.caches[type].set(key, cloneAudioBuffer(cache));
		else this.caches[type].set(key, cache);
	}
	getCache(type, key) {
		const cache = this.caches[type].get(key);
		if (!cache) return void 0;
		if (type === "array") return cache.slice(0);
		else if (type === "audio") return cloneAudioBuffer(cache);
		return cache;
	}
	get offContext() {
		return this._offContext;
	}
	get offUIContext() {
		return this._offUIContext;
	}
	get offGroupContext() {
		return this._offGroupContext;
	}
	get cursor() {
		return this.canvas.style.cursor;
	}
	set cursor(cursor) {
		this.canvas.style.cursor = cursor;
	}
	get scale() {
		return this._transform.a;
	}
	/** Current transformation matrix */
	get transform() {
		return this._transform;
	}
	/** No. of milliseconds passed since the start of one or more flows */
	get time() {
		return this.startTime < 0 ? this.startTime : performance.now() - this.startTime;
	}
	registerRenderer(type, renderer) {
		if (!type || !renderer) return false;
		this.renderers[type] = renderer;
		return true;
	}
	getRegisteredRenderer(type) {
		return this.renderers[type];
	}
	setDefaultStyle(type, nameOrStyle, style) {
		if ((type === "node" || type === "ui") && typeof nameOrStyle === "string" && style) this.defaultStyles[type][nameOrStyle] = style;
		else if ((type === "node" || type === "ui") && typeof nameOrStyle === "object") this.defaultStyles.global[type] = nameOrStyle;
		else if (type !== "node" && type !== "ui") this.defaultStyles[type] = nameOrStyle;
	}
	getDefaultStyle(type, name) {
		if ((type === "node" || type === "ui") && name) return this.defaultStyles[type][name] ?? this.defaultStyles.global[type];
		else return this.defaultStyles[type];
	}
	/**
	* @param mount HTML element (div or canvas) on which FlowConnect will render Flows, if no mount is provided, a new canvas element will be created and attached to document.body
	*/
	constructor(mount) {
		super();
		this.version = "2.1.0";
		this.caches = {
			array: /* @__PURE__ */ new Map(),
			audio: /* @__PURE__ */ new Map()
		};
		this.canvasDimensions = {
			top: 0,
			left: 0,
			width: 0,
			height: 0
		};
		this.state = "Stopped";
		this.flows = [];
		this.pointers = [];
		this.keymap = {};
		this.touchControls = { CreateGroup: false };
		this.genericInput = document.createElement("input");
		this.minScale = .1;
		this.maxScale = 5;
		this.wheelScaleDelta = 1.05;
		this.pinchScaleDelta = 1.02;
		this.disableScale = false;
		this._transform = new DOMMatrix();
		this.inverseTransform = new DOMMatrix();
		this.identity = new DOMMatrix();
		this.startTime = -1;
		this.renderers = {};
		this.defaultStyles = {
			global: {
				node: {},
				ui: {}
			},
			node: {},
			ui: {},
			connector: {},
			terminal: {},
			group: {}
		};
		this.throttle = false;
		this.prepareCanvas(mount);
		this.setupHitCanvas();
		this.calcCanvasDimension(true);
		this.registerChangeListeners();
		this.attachStyles();
		this.canvasUtils();
		this.registerEvents();
		this.setGenericInput();
	}
	/** Re-calculates cavnvas position/dimension when scrolling/resizing happens */
	registerChangeListeners() {
		document.addEventListener("scroll", this.scrollListener.bind(this));
		this.registerObservers(this.canvas.parentElement);
	}
	deRegisterChangeListeners() {
		document.removeEventListener("scroll", this.scrollListener);
	}
	scrollListener() {
		if (!this.throttle) {
			window.requestAnimationFrame(() => {
				this.calcCanvasDimension(false);
				this.throttle = false;
			});
			this.throttle = true;
		}
	}
	registerObservers(parent) {
		this.deRegisterObservers();
		this.parentResizeObserver = new ResizeObserver(() => {
			this.calcCanvasDimension(true);
			this.updateTransform();
		});
		this.parentResizeObserver.observe(parent);
		if (parent !== document.body) {
			this.bodyResizeObserver = new ResizeObserver(() => {
				this.calcCanvasDimension(true);
				this.updateTransform();
			});
			this.bodyResizeObserver.observe(document.body);
		}
	}
	deRegisterObservers() {
		this.parentResizeObserver && this.parentResizeObserver.disconnect();
		this.bodyResizeObserver && this.bodyResizeObserver.disconnect();
	}
	prepareCanvas(mount) {
		if (!mount) {
			this.canvas = document.createElement("canvas");
			this.canvas.width = document.body.clientWidth;
			this.canvas.height = document.body.clientHeight;
			document.body.appendChild(this.canvas);
		} else if (mount instanceof HTMLDivElement) {
			this.canvas = document.createElement("canvas");
			this.canvas.width = mount.clientWidth;
			this.canvas.height = mount.clientHeight;
			mount.appendChild(this.canvas);
		} else if (mount instanceof HTMLCanvasElement) this.canvas = mount;
		else Log.error("'mount' provided is not of type HTMLDivElement or HTMLCanvasElement");
		Object.assign(this.canvas.style, {
			position: "absolute",
			left: 0,
			top: 0,
			width: "100%",
			height: "100%"
		});
		this._context = this.canvas.getContext("2d");
	}
	/** Creates additional canvases for rendering color hit-maps */
	setupHitCanvas() {
		if (typeof OffscreenCanvas !== "undefined" && typeof OffscreenCanvasRenderingContext2D !== "undefined") {
			this.offCanvas = new OffscreenCanvas(this.canvasDimensions.width, this.canvasDimensions.height);
			this.offUICanvas = new OffscreenCanvas(this.canvasDimensions.width, this.canvasDimensions.height);
			this.offGroupCanvas = new OffscreenCanvas(this.canvasDimensions.width, this.canvasDimensions.height);
		} else {
			this.offCanvas = document.createElement("canvas");
			this.offUICanvas = document.createElement("canvas");
			this.offGroupCanvas = document.createElement("canvas");
		}
		this._offContext = this.offCanvas.getContext("2d", { willReadFrequently: true });
		this._offUIContext = this.offUICanvas.getContext("2d", { willReadFrequently: true });
		this._offGroupContext = this.offGroupCanvas.getContext("2d", { willReadFrequently: true });
	}
	attachStyles() {
		this.canvas.style.touchAction = "none";
		let inputStyle = document.createElement("style");
		inputStyle.innerHTML = "input.flow-connect-input { position: fixed; visibility: hidden; pointer-events: none; z-index: 100; border: none; border-radius: 0; box-sizing: border-box;} input.flow-connect-input:focus { outline: none; }";
		document.getElementsByTagName("head")[0].appendChild(inputStyle);
	}
	calcCanvasDimension(adjust) {
		if (adjust && this.canvas.parentElement) {
			let parentBoundingRect = this.canvas.parentElement.getBoundingClientRect();
			this.canvas.width = parentBoundingRect.width;
			this.canvas.height = parentBoundingRect.height;
			this.call("dimension-change", this, this.canvas.width, this.canvas.height);
		}
		let boundingRect = this.canvas.getBoundingClientRect();
		this.canvasDimensions = {
			top: Math.round(boundingRect.top),
			left: Math.round(boundingRect.left),
			width: Math.round(boundingRect.width),
			height: Math.round(boundingRect.height)
		};
		this.offCanvas.width = this.canvasDimensions.width;
		this.offCanvas.height = this.canvasDimensions.height;
		this.offUICanvas.width = this.canvasDimensions.width;
		this.offUICanvas.height = this.canvasDimensions.height;
		this.offGroupCanvas.width = this.canvasDimensions.width;
		this.offGroupCanvas.height = this.canvasDimensions.height;
	}
	canvasUtils() {
		FlowConnect.prototype.canvasUtils = noop;
		CanvasRenderingContext2D.prototype.roundRect = function(x, y, width, height, radius) {
			this.beginPath();
			this.moveTo(x + radius, y);
			this.lineTo(x + width - radius, y);
			this.quadraticCurveTo(x + width, y, x + width, y + radius);
			this.lineTo(x + width, y + height - radius);
			this.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
			this.lineTo(x + radius, y + height);
			this.quadraticCurveTo(x, y + height, x, y + height - radius);
			this.lineTo(x, y + radius);
			this.quadraticCurveTo(x, y, x + radius, y);
			this.closePath();
		};
		CanvasRenderingContext2D.prototype.strokeRoundRect = function(x, y, width, height, radius) {
			if (typeof radius === "undefined") radius = 5;
			this.roundRect(x, y, width, height, radius);
			this.stroke();
		};
		CanvasRenderingContext2D.prototype.fillRoundRect = function(x, y, width, height, radius) {
			if (typeof radius === "undefined") radius = 5;
			this.roundRect(x, y, width, height, radius);
			this.fill();
		};
	}
	handleKeyDown(ev) {
		!this.keymap[ev.key] && (this.keymap[ev.key] = true);
	}
	registerEvents() {
		let dragDelta;
		let prevPanPosition = Vector.Zero();
		let prevPinchDistance = -1;
		let longPressTimerId = -1;
		let longPressPointer;
		document.onkeydown = (ev) => this.handleKeyDown(ev);
		document.onkeyup = (ev) => {
			this.keymap[ev.key] = false;
		};
		this.canvas.onpointerdown = (ev) => {
			if (!this.currFlow) return;
			this.addPointer(ev.pointerId, this.getRelativePosition(ev));
			if (this.pointers.length === 1) {
				prevPanPosition = this.pointers[0].screenPosition;
				this.currHitNode = this.getHitNode(this.pointers[0].screenPosition);
				if (this.currHitNode) {
					this.currHitNode.zIndex = Number.MAX_SAFE_INTEGER;
					if (this.keymap["Control"]) this.currHitNode.focused = !this.currHitNode.focused;
					else {
						this.currFlow.removeAllFocus();
						this.currHitNode.focused = true;
					}
					this.currHitNode.onDown(this.pointers[0].screenPosition.clone(), this.pointers[0].realPosition.clone());
					dragDelta = this.currHitNode.position?.subtract(this.pointers[0].realPosition);
					if (!this.currHitNode.currHitUINode) {
						longPressTimerId = window.setTimeout(() => {
							this.currHitNode.onContextMenu(this.pointers[0].screenPosition, this.pointers[0].realPosition);
							this.call("context-menu", {
								screenPos: this.pointers[0].screenPosition,
								realPos: this.pointers[0].realPosition,
								target: this.currHitNode
							});
						}, 1e3);
						longPressPointer = this.pointers[0];
					}
				} else if (!this.keymap["Control"]) {
					this.currFlow.removeAllFocus();
					this.currHitGroup = this.getHitGroup(this.pointers[0].screenPosition);
					this.currHitGroup && (dragDelta = this.currHitGroup.position?.subtract(this.pointers[0].realPosition));
				} else if (this.keymap["Control"] || this.touchControls["CreateGroup"]) {
					this.groupStartPoint = this.pointers[0].realPosition.clone();
					this.currGroup = Group.create(this.currFlow, this.groupStartPoint.clone(), {
						name: "New Group",
						width: 0,
						height: 0
					});
				}
			} else {
				clearTimeout(longPressTimerId);
				longPressPointer = void 0;
				this.currHitNode = void 0;
				this.currHitGroup = void 0;
				this.currFlow.removeAllFocus();
				this.currFlow.removeFloatingConnector();
			}
		};
		this.canvas.onpointerup = (ev) => {
			if (!this.currFlow) return;
			this.removePointer(this.pointers, ev);
			if (longPressPointer?.id === ev.pointerId) {
				clearTimeout(longPressTimerId);
				longPressPointer = void 0;
			}
			if (this.pointers.length < 2) prevPinchDistance = -1;
			let screenPosition = this.getRelativePosition(ev);
			let realPosition = screenPosition.transform(this.inverseTransform);
			if (this.currHitNode) this.handleGrouping(screenPosition);
			this.currHitNode = void 0;
			this.currHitGroup = void 0;
			let hitNode = this.getHitNode(screenPosition);
			hitNode?.onUp(screenPosition.clone(), realPosition.clone());
			if (this.currGroup) this.addCurrAsNewGroup();
			if (this.currFlow.floatingConnector) this.handleConnection(screenPosition, realPosition, hitNode);
		};
		this.canvas.onpointerout = (ev) => {
			if (!this.currFlow) return;
			this.removePointer(this.pointers, ev);
			if (longPressPointer?.id === ev.pointerId) {
				clearTimeout(longPressTimerId);
				longPressPointer = void 0;
			}
			if (this.pointers.length === 0) {
				if (this.currHitNode) {
					let screenPosition = this.getRelativePosition(ev);
					this.handleGrouping(screenPosition);
				}
				this.currHitNode = void 0;
				this.currHitGroup = void 0;
			}
			if (this.currGroup) this.addCurrAsNewGroup();
			if (this.currFlow.floatingConnector) this.currFlow.removeFloatingConnector();
			if (this.prevHitNode) {
				let screenPosition = this.getRelativePosition(ev);
				let realPosition = screenPosition.transform(this.inverseTransform);
				this.prevHitNode.onExit(screenPosition, realPosition);
				this.prevHitNode = void 0;
			}
		};
		this.canvas.onpointermove = (ev) => {
			if (!this.currFlow) return;
			let screenPosition = this.getRelativePosition(ev);
			let realPosition = screenPosition.transform(this.inverseTransform);
			if (longPressPointer?.id === ev.pointerId) {
				clearTimeout(longPressTimerId);
				longPressPointer = void 0;
			}
			this.updatePointer(ev.pointerId, screenPosition, realPosition);
			if (this.pointers.length === 2) {
				let currPinchDistance = Vector.Distance(this.pointers[0].screenPosition, this.pointers[1].screenPosition);
				if (prevPinchDistance > 0) {
					if (currPinchDistance !== prevPinchDistance) this.handleZoom(currPinchDistance > prevPinchDistance, Vector.Midpoint(this.pointers[0].screenPosition, this.pointers[1].screenPosition), this.pinchScaleDelta);
				}
				prevPinchDistance = currPinchDistance;
			}
			if (this.currGroup?.position) {
				if (realPosition.x < this.groupStartPoint.x) this.currGroup.position.x = realPosition.x;
				this.currGroup.width = Math.abs(this.groupStartPoint.x - realPosition.x);
				if (realPosition.y < this.groupStartPoint.y) this.currGroup.position.y = realPosition.y;
				this.currGroup.height = Math.abs(this.groupStartPoint.y - realPosition.y);
			}
			if (this.currHitNode) {
				this.currHitNode.onDrag(screenPosition.clone(), realPosition.clone());
				if (this.currHitNode) {
					if ((!this.currHitNode.currHitUINode || !this.currHitNode.currHitUINode.draggable) && !this.currHitNode.currHitTerminal && !this.currFlow.floatingConnector && dragDelta) {
						this.currHitNode.position = realPosition.add(dragDelta);
						let hitGroup = this.getHitGroup(screenPosition);
						if (hitGroup?.position && hitGroup === this.currHitNode.group) {
							let groupRealPos = hitGroup.position.transform(this._transform);
							let nodeRealPos = this.currHitNode.position.transform(this._transform);
							if (intersects(groupRealPos.x, groupRealPos.y, groupRealPos.x + hitGroup.width * this.scale, groupRealPos.y + hitGroup.height * this.scale, nodeRealPos.x, nodeRealPos.y, nodeRealPos.x + this.currHitNode.width * this.scale, nodeRealPos.y + this.currHitNode.ui.height * this.scale) === ViewPort.INSIDE) {
								let nodeIndex = hitGroup.nodes.findIndex((node) => node.id === this.currHitNode.id);
								hitGroup.nodeDeltas[nodeIndex] = this.currHitNode.position.subtract(hitGroup.position);
							}
						}
					}
				}
			} else if (this.currHitGroup && dragDelta) this.currHitGroup.position = realPosition.add(dragDelta);
			else if (this.pointers.length === 1 && !this.keymap["Control"] && !this.touchControls["CreateGroup"]) {
				let delta = screenPosition.subtract(prevPanPosition).multiplyInPlace(1 / this.scale);
				prevPanPosition = screenPosition;
				this.updateTransform(void 0, void 0, delta);
			}
			if (this.currFlow.floatingConnector) this.currFlow.floatingConnector.floatingTip = realPosition;
			if (ev.pointerType === "mouse" && !this.currHitNode) {
				let hitNode = this.getHitNode(screenPosition);
				if (hitNode !== this.prevHitNode) {
					this.prevHitNode?.onExit(screenPosition, realPosition);
					hitNode?.onEnter(screenPosition, realPosition);
				} else hitNode && !this.currHitNode && hitNode.onOver(screenPosition, realPosition);
				this.prevHitNode = hitNode;
			}
		};
		this.canvas.onclick = (ev) => {
			if (!this.currFlow) return;
			let screenPosition = this.getRelativePosition(ev);
			let realPosition = screenPosition.transform(this.inverseTransform);
			let hitNode = this.getHitNode(screenPosition);
			hitNode?.onClick(screenPosition.clone(), realPosition.clone());
			if (!hitNode) this.getHitGroup(screenPosition)?.onClick(screenPosition.clone(), realPosition.clone());
		};
		this.canvas.oncontextmenu = (ev) => {
			if (!this.currFlow) return;
			ev.preventDefault();
			let screenPosition = this.getRelativePosition(ev);
			let realPosition = screenPosition.transform(this.inverseTransform);
			let hitNode = this.getHitNode(screenPosition);
			hitNode?.onContextMenu(screenPosition, realPosition);
			this.call("context-menu", {
				screenPos: screenPosition,
				realPos: realPosition,
				target: hitNode ?? this
			});
			if (!this.keymap["Control"]) this.currFlow.removeAllFocus();
			hitNode && (hitNode.focused = true);
		};
		this.canvas.ondblclick = (ev) => {
			if (!this.currFlow) return;
			ev.preventDefault();
			let screenPosition = this.getRelativePosition(ev);
			let realPosition = screenPosition.transform(this.inverseTransform);
			let hitNode = this.getHitNode(screenPosition);
			let hitColor = Color.rgbaToString(this.offUIContext.getImageData(screenPosition.x, screenPosition.y, 1, 1).data);
			if (!hitNode?.getHitUINode(hitColor)) this.call("dbl-press", {
				screenPos: screenPosition,
				realPos: realPosition,
				target: hitNode ?? this
			});
		};
		this.canvas.onwheel = (ev) => {
			if (!this.currFlow) return;
			let screenPosition = this.getRelativePosition(ev);
			let hitNode = this.getHitNode(screenPosition);
			if (hitNode) {
				let hitColor = Color.rgbaToString(this.offUIContext.getImageData(screenPosition.x, screenPosition.y, 1, 1).data);
				if (hitNode.getHitUINode(hitColor)?.zoomable) {
					hitNode.onWheel(ev.deltaY < 0, screenPosition, screenPosition.transform(this.inverseTransform));
					return;
				}
			}
			if (!this.disableScale) {
				ev.preventDefault();
				this.handleZoom(ev.deltaY < 0, screenPosition, this.wheelScaleDelta);
			}
		};
	}
	deRegisterEvents() {
		document.onkeydown = null;
		document.onkeyup = null;
		this.canvas.onpointerdown = null;
		this.canvas.onpointerup = null;
		this.canvas.onpointerout = null;
		this.canvas.onpointermove = null;
		this.canvas.onclick = null;
		this.canvas.oncontextmenu = null;
		this.canvas.ondblclick = null;
		this.canvas.onwheel = null;
	}
	addCurrAsNewGroup() {
		let newGroup = this.currGroup;
		this.currGroup = void 0;
		if (newGroup && newGroup.width > 10 && newGroup.height > 10) {
			this.currFlow.groups.push(newGroup);
			[...this.currFlow.nodes.values()].filter((node) => !node.group && node.renderState.viewport === ViewPort.INSIDE).forEach((node) => newGroup.add(node));
		}
	}
	setGenericInput() {
		this.genericInput.className = "flow-connect-input";
		this.genericInput.style.visibility = "hidden";
		this.genericInput.style.pointerEvents = "none";
		this.genericInput.style.padding = "0";
		this.genericInput.onblur = () => {
			this.genericInput.style.visibility = "hidden";
			this.genericInput.style.pointerEvents = "none";
			this.genericInput.onchange = null;
		};
		document.body.appendChild(this.genericInput);
	}
	async setupAudioContext() {
		if (window.__FLOWCONNECT_AUDIO_CTX__) this._audioContext = window.__FLOWCONNECT_AUDIO_CTX__;
		else {
			this._audioContext = new AudioContext();
			window.__FLOWCONNECT_AUDIO_CTX__ = this._audioContext;
		}
		if (!FlowConnect.audioWorkletsRegistered) {
			let workletUtils = generateWorkletUtils();
			let audioWorklets = generateAudioWorklets(workletUtils.CircularBuffer);
			await this.audioContext.audioWorklet.addModule(workletUtils.CircularBuffer);
			await Promise.all(Object.keys(audioWorklets).map((key) => this.audioContext.audioWorklet.addModule(audioWorklets[key])));
			FlowConnect.audioWorkletsRegistered = true;
		}
	}
	showGenericInput(position, value, styles, attributes, callback) {
		if (document.activeElement === this.genericInput) return;
		Object.assign(this.genericInput.style, styles);
		Object.assign(this.genericInput, attributes);
		Object.assign(this.genericInput.style, {
			left: position.x + this.canvasDimensions.left + "px",
			top: position.y + this.canvasDimensions.top - 3 + "px",
			visibility: "visible",
			pointerEvents: "all"
		});
		this.genericInput.value = value;
		this.genericInput.onchange = () => callback(this.genericInput.value);
		this.genericInput.focus();
	}
	changeParent(newParent) {
		this.registerObservers(newParent);
	}
	updatePointer(id, screenPosition, realPosition) {
		let pointer = this.pointers.find((pntr) => pntr.id === id);
		if (pointer) {
			pointer.screenPosition = screenPosition;
			pointer.realPosition = realPosition;
		}
	}
	handleZoom(zoomIn, origin, scaleDelta) {
		if (this._transform.a >= this.maxScale && zoomIn || this._transform.a <= this.minScale && !zoomIn) return;
		this.updateTransform(zoomIn ? scaleDelta : 1 / scaleDelta, origin);
		this.currFlow?.call("scale", this.scale);
		this.call("scale", this.scale);
	}
	handleGrouping(screenPosition) {
		let hitGroup = this.getHitGroup(screenPosition);
		let intersection;
		if (hitGroup?.position && this.currHitNode?.position) {
			let groupRealPos = hitGroup.position.transform(this._transform);
			let nodeRealPos = this.currHitNode.position.transform(this._transform);
			intersection = intersects(groupRealPos.x, groupRealPos.y, groupRealPos.x + hitGroup.width * this.scale, groupRealPos.y + hitGroup.height * this.scale, nodeRealPos.x, nodeRealPos.y, nodeRealPos.x + this.currHitNode.width * this.scale, nodeRealPos.y + this.currHitNode.height * this.scale);
		}
		if (this.currHitNode.group && (this.currHitNode.group !== hitGroup || intersection !== ViewPort.INSIDE)) this.currHitNode.group.remove(this.currHitNode);
		if (hitGroup && intersection === ViewPort.INSIDE) hitGroup.add(this.currHitNode);
	}
	handleConnection(screenPosition, realPosition, hitNode) {
		if (!hitNode) {
			this.currFlow.removeFloatingConnector();
			return;
		}
		let hitTerminal = hitNode.getHitTerminal(Color.rgbaToString(this._offUIContext.getImageData(screenPosition.x, screenPosition.y, 1, 1).data), screenPosition.clone(), realPosition.clone());
		if (hitTerminal) hitNode.currHitTerminal = hitTerminal;
		if (!hitTerminal) {
			this.currFlow.removeFloatingConnector();
			return;
		}
		let destination = hitNode.currHitTerminal;
		if (!this.currFlow.floatingConnector?.canConnect(destination)) {
			this.currFlow.removeFloatingConnector();
			hitNode.currHitTerminal = void 0;
			if (destination) destination.node.currHitTerminal = void 0;
		} else if (destination?.type === TerminalType.OUT) {
			const terminal = this.currFlow.removeFloatingConnector();
			hitNode.currHitTerminal?.onExit();
			hitNode.currHitTerminal = void 0;
			destination.node.currHitTerminal?.onExit();
			destination.node.currHitTerminal = void 0;
			terminal?.connect(destination);
		} else if (destination && destination.connectors.length > 0) {
			if (destination.connectors[0].start === this.currFlow.floatingConnector.start) {
				this.currFlow.removeFloatingConnector();
				hitNode.currHitTerminal = void 0;
				return;
			}
			destination.disconnect();
			const terminal = this.currFlow.removeFloatingConnector();
			hitNode.currHitTerminal?.onExit();
			hitNode.currHitTerminal = void 0;
			destination.node.currHitTerminal?.onExit();
			destination.node.currHitTerminal = void 0;
			terminal?.connect(destination);
		} else {
			const terminal = this.currFlow.removeFloatingConnector();
			hitNode.currHitTerminal?.onExit();
			hitNode.currHitTerminal = void 0;
			destination.node.currHitTerminal?.onExit();
			destination.node.currHitTerminal = void 0;
			terminal?.connect(destination);
		}
	}
	getRelativePosition(ev) {
		return Vector.create(ev.clientX - this.canvasDimensions.left, ev.clientY - this.canvasDimensions.top);
	}
	updateTransform(scale, scaleOrigin, translate) {
		if (scale && scaleOrigin) {
			let realSpaceOrigin = scaleOrigin.transform(this.inverseTransform);
			this._transform.translateSelf(realSpaceOrigin.x, realSpaceOrigin.y).scaleSelf(scale, scale).translateSelf(-realSpaceOrigin.x, -realSpaceOrigin.y);
		}
		if (translate) this._transform.translateSelf(translate.x, translate.y);
		this.inverseTransform = this._transform.inverse();
		this._context.setTransform(this._transform);
		this._offContext.setTransform(this._transform);
		this._offUIContext.setTransform(this._transform);
		this._offGroupContext.setTransform(this._transform);
		this.currFlow?.transform();
		this.call("transform", this);
	}
	translateBy(delta) {
		this.updateTransform(void 0, void 0, delta);
	}
	scaleBy(scale, scaleOrigin) {
		this.updateTransform(scale, scaleOrigin);
	}
	addPointer(pointerId, position) {
		this.pointers.push({
			id: pointerId,
			screenPosition: position,
			realPosition: position.transform(this.inverseTransform)
		});
	}
	screenToReal(pos) {
		return pos.transform(this.inverseTransform);
	}
	removePointer(pointers, ev) {
		pointers.splice(pointers.findIndex((pointer) => pointer.id === ev.pointerId), 1);
	}
	getHitNode(position) {
		let rgbaString = Color.rgbaToString(this._offContext.getImageData(position.x, position.y, 1, 1).data);
		return this.currFlow.nodeHitColors.get(rgbaString);
	}
	getHitGroup(position) {
		let rgbaString = Color.rgbaToString(this._offGroupContext.getImageData(position.x, position.y, 1, 1).data);
		return this.currFlow.groupHitColors.get(rgbaString);
	}
	clear() {
		this._context.save();
		this._context.setTransform(this.identity);
		this._context.clearRect(0, 0, this.canvasDimensions.width, this.canvasDimensions.height);
		this._context.restore();
		this._offContext.save();
		this._offContext.setTransform(this.identity);
		this._offContext.clearRect(0, 0, this.canvasDimensions.width, this.canvasDimensions.height);
		this._offContext.restore();
		this._offUIContext.save();
		this._offUIContext.setTransform(this.identity);
		this._offUIContext.clearRect(0, 0, this.canvasDimensions.width, this.canvasDimensions.height);
		this._offUIContext.restore();
		this._offGroupContext.save();
		this._offGroupContext.setTransform(this.identity);
		this._offGroupContext.clearRect(0, 0, this.canvasDimensions.width, this.canvasDimensions.height);
		this._offGroupContext.restore();
	}
	startGlobalTime() {
		if (this.startTime < 0) {
			this.startTime = performance.now();
			this._startGlobalTime();
			this.state = "Running";
			this.call("start", this);
		}
	}
	_startGlobalTime() {
		this.call("tick", this);
		this.currFlow?.call("tick");
		this.timerId = window.requestAnimationFrame(this._startGlobalTime.bind(this));
	}
	stopGlobalTime() {
		if (this.isRootFlowStopped()) {
			cancelAnimationFrame(this.timerId);
			this.startTime = -1;
			this.state = "Stopped";
			this.call("tickreset", this);
			this.currFlow?.call("tickreset");
			this.call("stop", this);
		}
	}
	isRootFlowStopped() {
		let root = this.currFlow;
		while (true) if (root.parentFlow) root = root.parentFlow;
		else return root.state === FlowState.Stopped;
	}
	_render() {
		this.clear();
		this.currGroup && this.currGroup.render();
		this.currFlow.render();
		this.call("render", this);
		this.frameId = window.requestAnimationFrame(this._render.bind(this));
	}
	addFlow(flow) {
		this.flows.push(flow);
		flow.on("start", () => this.startGlobalTime());
		flow.on("stop", () => this.stopGlobalTime());
	}
	createFlow(options) {
		const flow = Flow.create(this, options);
		this.addFlow(flow);
		return flow;
	}
	render(flow) {
		if (flow === this.currFlow) return;
		if (this.currFlow) window.cancelAnimationFrame(this.frameId);
		if (!this.rootFlow || !this.rootFlow.existsInFlow(flow)) this.rootFlow = flow;
		this.currFlow = flow;
		this._render();
	}
	/** Render the root flow of the flow-tree */
	top() {
		this.render(this.rootFlow);
	}
	/** Serializes a flow to json
	* @param flow The flow
	*/
	async toJson(flow, persist) {
		try {
			let serializedFlow = await flow.serialize(persist);
			return JSON.stringify(serializedFlow);
		} catch (error) {
			Log.error(error);
		}
	}
	/** Creates a flow from json
	* @param json Json string with schema SerializedFlow
	*/
	async fromJson(json, receive) {
		let data;
		let flow;
		try {
			data = JSON.parse(json);
			flow = await Flow.deSerialize(this, data, receive);
			if (!flow) return;
			this.addFlow(flow);
		} catch (error) {
			Log.error(error);
		}
		return flow;
	}
	detach() {
		this.currFlow?.stop();
		this.deRegisterObservers();
		this.deRegisterEvents();
		this.deRegisterChangeListeners();
	}
};
var FlowConnectState = /* @__PURE__ */ function(FlowConnectState) {
	FlowConnectState["Stopped"] = "Stopped";
	FlowConnectState["Running"] = "Running";
	return FlowConnectState;
}({});
//#endregion
export { NodeUIState as A, FlowState as C, Group as D, SubFlowNode as E, Connector as F, Color as I, Vector as L, UINode as M, Terminal as N, Node as O, TerminalType as P, Flow as S, GraphNode as T, Display as _, VSlider as a, Slider2D as b, Slider as c, Label as d, Input as f, CanvasType as g, Envelope as h, Stack as i, Container as j, NodeButton as k, Select as l, Image as m, FlowConnectState as n, Toggle as o, InputType as p, HorizontalLayout as r, Source as s, FlowConnect as t, RadioGroup as u, Dial as v, Graph as w, TunnelNode as x, Button as y };

//# sourceMappingURL=flow-connect-Cr8EqIwr.js.map